import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialModal from './components/TutorialModal';
import CompletedDateModal from './components/CompletedDateModal';
import { GoogleMap, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import axios from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Link, useNavigate } from 'react-router-dom';
import './App.css';
import { FaFacebook, FaMedium, FaTwitter } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid'; // Import a UUID library
import { useWebSocket } from './WebSocketContext';
import { format, toZonedTime, formatInTimeZone } from 'date-fns-tz';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import ModernNoteCard from './components/ModernNoteCard';
import ModernDashboard from './components/ModernDashboard';
import AddTagTooltip from './components/AddTagTooltip';
import Header from './components/Header';
import Footer from './components/Footer';
import TabNavigation from './components/TabNavigation';
import ExplorerBoard from './components/ExplorerBoard';

const mapContainerStyle = {
  height: '100%',
  width: '100%',
};

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };
const DEFAULT_ZOOM = 5;
const DEFAULT_PROVIDER = 'mapbox';
const DEFAULT_PLAN = 'Basic';
const apiUrl = process.env.REACT_APP_API_URL;

// Fix 3: Debounce utility function for map event handlers
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const colorOptions = ['Blue', 'Red', 'Green', 'Purple', 'Pink', 'Yellow', 'Orange', 'Light Blue'];

const geocodifyApiKey = process.env.REACT_APP_GEOCODIFY_API_KEY; 
const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Utility function to calculate distance between two coordinates using Haversine formula
// Returns distance in miles
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Format distance for display
const formatDistance = (distanceInMiles) => {
  if (distanceInMiles < 1) {
    return `${Math.round(distanceInMiles * 5280)} feet`;
  } else if (distanceInMiles < 10) {
    return `${distanceInMiles.toFixed(1)} miles`;
  } else {
    return `${Math.round(distanceInMiles)} miles`;
  }
};
//const mapboxApiKey = 'pk.eyJ1IjoiaWRlYWx1c2VyIiwiYSI6ImNtMG15bzYwMTA3eG0ybW9wdnN4bTg2eXMifQ.Kb8gxDbHJvYA18e4a4nmw';
const mapboxApiKey = process.env.REACT_APP_MAPBOX_API_KEY;

const googleMapsLibraries = ['places', 'marker'];
const googleMapsOptions = {
  googleMapsApiKey,
  libraries: googleMapsLibraries,
  id: 'script-loader',
  version: 'weekly',
  language: 'en',
  region: 'US',
};

// Define default values for colors if the categorization is empty or API call fails
const DEFAULT_CATEGORIZATION = {
  red: 'Shopping',
  orange: 'Entertainment',
  green: 'To do',
  blue: 'Travel',
  yellow: 'Food',
  purple: 'Health',
  ltblue: 'Reminder'
};

const unwrapTaggedColorValue = (value) => {
  if (value == null) {
    return value;
  }
  if (typeof value === 'object' && value.value != null) {
    return value.value;
  }
  return value;
};

// Helper function to get the effective category for a note
// Priority order:
// 1. Manual tag with key "color" (case-insensitive) - highest priority
// 2. AI categorization from aiPromptResults.category[0].color.value - fallback
const getEffectiveCategory = (note) => {
  // Guard against undefined/null notes
  if (!note) {
    return null;
  }

  // First check for manual color tags (case-insensitive)
  const manualTags = note.manualPromptResults?.manualTags || {};
  const colorTag = Object.entries(manualTags).find(
    ([key, value]) => key.toLowerCase() === 'color' && value
  );

  if (colorTag) {
    return unwrapTaggedColorValue(colorTag[1]);
  }

  // Fall back to AI categorization
  return unwrapTaggedColorValue(note.aiPromptResults?.category?.[0]?.color?.value);
};

const sanitizeTagKey = (key) => {
  if (!key) {
    return '';
  }
  return key.toString().toLowerCase().replace(/[^a-z0-9]/g, '');
};

const addManualTagValue = (map, key, value) => {
  if (value === undefined || value === null) {
    return;
  }
  const normalizedKey = sanitizeTagKey(key);
  if (!normalizedKey) {
    return;
  }
  if (!map.has(normalizedKey)) {
    map.set(normalizedKey, new Set());
  }
  const targetSet = map.get(normalizedKey);

  const addValue = (raw) => {
    if (raw === undefined || raw === null) {
      return;
    }
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed) {
        targetSet.add(trimmed);
      }
    } else if (typeof raw === 'number' || typeof raw === 'boolean') {
      targetSet.add(String(raw));
    } else if (typeof raw === 'object' && raw.value) {
      addValue(raw.value);
    }
  };

  if (Array.isArray(value)) {
    value.forEach((item) => addValue(item));
  } else {
    addValue(value);
  }
};

const aggregateManualTagValues = (notesList) => {
  const manualTagMap = new Map();
  notesList.forEach((note) => {
    const manualTags = note.manualPromptResults?.manualTags || {};
    Object.entries(manualTags).forEach(([key, value]) => {
      addManualTagValue(manualTagMap, key, value);
    });
  });
  return manualTagMap;
};

const collectManualTagValues = (manualTagMap, keys) => {
  const collected = [];
  keys.forEach((key) => {
    const normalizedKey = sanitizeTagKey(key);
    if (normalizedKey && manualTagMap.has(normalizedKey)) {
      manualTagMap.get(normalizedKey).forEach((value) => collected.push(value));
    }
  });
  return collected;
};

const firstNonEmptyString = (values) => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return '';
};

const parseDateCandidate = (values) => {
  for (const value of values) {
    if (!value) {
      continue;
    }
    const candidate = typeof value === 'string' ? value : value.toString();
    const parsed = new Date(candidate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return null;
};

const booleanFromValues = (values) => {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', 'yes', '1', 'y', 'family', 'kids', 'children'].includes(normalized)) {
        return true;
      }
      if (['false', 'no', '0', 'n'].includes(normalized)) {
        return false;
      }
    }
  }
  return false;
};

const splitStringValues = (values) => {
  const result = new Set();
  values.forEach((value) => {
    if (!value) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) {
          const trimmed = item.toString().trim();
          if (trimmed) {
            result.add(trimmed);
          }
        }
      });
    } else if (typeof value === 'string') {
      value
        .split(/[,;/|]+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => result.add(part));
    } else {
      result.add(value.toString());
    }
  });
  return Array.from(result);
};

const normalizeTripType = (value) => {
  if (!value) {
    return '';
  }
  const normalized = value.toString().toLowerCase();
  if (normalized.includes('solo')) {
    return 'solo';
  }
  if (normalized.includes('couple')) {
    return 'couples';
  }
  if (normalized.includes('friend')) {
    return 'friends';
  }
  if (normalized.includes('family') || normalized.includes('kids')) {
    return 'family';
  }
  return '';
};

const normalizeColorKey = (color) => {
  if (color == null || color === '') {
    return null;
  }
  const raw =
    typeof color === 'object' && color !== null && color.value != null ? color.value : color;
  const normalized = raw
    .toString()
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ');
  if (!normalized) {
    return null;
  }
  const compact = normalized.replace(/\s+/g, '');
  // UI filter uses "light blue" (spaced); records often use LightBlue / lightblue / ltblue
  if (
    normalized === 'light blue' ||
    normalized === 'lt blue' ||
    compact === 'lightblue' ||
    compact === 'ltblue' ||
    normalized === 'ltblue'
  ) {
    return 'ltblue';
  }
  return normalized;
};

const aggregateAiData = (notesList) => {
  const data = {
    names: new Set(),
    descriptions: new Set(),
    addresses: new Set(),
    interests: new Set(),
    packingNeeds: new Set(),
    colors: new Set(),
    tripTypes: new Set(),
    nearby: new Set(),
  };

  notesList.forEach((note) => {
    const ai = note.aiPromptResults || {};
    if (ai.name?.value) {
      data.names.add(ai.name.value);
    }
    if (ai.description?.value) {
      data.descriptions.add(ai.description.value);
    }
    if (ai.address?.value) {
      data.addresses.add(ai.address.value);
    }
    if (ai.tripType?.value) {
      data.tripTypes.add(ai.tripType.value);
    }

    const recTags = Array.isArray(ai.recTags?.value)
      ? ai.recTags.value
      : Array.isArray(ai.recTags)
        ? ai.recTags.map((item) => item?.value).filter(Boolean)
        : [];
    recTags.forEach((tag) => data.interests.add(tag));

    const activities = Array.isArray(ai.activities?.value)
      ? ai.activities.value
      : Array.isArray(ai.activities)
        ? ai.activities.map((item) => item?.value).filter(Boolean)
        : [];
    activities.forEach((activity) => data.interests.add(activity));

    const packing = Array.isArray(ai.packingNeeds?.value)
      ? ai.packingNeeds.value
      : Array.isArray(ai.packingNeeds)
        ? ai.packingNeeds.map((item) => item?.value).filter(Boolean)
        : [];
    packing.forEach((item) => data.packingNeeds.add(item));

    if (Array.isArray(ai.category)) {
      ai.category.forEach((category) => {
        const colorValue = category?.color?.value;
        if (colorValue) {
          data.colors.add(colorValue);
        }
      });
    }

    const nearbyValues = Array.isArray(ai.nearby?.value)
      ? ai.nearby.value
      : Array.isArray(ai.nearby)
        ? ai.nearby.map((item) => item?.value).filter(Boolean)
        : [];
    nearbyValues.forEach((value) => data.nearby.add(value));
  });

  return data;
};

const createSnippet = (text, length = 180) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= length) {
    return normalized;
  }
  return `${normalized.slice(0, length)}...`;
};

const buildSelectedNotesSummary = (notesList) =>
  notesList.map((note) => ({
    noteID: note.noteID,
    title: note.manualPromptResults?.emailSubject || 'Untitled Note',
    snippet: createSnippet(note.manualPromptResults?.bodyText || ''),
    category: getEffectiveCategory(note),
  }));

const buildPackitPrefillPayload = (notesList, userHomeLocation) => {
  if (!notesList || notesList.length === 0) {
    return null;
  }

  const manualTagMap = aggregateManualTagValues(notesList);
  const aiData = aggregateAiData(notesList);

  const tripNameCandidates = [
    ...collectManualTagValues(manualTagMap, ['tripname', 'packitname', 'name']),
    ...Array.from(aiData.names),
    ...notesList
      .map((note) => note.manualPromptResults?.emailSubject)
      .filter((value) => typeof value === 'string' && value.trim().length > 0),
  ];
  const tripName =
    firstNonEmptyString(tripNameCandidates) ||
    `Pack-It from ${notesList.length} Note${notesList.length === 1 ? '' : 's'}`;

  const bodyTexts = notesList
    .map((note) => note.manualPromptResults?.bodyText)
    .filter((text) => typeof text === 'string' && text.trim().length > 0);
  const descriptionsCombined = [
    ...bodyTexts,
    ...Array.from(aiData.descriptions),
  ];
  const description = descriptionsCombined.length
    ? descriptionsCombined.join('\n\n')
    : '';

  const startDateCandidates = collectManualTagValues(manualTagMap, [
    'startdate',
    'departuredate',
    'arrivaldate',
    'checkindate',
  ]);
  const endDateCandidates = collectManualTagValues(manualTagMap, [
    'enddate',
    'returndate',
    'checkoutdate',
  ]);
  const startDate = parseDateCandidate(startDateCandidates);
  const endDate = parseDateCandidate(endDateCandidates);

  const tripTypeCandidate = normalizeTripType(
    firstNonEmptyString([
      ...collectManualTagValues(manualTagMap, ['triptype', 'tripcategory']),
      ...Array.from(aiData.tripTypes),
    ])
  );

  const includesKidsValues = [
    ...collectManualTagValues(manualTagMap, [
      'includeskids',
      'kids',
      'familyfriendly',
      'family',
    ]),
  ];
  const includesKids =
    booleanFromValues(includesKidsValues) || tripTypeCandidate === 'family';

  const includesPetsValues = [
    ...collectManualTagValues(manualTagMap, ['includespets', 'pets']),
  ];
  const includesPets = booleanFromValues(includesPetsValues);

  const packingNeedsValues = [
    ...collectManualTagValues(manualTagMap, [
      'packingneeds',
      'packing',
      'packlist',
      'packinglist',
    ]),
    ...Array.from(aiData.packingNeeds),
  ];
  const packingNeeds = splitStringValues(packingNeedsValues).map((item) =>
    item.toLowerCase()
  );

  const interestsValues = [
    ...collectManualTagValues(manualTagMap, [
      'interests',
      'activities',
      'tags',
    ]),
    ...Array.from(aiData.interests),
  ];
  const interests = splitStringValues(interestsValues);

  const colorValues = [
    ...collectManualTagValues(manualTagMap, ['color', 'categorization']),
    ...Array.from(aiData.colors),
    ...notesList
      .map((note) => getEffectiveCategory(note))
      .filter((value) => value),
  ];
  const selectedColors = Array.from(
    new Set(colorValues.map((color) => normalizeColorKey(color)).filter(Boolean))
  );

  const destinationCandidates = [
    ...collectManualTagValues(manualTagMap, [
      'destination',
      'location',
      'address',
      'city',
      'place',
      'region',
    ]),
    ...Array.from(aiData.addresses),
    ...Array.from(aiData.nearby),
  ];
  const destinationSet = new Set();
  destinationCandidates.forEach((value) => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        destinationSet.add(trimmed);
      }
    }
  });
  const destinationsList = Array.from(destinationSet);

  let destinations =
    destinationsList.length > 0
      ? destinationsList.map((name, index) => ({
          id: `prefill-${index}`,
          name,
          minimumNumberOfDesiredDaysAtLocation: 1,
        }))
      : [];

  if (userHomeLocation) {
    if (destinations.length === 0) {
      destinations = [
        {
          id: 'prefill-home',
          name: userHomeLocation,
          minimumNumberOfDesiredDaysAtLocation: 0,
        },
      ];
    } else {
      destinations = [
        {
          id: 'prefill-home-start',
          name: userHomeLocation,
          minimumNumberOfDesiredDaysAtLocation: 0,
        },
        ...destinations,
        {
          id: 'prefill-home-end',
          name: userHomeLocation,
          minimumNumberOfDesiredDaysAtLocation: 0,
        },
      ];
    }
  }

  if (destinations.length === 0) {
    destinations = [
      {
        id: 'prefill-0',
        name: '',
        minimumNumberOfDesiredDaysAtLocation: 1,
      },
    ];
  }

  destinations = destinations.map((destination, index) => ({
    ...destination,
    isStart: index === 0,
    isEnd: index === destinations.length - 1,
  }));

  return {
    formData: {
      tripName,
      description,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      isFlexibleDate: false,
      category: '',
      tripType: tripTypeCandidate,
      includesKids,
      includesPets,
      receiveAlerts: false,
      alertFrequency: '',
      packingNeeds,
      interests,
      selectedColors,
      checkpoints: [],
    },
    destinations,
    selectedNotes: buildSelectedNotesSummary(notesList),
  };
};


function formatTimestamp(date) {
  const pad = (num) => (num < 10 ? '0' + num : num);
  const padMilliseconds = (num) => {
    if (num < 10) return '00' + num;
    if (num < 100) return '0' + num;
    return num.toString();
  };

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = padMilliseconds(date.getMilliseconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
}

const MainApp = () => {
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [notesPerPage, setNotesPerPage] = useState(50); // Configurable page size, default 50
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null); // Pagination token for current page
  const [paginationTokens, setPaginationTokens] = useState({}); // Store tokens for each page: { pageNumber: token }
  const [hasMorePages, setHasMorePages] = useState(false); // Whether there are more pages to load
  const [totalNotesCount, setTotalNotesCount] = useState(0); // Total count - accumulated as we fetch
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [allNotesLoaded, setAllNotesLoaded] = useState(false); // Track if we've loaded all notes to get accurate count
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [markers, setMarkers] = useState([]);
  const mapRef = useRef(null);
  const journeyOverlaysRef = useRef(new Map()); // Track journey overlays: journeyID -> { dataLayer, infoWindow }
  const journeyInfoWindowRef = useRef(null); // Single InfoWindow for journey overlays
  const currentUserIdRef = useRef(null); // Current user ID for journey ownership check (Edit button)
  const progressUpdateTimeoutRef = useRef(null); // Debounce timer for journey progress updates
  const viewportBoundsRef = useRef(null); // Current viewport bounds for lazy loading
  const loadedGeoJsonRef = useRef(new Map()); // Track loaded GeoJSON: journeyID -> { geoJson, bounds }
  const styleUpdateFrameRef = useRef(null); // RAF for throttled style updates
  const mapMoveTimeoutRef = useRef(null); // Timeout for debouncing map movements
  const skipApplyFiltersCountRef = useRef(0); // Skip N applyFilters runs after completion update (handles StrictMode double-invoke)
  const MAX_ACTIVE_JOURNEYS = 3; // Maximum number of active journeys at once
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [isNightMode, setIsNightMode] = useState(false);
  const [categorization, setCategorization] = useState(DEFAULT_CATEGORIZATION);
  const [selectedTab, setSelectedTab] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedNote, setEditedNote] = useState('');
  const [originalNote, setOriginalNote] = useState('');
  const [editedLink, setEditedLink] = useState('');
  const [newBox, setNewBox] = useState({ key: '', value: '', visible: false });
  const [editedEmailSubject, setEditedEmailSubject] = useState('');
  const [colorFilter, setColorFilter] = useState('');
  const navigate = useNavigate();
  const [isFilterActive, setIsFilterActive] = useState(false);
  const { isLoaded, loadError } = useJsApiLoader(googleMapsOptions);
  const autocompleteRef = useRef(null);
  const [clickPosition, setClickPosition] = useState(null);
  const { socket, clientId, connectionId, setConnectionId, connectionQuality } = useWebSocket(); 
  const clientIdRef = useRef(clientId);
  const [userTimezone, setUserTimezone] = useState('UTC'); // Default to UTC
  const [theme, setTheme] = useState('Light'); // Initialize theme state
  const [userAddress, setUserAddress] = useState(null); // Store user's geocoded address { lat, lng, address }
  const [isUpdatingNote, setIsUpdatingNote] = useState(false); // Add this state
  const [provider, setProvider] = useState(DEFAULT_PROVIDER); // Initialize provider state
  const [plan, setPlan] = useState(DEFAULT_PLAN); // Initialize plan state
  const providerRef = useRef(provider); // Create ref for provider
  const [showCompletedDateModal, setShowCompletedDateModal] = useState(false);
  const [pendingCompleteNoteId, setPendingCompleteNoteId] = useState(null);
  const location = useLocation();
  const [showUpgradeMessage, setShowUpgradeMessage] = useState(false);
  const [showBriefings, setShowBriefings] = useState(true);
  const [showUpgradeLimitMessage, setShowUpgradeLimitMessage] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedNoteIds, setSelectedNoteIds] = useState([]);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showExplorerHint, setShowExplorerHint] = useState(false);
  const [packitTrialComplete, setPackitTrialComplete] = useState(false);
  const [userHomeLocation, setUserHomeLocation] = useState(null);
  const [isLegendExpanded, setIsLegendExpanded] = useState(true); // Legend expanded by default
  // Helper function to validate and parse lat_long values
  const parseAndValidateLatLong = useCallback((latLongValue) => {
    // Check if value exists and is not null/undefined/empty
    if (!latLongValue || typeof latLongValue !== 'string') {
      return null;
    }

    // Check for invalid values like "TBD", "N/A", etc.
    const upperValue = latLongValue.trim().toUpperCase();
    if (upperValue === 'TBD' || upperValue === 'N/A' || upperValue === 'NULL' || upperValue === '') {
      return null;
    }

    // Try to parse the coordinates
    try {
      const parts = latLongValue.split(',').map(coord => coord.trim());
      if (parts.length !== 2) {
        return null;
      }

      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);

      // Validate that both are numbers and finite
      if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
        return null;
      }

      // Validate ranges: lat must be between -90 and 90, lng between -180 and 180
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return null;
      }

      return { lat, lng };
    } catch (error) {
      console.warn('Error parsing lat_long:', latLongValue, error);
      return null;
    }
  }, []);

  const selectedNotes = useMemo(() => {
    if (!selectedNoteIds.length) {
      return [];
    }
    const noteMap = new Map(notes.map((note) => [note.noteID, note]));
    return selectedNoteIds
      .map((id) => noteMap.get(id))
      .filter((note) => note);
  }, [notes, selectedNoteIds]);

  // Explorer Board / Journeys state
  const [activeTab, setActiveTab] = useState('quicknotes'); // 'quicknotes' or 'explorer'
  const [journeys, setJourneys] = useState([]);
  const [filteredJourneys, setFilteredJourneys] = useState([]);
  const [selectedJourneyId, setSelectedJourneyId] = useState(null);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [journeyFiltersVisible, setJourneyFiltersVisible] = useState(false);
  const [selectedJourneyFilter, setSelectedJourneyFilter] = useState('');
  const [isLoadingJourneys, setIsLoadingJourneys] = useState(false);
  const [journeyCurrentPage, setJourneyCurrentPage] = useState(1);
  const [journeysPerPage, setJourneysPerPage] = useState(10);
  const [calculatingJourneyIds, setCalculatingJourneyIds] = useState(new Set()); // Track journeys being calculated
  const [journeyFilters, setJourneyFilters] = useState({
    journeyName: '',
    destination: '',
    startDate: '',
    endDate: '',
    tripType: '',
    status: '',
    isComplete: '',
    isActive: '',
    isFavorite: '',
    progressRange: ''
  });

  const [filters, setFilters] = useState({
    completed: '',
    noteCreationDate: '',
    fromEmail: '',
    noteActive: '',
    lastEdited: '',
    recTags: '',
    activities: '',
    bodyText: '',
    emailSubject: '',
    name: '',
    description: '',
    categoryColor: '',
    categoryDescription: '',
    favorite: '',
    recTimes: ''
  });

    // Pagination is handled by ModernDashboard component

  const BriefingDisplay = () => (
    <Link to="/pack-it">Trip Pack-it</Link>
  );

  
  const normalizeKeys = (obj) => {
    // Convert all keys to lowercase
    const normalizedObj = {};
    for (const key in obj) {
      if (Object.hasOwnProperty.call(obj, key)) {
        normalizedObj[key.toLowerCase()] = obj[key];
      }
    }
    return normalizedObj;
  };

  // Extract value from API response - handles both plain strings and DynamoDB format { S: "value" }
  const extractCatValue = (v) => {
    if (v == null) return undefined;
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && v.S != null) return v.S;
    return undefined;
  };

  const handleSubscriptionUpgrade = () => {
    ReactGA.event({
      category: 'User',
      action: 'Manage Subscription Clicked',
    });

    navigate('/plan-subscription');
  };

  const getUserFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  };
    // Fetch user information, including plan, theme, and categorization
    const fetchUserInfo = async () => {

      try {
        const session = await fetchAuthSession(); // Modify as needed based on your authentication setup
        const jwtToken = session?.idToken?.jwtToken;
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
  
        // Extract the email from the JWT token
        const user = getUserFromToken(token);
        currentUserIdRef.current = user; // Store for journey ownership check (Edit button)
  
        if (!user) {
          alert('Failed to retrieve user from JWT token.');
          return;
        }
  
        const headers = {
          'Content-type': 'application/json; charset=UTF-8',
          'Authorization': `Bearer ${token}`
        };
  
        // Define the API URL with the userID path parameter (adjust as necessary)
        const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
        // Make the GET request to the API with JWT in the header
        const response = await axios.get(url, { headers });
        //console.log('response:', response);
        // Assuming the response is an array with one object
        const data = response.data[0];
        //const userData = response.data[0];
        //const data = response.data;
            // Normalize categorization keys to lowercase
        const categorizationData = normalizeKeys(data.categorization || {});
        //console.log('Normalized Categorization Data:', categorizationData); 
  
        // Extract and update user information
        setPackitTrialComplete(Boolean(data?.packitTrialComplete));

        const resolveProfileValue = (...keys) => {
          for (const key of keys) {
            if (!key) continue;
            if (typeof key === 'string' && key.trim()) {
              return key;
            }
          }
          return '';
        };

        const street = resolveProfileValue(
          data?.street,
          data?.Street,
          data?.address?.street,
          data?.address?.streetAddress,
          data?.profile?.street,
          data?.profile?.streetAddress,
          data?.location?.street
        );

        const city = resolveProfileValue(
          data?.city,
          data?.City,
          data?.homeCity,
          data?.address?.city,
          data?.profile?.city,
          data?.location?.city
        );
        const state = resolveProfileValue(
          data?.state,
          data?.State,
          data?.stateProvince,
          data?.province,
          data?.region,
          data?.address?.state,
          data?.profile?.state,
          data?.location?.state
        );
        const postalCode = resolveProfileValue(
          data?.zipCode,
          data?.zipcode,
          data?.zip,
          data?.postalCode,
          data?.postalcode,
          data?.address?.postalCode,
          data?.profile?.postalCode,
          data?.location?.postalCode
        );
        const country = resolveProfileValue(
          data?.country,
          data?.Country,
          data?.address?.country,
          data?.profile?.country,
          data?.location?.country
        );

        const combinedStatePostal = [state, postalCode].filter(Boolean).join(' ').trim();
        const locationParts = [street, city, combinedStatePostal, country]
          .map((part) => (part || '').replace(/\s+/g, ' ').trim())
          .filter(Boolean);
        const homeLocation = locationParts.join(', ');
        setUserHomeLocation(homeLocation || null);
        
        // Geocode user's home address to get coordinates for distance calculations
        if (homeLocation) {
          try {
            const location = await fetchGeolocationFromProvider(homeLocation);
            if (location) {
              setUserAddress({ lat: location.lat, lng: location.lng, address: homeLocation });
            }
          } catch (error) {
            console.error('Error geocoding user address:', error);
            setUserAddress(null);
          }
        } else {
          setUserAddress(null);
        }

        //const categorizationData = data.categorization || {};
        // Set categorization state using normalized keys; support both "ltblue" and "LightBlue" (→ lightblue)
        const ltblueVal = extractCatValue(categorizationData.ltblue) || extractCatValue(categorizationData.lightblue);
        setCategorization({
          red: extractCatValue(categorizationData.red) || DEFAULT_CATEGORIZATION.red,
          orange: extractCatValue(categorizationData.orange) || DEFAULT_CATEGORIZATION.orange,
          green: extractCatValue(categorizationData.green) || DEFAULT_CATEGORIZATION.green,
          blue: extractCatValue(categorizationData.blue) || DEFAULT_CATEGORIZATION.blue,
          yellow: extractCatValue(categorizationData.yellow) || DEFAULT_CATEGORIZATION.yellow,
          purple: extractCatValue(categorizationData.purple) || DEFAULT_CATEGORIZATION.purple,
          ltblue: ltblueVal || DEFAULT_CATEGORIZATION.ltblue,
        });
  
        //console.log('Categorization Data:', categorizationData);
        //Object.entries(categorizationData).forEach(([key, value]) => {
        //  console.log(`Key: ${key}, Value: ${value}`);
        //});

        // Update theme and isNightMode
        setTheme(data.theme || 'Light');
        setIsNightMode((data.theme || 'Light').toLowerCase() === 'dark');

        // Set the user's timezone
        setUserTimezone(data.timezone || 'UTC');
  
        // Update plan if available
        if (data.plan) {
          setPlan(data.plan);
          updatePlanAndProvider(data.plan);
          console.log(`my plan is: ` + data.plan);
          //console.log(`my plan is2: ` + plan);

          await fetchNotes(data.plan, 1, null);
        } else {
          // Fallback to default plan if not provided
          const defaultPlan = DEFAULT_PLAN || 'Basic';
          setPlan(defaultPlan);
          updatePlanAndProvider(defaultPlan);
          console.log(`No plan found in API response, using default plan: ` + defaultPlan);
          await fetchNotes(defaultPlan, 1, null);
        }
  
        // Update connectionId if available
        //if (data.clientId) {
        //  setConnectionId(data.clientId);
        //}
      } catch (error) {
        console.error('Error fetching user information:', error);
        // Use default categorization if API call fails
        setCategorization(DEFAULT_CATEGORIZATION);
      }
    };

  const fetchNotes = async (currentPlan, pageNumber = 1, paginationToken = null, pageSize = null) => {
    setIsLoadingNotes(true);
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
      
      // Use provided pageSize or current notesPerPage state
      const effectivePageSize = pageSize !== null ? pageSize : notesPerPage;
      
      // Build URL with pagination parameters
      // If effectivePageSize is 0, fetch all notes (no limit)
      let url = `${apiUrl}/notes`;
      if (effectivePageSize > 0) {
        url += `?limit=${effectivePageSize}`;
        if (paginationToken) {
          url += `&lastEvaluatedKey=${encodeURIComponent(paginationToken)}`;
        }
      } else {
        // Fetch all notes - use a large limit and paginate through all
        url += `?limit=100`;
        if (paginationToken) {
          url += `&lastEvaluatedKey=${encodeURIComponent(paginationToken)}`;
        }
      }
      
      const response = await axios.get(url, { headers });
      const data = response.data;
      console.log('Fetched notes page', pageNumber, 'Count:', data.Count, 'Has more:', !!data.lastEvaluatedKey);
      
      if (data.Items) {
        let notesToDisplay = data.Items;
        console.log('Current plan:', currentPlan);
        
        // Check if the user has a Basic plan and limit to 30 notes
        // For pagination, we'll check total count across all pages
        if (currentPlan === 'Basic') {
          // For Basic plan, we need to track total notes loaded
          // This is a simplified check - you may want to enhance this
          const totalLoaded = (pageNumber - 1) * effectivePageSize + notesToDisplay.length;
          // Limit to 30 notes total
          if (totalLoaded > 30) {
            notesToDisplay = notesToDisplay.slice(0, 30 - (pageNumber - 1) * effectivePageSize);
          }
        }

        if(currentPlan === 'Basic'){
          setShowBriefings(true);
        } else {
          setShowBriefings(true);
        }
  
        // Sort notes by lastEdited in descending order (most recent first)
        notesToDisplay.sort((a, b) => new Date(b.lastEdited) - new Date(a.lastEdited));
        
        // Update pagination state
        const newLastEvaluatedKey = data.lastEvaluatedKey || null;
        setLastEvaluatedKey(newLastEvaluatedKey);
        setHasMorePages(!!newLastEvaluatedKey);
        
        // Store pagination token for this page
        // The token stored at [pageNumber] is the token to use to get FROM this page to the next page
        // This allows us to navigate forward from any previously visited page
        if (newLastEvaluatedKey) {
          setPaginationTokens(prev => ({
            ...prev,
            [pageNumber]: newLastEvaluatedKey
          }));
        }
        
        // Deduplicate notes by noteID to prevent duplicates
        const uniqueNotes = notesToDisplay.filter((note, index, self) => 
          index === self.findIndex(n => n.noteID === note.noteID)
        );
        
        if (uniqueNotes.length !== notesToDisplay.length) {
          console.warn(`Removed ${notesToDisplay.length - uniqueNotes.length} duplicate notes from page ${pageNumber}`);
        }
        
        setNotes(uniqueNotes);
        setFilteredNotes(uniqueNotes);
        
        // Check if user should see tutorial (new user with no notes)
        if (uniqueNotes.length === 0 && pageNumber === 1) {
          const tutorialCompleted = localStorage.getItem('idealscribe_tutorial_completed');
          if (!tutorialCompleted) {
            // Show tutorial for new users with no notes
            setTimeout(() => {
              setShowTutorial(true);
            }, 1500); // Delay to let page fully load
          }
          // Show Explorer Board tab hint (pulse/badge) for new users with no notes
          if (!localStorage.getItem('idealscribe_explorer_hint_dismissed')) {
            setShowExplorerHint(true);
          }
        } else if (uniqueNotes.length > 0) {
          setShowExplorerHint(false);
        }
        
        // Update total count: if no more pages, we know the exact count
        if (!newLastEvaluatedKey) {
          // This is the last page, so we know the exact total
          const exactTotal = (pageNumber - 1) * (effectivePageSize || 100) + notesToDisplay.length;
          setTotalNotesCount(exactTotal);
          setAllNotesLoaded(true);
        } else {
          // Still more pages, estimate total (only if we have a page size)
          if (effectivePageSize > 0) {
            setTotalNotesCount((pageNumber - 1) * effectivePageSize + notesToDisplay.length + effectivePageSize);
          } else {
            // Fetching all - continue accumulating
            setTotalNotesCount(prev => prev + notesToDisplay.length);
          }
          setAllNotesLoaded(false);
        }
        
        if (notesToDisplay.length > 0) {
          geocodeAddresses(notesToDisplay);
        }
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  // Function to fetch total count of notes
  const fetchTotalNotesCount = async () => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
      
      // Fetch all notes without limit to get accurate count
      // Note: This might be slow for users with many notes, but gives accurate count
      let totalCount = 0;
      let lastKey = null;
      let pageNum = 1;
      
      do {
        let url = `${apiUrl}/notes?limit=100`; // Use larger limit to reduce requests
        if (lastKey) {
          url += `&lastEvaluatedKey=${encodeURIComponent(lastKey)}`;
        }
        
        const response = await axios.get(url, { headers });
        const data = response.data;
        
        if (data.Items) {
          totalCount += data.Items.length;
          lastKey = data.lastEvaluatedKey || null;
          pageNum++;
          
          // Safety limit to prevent infinite loops
          if (pageNum > 100) {
            console.warn('Reached safety limit while counting notes');
            break;
          }
        } else {
          break;
        }
      } while (lastKey);
      
      setTotalNotesCount(totalCount);
      setAllNotesLoaded(true);
      console.log('Total notes count:', totalCount);
    } catch (error) {
      console.error('Error fetching total notes count:', error);
    }
  };

  // Function to handle page size change
  const handlePageSizeChange = async (newPageSize) => {
    // Clear all pagination state first - this is critical to prevent duplicates
    setPaginationTokens({});
    setLastEvaluatedKey(null);
    setAllNotesLoaded(false);
    setTotalNotesCount(0);
    setCurrentPage(1);
    
    // Update page size state
    setNotesPerPage(newPageSize);
    
    // Fetch with the new page size directly (don't rely on state update)
    if (newPageSize === 0) {
      // Fetch all notes
      await fetchAllNotes(plan);
    } else {
      // Pass the new page size directly to fetchNotes to avoid stale state
      await fetchNotes(plan, 1, null, newPageSize);
    }
  };

  // Function to fetch all notes at once
  const fetchAllNotes = async (currentPlan) => {
    setIsLoadingNotes(true);
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
      
      let allNotes = [];
      let lastKey = null;
      let pageNum = 1;
      
      // Fetch all pages
      do {
        let url = `${apiUrl}/notes?limit=100`; // Use larger limit to reduce requests
        if (lastKey) {
          url += `&lastEvaluatedKey=${encodeURIComponent(lastKey)}`;
        }
        
        const response = await axios.get(url, { headers });
        const data = response.data;
        
        if (data.Items && data.Items.length > 0) {
          allNotes = [...allNotes, ...data.Items];
          lastKey = data.lastEvaluatedKey || null;
          pageNum++;
          
          // Safety limit
          if (pageNum > 100) {
            console.warn('Reached safety limit while fetching all notes');
            break;
          }
        } else {
          break;
        }
      } while (lastKey);
      
      console.log('Fetched all notes, total count:', allNotes.length);
      
      // Apply plan restrictions
      let notesToDisplay = allNotes;
      if (currentPlan === 'Basic') {
        if (allNotes.length > 30) {
          notesToDisplay = allNotes.slice(-30); // Get last 30
        }
      }
      
      // Sort notes by lastEdited in descending order
      notesToDisplay.sort((a, b) => new Date(b.lastEdited) - new Date(a.lastEdited));
      
      // Deduplicate notes by noteID to prevent duplicates
      const uniqueNotes = notesToDisplay.filter((note, index, self) => 
        index === self.findIndex(n => n.noteID === note.noteID)
      );
      
      if (uniqueNotes.length !== notesToDisplay.length) {
        console.warn(`Removed ${notesToDisplay.length - uniqueNotes.length} duplicate notes when fetching all notes`);
      }
      
      setNotes(uniqueNotes);
      setFilteredNotes(uniqueNotes);
      setTotalNotesCount(uniqueNotes.length);
      setAllNotesLoaded(true);
      setLastEvaluatedKey(null);
      setHasMorePages(false);
      
      if (notesToDisplay.length > 0) {
        geocodeAddresses(notesToDisplay);
      }
    } catch (error) {
      console.error('Error fetching all notes:', error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  // Journey API stub functions
  const fetchMyJourneys = async () => {
    setIsLoadingJourneys(true);
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
      
      // Get user ID from token
      const userID = getUserFromToken(token);
      if (!userID) {
        throw new Error('Failed to retrieve user ID from token');
      }
      
      // Fetch user data to get journeys array with journey completion data
      console.log('Fetching my journeys from user table...');
      const userResponse = await axios.get(`${apiUrl}/users/${encodeURIComponent(userID)}?journeyComplete=true`, { headers });
      const userData = userResponse.data;
      
      // Extract journeys array from response (handle both array and object response formats)
      const userJourneys = Array.isArray(userData) ? userData[0]?.journeys : userData?.journeys;
      
      // Extract journeyProgress object from response
      // Structure: { totalCompletedNotes, journeyProgress: [{ journeyID, journeyPercentComplete, compGeoBoundaries, ... }], calculatedAt }
      const journeyProgressObj = Array.isArray(userData) ? userData[0]?.journeyProgress : userData?.journeyProgress;
      const journeyProgressArray = journeyProgressObj?.journeyProgress || [];
      
      // Create a map for quick lookup: journeyID -> { journeyPercentComplete, completedLocationIds }
      const journeyProgressMap = {};
      journeyProgressArray.forEach(progress => {
        if (progress.journeyID) {
          // Extract completed location identifiers from compGeoBoundaries
          const completedLocationIds = [];
          const visitedContinents = new Set(); // Track visited continents for continentStyling
          const completedGeoids = new Set(); // Track completed GEOIDs for StateStyling_US
          const completedObjectIds = new Set(); // Track completed OBJECTIDs for WorldWHCStyling
          
          if (progress.compGeoBoundaries && Array.isArray(progress.compGeoBoundaries)) {
            progress.compGeoBoundaries.forEach(boundary => {
              if (boundary.properties) {
                // Add OBJECTID if available (for WorldWHCStyling and ParkStyling_US_National matching)
                if (boundary.properties.OBJECTID !== undefined && boundary.properties.OBJECTID !== null) {
                  completedLocationIds.push(boundary.properties.OBJECTID);
                  completedObjectIds.add(String(boundary.properties.OBJECTID)); // Store as string for matching
                }
                // Add name if available
                if (boundary.properties.name) {
                  completedLocationIds.push(boundary.properties.name);
                }
                // Add woe_id if available (for Google dataset matching)
                if (boundary.properties.woe_id !== undefined && boundary.properties.woe_id !== null) {
                  completedLocationIds.push(boundary.properties.woe_id);
                }
                // Add GEOID if available (for StateStyling_US matching)
                if (boundary.properties.GEOID !== undefined && boundary.properties.GEOID !== null) {
                  completedLocationIds.push(boundary.properties.GEOID);
                  completedGeoids.add(String(boundary.properties.GEOID)); // Store as string for matching
                }
                // Extract continent for continentStyling logic
                if (boundary.properties.continent) {
                  visitedContinents.add(boundary.properties.continent);
                }
              }
            });
          }
          
          journeyProgressMap[progress.journeyID] = {
            journeyPercentComplete: progress.journeyPercentComplete || 0,
            completedLocationIds: completedLocationIds,
            visitedContinents: Array.from(visitedContinents), // Convert Set to Array for storage
            completedGeoids: Array.from(completedGeoids), // Convert Set to Array for StateStyling_US
            completedObjectIds: Array.from(completedObjectIds) // Convert Set to Array for WorldWHCStyling
          };
        }
      });
      
      if (!userJourneys || !Array.isArray(userJourneys) || userJourneys.length === 0) {
        setJourneys([]);
        setFilteredJourneys([]);
        return;
      }
      
      // Fetch full journey details for each journey
      const enrichedJourneys = await Promise.all(
        userJourneys.map(async (userJourney) => {
          try {
            // Determine dataType from journey if available, otherwise default to geojson
            let dataType = userJourney.dataType || 'geojson';
            
            // First, fetch the journey to check mapType
            let journeyResponse = await axios.get(`${apiUrl}/journeys/${userJourney.elementID}?dataType=${dataType}`, { headers });
            let journeyDetails = journeyResponse.data;
            
            // If mapType is WorldWHCStyling or ParkStyling_US_National, fetch again with S3Reference=true
            // When S3Reference=true is used, use dataType=boundingbox
            if (journeyDetails.mapType === 'WorldWHCStyling' || journeyDetails.mapType === 'ParkStyling_US_National') {
              dataType = 'boundingbox'; // Use boundingbox when S3Reference is used
              journeyResponse = await axios.get(`${apiUrl}/journeys/${userJourney.elementID}?dataType=${dataType}&S3Reference=true`, { headers });
              journeyDetails = journeyResponse.data;
            }
            
            // Get journey completion data from the journeyProgress map
            const progressData = journeyProgressMap[userJourney.elementID] || {
              journeyPercentComplete: 0,
              completedLocationIds: [],
              visitedContinents: [],
              completedGeoids: [],
              completedObjectIds: []
            };
            
            // Use journeyPercentComplete from journeyProgress if manualPercentComplete is 0 or not set
            // Otherwise use manualPercentComplete override
            const percentComplete = (userJourney.manualPercentComplete !== undefined && 
                                     userJourney.manualPercentComplete !== null && 
                                     userJourney.manualPercentComplete !== 0)
              ? userJourney.manualPercentComplete
              : (progressData.journeyPercentComplete !== undefined ? progressData.journeyPercentComplete : 0);
            
            // Combine user journey metadata with full journey details
            return {
              ...journeyDetails,
              journeyID: userJourney.elementID,
              // User-specific journey metadata
              journeyColor: userJourney.journeyColor,
              displayItemsMode: userJourney.displayItemsMode,
              overrides: userJourney.overrides || [],
              isActive: userJourney.isActive,
              manualPercentComplete: userJourney.manualPercentComplete,
              journeyPercentComplete: progressData.journeyPercentComplete, // From journeyProgress object
              percentComplete: percentComplete, // Final calculated percentage
              journeyProgress: progressData.completedLocationIds, // Array of completed location identifiers
              visitedContinents: progressData.visitedContinents || [], // Array of visited continents for continentStyling
              completedGeoids: progressData.completedGeoids || [], // Array of completed GEOIDs for StateStyling_US
              completedObjectIds: progressData.completedObjectIds || [], // Array of completed OBJECTIDs for WorldWHCStyling
              displayJourney: userJourney.displayJourney,
              isComplete: userJourney.isComplete,
              isFavorite: userJourney.isFavorite
            };
          } catch (error) {
            console.error(`Error fetching journey details for ${userJourney.elementID}:`, error);
            
            // Get journey completion data even if details fetch fails
            const progressData = journeyProgressMap[userJourney.elementID] || {
              journeyPercentComplete: 0,
              completedLocationIds: []
            };
            
            // Use journeyPercentComplete from journeyProgress if manualPercentComplete is 0 or not set
            const percentComplete = (userJourney.manualPercentComplete !== undefined && 
                                     userJourney.manualPercentComplete !== null && 
                                     userJourney.manualPercentComplete !== 0)
              ? userJourney.manualPercentComplete
              : (progressData.journeyPercentComplete !== undefined ? progressData.journeyPercentComplete : 0);
            
            // Return user journey data even if details fetch fails
            return {
              journeyID: userJourney.elementID,
              title: `Journey ${userJourney.elementID}`,
              description: 'Journey details unavailable',
              journeyColor: userJourney.journeyColor,
              displayItemsMode: userJourney.displayItemsMode,
              overrides: userJourney.overrides || [],
              isActive: userJourney.isActive,
              manualPercentComplete: userJourney.manualPercentComplete,
              journeyPercentComplete: progressData.journeyPercentComplete,
              percentComplete: percentComplete,
              journeyProgress: progressData.completedLocationIds,
              displayJourney: userJourney.displayJourney,
              isComplete: userJourney.isComplete,
              isFavorite: userJourney.isFavorite
            };
          }
        })
      );
      
      setJourneys(enrichedJourneys);
      setFilteredJourneys(enrichedJourneys);
      
      // Note: Journey progress recalculation is NOT triggered here to prevent infinite loops
      // It should only be triggered manually (e.g., when a note is completed)
      // WebSocket messages will update journey progress silently without refetching
    } catch (error) {
      console.error('Error fetching journeys:', error);
      // On error, set empty array
      setJourneys([]);
      setFilteredJourneys([]);
    } finally {
      setIsLoadingJourneys(false);
    }
  };

  const fetchJourney = async (journeyId) => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
      // TODO: Replace with actual API endpoint
      // const response = await axios.get(`${apiUrl}/journeys/${journeyId}`, { headers });
      // const data = response.data;
      
      // Stub: Return null for now
      console.log(`Fetching journey ${journeyId} from journey API...`);
      const data = null; // Stub response
      
      setSelectedJourney(data);
      return data;
    } catch (error) {
      console.error('Error fetching journey:', error);
      setSelectedJourney(null);
      return null;
    }
  };

  const fetchAvailableJourneys = async () => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
      // TODO: Replace with actual API endpoint
      // const response = await axios.get(`${apiUrl}/journeys`, { headers });
      // const data = response.data;
      
      // Stub: Return empty array for now
      console.log('Fetching available journeys...');
      const data = []; // Stub response
      
      return data;
    } catch (error) {
      console.error('Error fetching available journeys:', error);
      return [];
    }
  };

  const createJourney = async (journeyData) => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
      // TODO: Replace with actual API endpoint
      // const response = await axios.post(`${apiUrl}/journeys`, journeyData, { headers });
      // const data = response.data;
      
      // Stub: Return mock data for now
      console.log('Creating new journey:', journeyData);
      const data = {
        journeyID: `journey-${Date.now()}`,
        ...journeyData,
        createdAt: new Date().toISOString()
      };
      
      // Refresh journeys list after creation
      await fetchMyJourneys();
      
      return data;
    } catch (error) {
      console.error('Error creating journey:', error);
      throw error;
    }
  };

  // Function to call Mapbox Geocoding API
  const fetchGeolocationFromProvider = async (address) => {
    console.log(`fetchGeolocationFromProvider`);
    try {
      const currentProvider = providerRef.current; // Use providerRef to access the current provider
      let response;
      if (currentProvider === 'mapbox') {
        console.log(`Sending request to mapbox:`);
        response = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`, {
        //response = await axios.get(`https://api.mapbox.com/search/geocode/v6/forward`, {
          //params: { q: address, access_token: mapboxApiKey, language: 'en', },
          params: {
            access_token: mapboxApiKey,
            limit: 1,
            language: 'en', // Enforce English language for address results
            types: 'address,place', // Specify types to filter results for more accurate addresses
          },
        });
        if (response.data.features && response.data.features.length > 0) {
          console.log("mapbox response: " + response.status);
          console.log("mapbox response: " + response.data.features[0].geometry.coordinates);
          const [lng, lat] = response.data.features[0].geometry.coordinates;
          return { lat, lng };
        }
      } else if (currentProvider === 'google') {
        console.log(`Sending request to google:`);
        response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
          params: { address, key: googleMapsApiKey, language: 'en', },
        });
        if (response.data.results && response.data.results.length > 0) {
          console.log("google response: " + response.status);
          console.log("google response: " + response.data.results[0].geometry.location);
          const { lat, lng } = response.data.results[0].geometry.location;
          return { lat, lng };
        }
      } else if (currentProvider === 'geocode') {
        console.log(`Sending request to geocode:`);
        response = await axios.get(`https://api.geocodify.com/v2/geocode`, {
          params: { api_key: geocodifyApiKey, q: address },
        });
        if (response.data.response.features.length > 0) {
          const location = response.data.response.features[0].geometry.coordinates;
          return { lat: location[1], lng: location[0] };
        }
      }
      throw new Error('No location found for the given address.');
    } catch (error) {
      console.error(`Error fetching location from ${providerRef.current}:`, error);
      return null;
    }
  };

  const fetchAddressFromCoordinates = async (lat, lng) => {
    console.log(`fetchAddressFromCoordinates`);
    try {
      const currentProvider = providerRef.current; // Use providerRef to access the current provider
      let response;
      if (currentProvider === 'mapbox') {
        console.log(`Sending request to mapbox lat long:`);
        response = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`, {
        //response = await axios.get(`https://api.mapbox.com/search/geocode/v6/reverse`, {
          //params: { longitude: `${lng}`, latitude: `${lat}`, access_token: mapboxApiKey, },
          params: {
            access_token: mapboxApiKey,
            limit: 1,
            language: 'en', // Enforce English language for address results
          },
        });
        if (response.data.features && response.data.features.length > 0) {
          return response.data.features[0].place_name || 'Unknown address';
        }
      } else if (currentProvider === 'google') {
        console.log(`Sending request to google lat long:`);
        response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
          params: { latlng: `${lat},${lng}`, key: googleMapsApiKey },
        });
        if (response.data.results && response.data.results.length > 0) {
          return response.data.results[0].formatted_address || 'Unknown address';
        }
      } else if (currentProvider === 'geocode') {
        console.log(`Sending request to geocode lat long:`);
        response = await axios.get(`https://api.geocodify.com/v2/reverse`, {
          params: { api_key: geocodifyApiKey, q: `${lat},${lng}` },
        });
        if (response.data.response.features.length > 0) {
          return response.data.response.features[0].properties.label || 'Unknown address';
        }
      }
      throw new Error('No address found for the given coordinates.');
    } catch (error) {
      console.error(`Error fetching address from ${providerRef.current}:`, error);
      return 'Unknown address';
    }
  };

  const centerMapOnAddress = async (address, geolat_long = null) => {
    try {
      // If geolat_long is provided, use it directly
      if (geolat_long) {
        const latLng = geolat_long.split(',').map(Number);
        setCenter({ lat: latLng[0], lng: latLng[1] });
        setZoom(5); // Adjust zoom level as needed
        return;
      }
  
      // If no geolat_long is provided, perform a geocode lookup
      if (!address || address.trim().toUpperCase() === "TBD") {
        console.warn(`Skipping geocoding for address: "${address}"`);
        return;
      }
  
      // Fetch location from Mapbox
      const location = await fetchGeolocationFromProvider(address);
      if (location) {
        setCenter(location);
        setZoom(5); // Adjust zoom level as needed
      } else {
        console.error(`Error geocoding address "${address}"`);
      }
    } catch (error) {
      console.error('Error geocoding address:', error.message);
    }
  };
  
  

  const updateNoteWithGeolatLong = async (noteID, latLng, originalNote) => {
    if (isUpdatingNote) return; // Prevent multiple updates
  
    setIsUpdatingNote(true); // Mark the update as in progress
  
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Accept': 'application/json, application/xml',
        'Authorization': `Bearer ${token}`,
      };
  
      const updatedNote = { ...originalNote };
  
      // Add the geolat_long attribute
      if (!updatedNote.aiPromptResults) {
        updatedNote.aiPromptResults = {};
      }
      updatedNote.aiPromptResults.geolat_long = { value: `${latLng[0]},${latLng[1]}` };
  
      // Preserve current lastEdited time and include other note details
      // Preserve completed status exactly as it is - don't change it unless explicitly toggled
      const preserveCompletedForGeoloc = (completedValue) => {
        if (completedValue === null || completedValue === undefined) {
          return null; // Don't include if null/undefined to preserve original state
        }
        const isCompleted = completedValue === true || completedValue === 'True' || completedValue === 'true';
        return isCompleted ? 'True' : 'False';
      };
      
      const noteDetails = {
        dateCreated: formatTimestamp(new Date(updatedNote.noteCreationDate)),
        lastUpdated: formatTimestamp(new Date(updatedNote.lastEdited)), // Keep the current lastEdited time
        fromEmail: updatedNote.fromEmail || '',
        emailSubject: updatedNote.manualPromptResults?.emailSubject || '',
        activeState: updatedNote.noteActive ? 'True' : 'False',
        ...(updatedNote.completed !== null && updatedNote.completed !== undefined && { 
          completed: preserveCompletedForGeoloc(updatedNote.completed) 
        }),
      };
  
      const tags = updatedNote.manualPromptResults?.manualTags || {};
  
      const aiRecommendations = {
        address: updatedNote.aiPromptResults?.address || { value: '', probability: 0 },
        recTags: Array.isArray(updatedNote.aiPromptResults?.recTags) ? updatedNote.aiPromptResults.recTags : [],
        activities: Array.isArray(updatedNote.aiPromptResults?.activities) ? updatedNote.aiPromptResults.activities : [],
        lat_long: updatedNote.aiPromptResults?.lat_long || { value: '', probability: 0 },
        geolat_long: updatedNote.aiPromptResults?.geolat_long || { value: `${latLng[0]},${latLng[1]}`  },
        name: updatedNote.aiPromptResults?.name || { value: '', probability: 0 },
        description: updatedNote.aiPromptResults?.description || { value: '', probability: 0 },
        recTimes: Array.isArray(updatedNote.aiPromptResults?.recTimes) ? updatedNote.aiPromptResults.recTimes : [],
        category: Array.isArray(updatedNote.aiPromptResults?.category) ? updatedNote.aiPromptResults.category : [],
        nearby: Array.isArray(updatedNote.aiPromptResults?.nearby) ? updatedNote.aiPromptResults.nearby : [],
      };
  

      const urlsArray = updatedNote.manualPromptResults?.url || [];
      const urlsPresent = urlsArray.length > 0;

      const combinedBodyText = JSON.stringify({
        bodyText: updatedNote.manualPromptResults?.bodyText || '',
        url: urlsArray,
        urlsPresent: urlsPresent,
        noteDetails,
        tags,
        aiRecommendations,
        //clientId: connectionId,
        lastEdited: formatTimestamp(new Date(originalNote.lastEdited)),
      }, null, 2);
  
      await axios.put(
        //`https://rccd7493ld.execute-api.us-west-2.amazonaws.com/dev/notes/${noteID}`,
        `${apiUrl}/notes/${noteID}`,
        { bodyText: combinedBodyText },
        { headers }
      );
  
      console.log(`Note ${noteID} updated successfully with geolat_long:`, latLng);
  
    } catch (error) {
      console.error(`Error updating note ${noteID} with geolat_long: ${error.message}`);
      if (error.response) {
        console.error(`HTTP Status Code: ${error.response.status}`);
        console.error('Response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error setting up the request:', error.message);
      }
    } finally {
      setIsUpdatingNote(false); // Mark the update as complete
    }
  };
  


  const geocodeAddresses = async (items) => {
    try {
      const geocodedMarkers = await Promise.all(
        items.map(async (item) => {
          // Use lat_long from backend - geolocation is now handled on the backend
          const latLng = parseAndValidateLatLong(item.aiPromptResults?.lat_long?.value);
  
          if (latLng) {
            const color = item.manualPromptResults?.manualTags?.Categorization || item.aiPromptResults?.category?.[0]?.color?.value;
            const marker = {
              lat: latLng.lat, // Ensure the correct order: latLng.lat is latitude, latLng.lng is longitude
              lng: latLng.lng,
              noteID: item.noteID,
              title: item.manualPromptResults?.emailSubject,
              body: item.manualPromptResults?.url,
              category: color,
            };
            return marker;
          } else {
            return null;
          }
        })
      );
  
      const validMarkers = geocodedMarkers.filter((marker) => marker !== null);
      setMarkers(validMarkers);
  
      if (validMarkers.length > 0) {
        setCenter(validMarkers[0]);
      }
    } catch (error) {
      console.error('Error processing markers:', error);
    }
  };
  


const createAdvancedMarkers = async (map, markers) => {
  const google = window.google;
  const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
  console.log(`Map Change: createAdvancedMarkers`);
  
  // Fix 2: Clear existing markers before creating new ones to prevent accumulation
  if (map.markers && Array.isArray(map.markers)) {
    map.markers.forEach(existingMarker => {
      try {
        // Remove marker from map (AdvancedMarkerElement API)
        if (existingMarker.map !== null && existingMarker.map !== undefined) {
          existingMarker.map = null;
        }
        // Clear all event listeners if available
        if (google && google.maps && google.maps.event) {
          google.maps.event.clearInstanceListeners(existingMarker);
        }
      } catch (error) {
        // Silently handle any errors during marker cleanup
        console.warn('Error cleaning up marker:', error);
      }
    });
    map.markers = [];
  } else {
    map.markers = [];
  }
  
  markers.forEach(marker => {
    // Skip markers for notes that no longer exist (e.g., after deletion)
    const note = notes.find(n => n && n.noteID === marker.noteID);
    if (!note) {
      console.warn(`Skipping marker for deleted note: ${marker.noteID}`);
      return; // Skip this marker
    }
    
    const icon = document.createElement("div");
    icon.className = marker.noteID;
    icon.textContent = marker.body;
    icon.innerHTML = '<i className="fa fa-pizza-slice fa-lg"></i>';

    const effectiveCategory = getEffectiveCategory(note);
    const iconUrl = getMarkerIconUrl(effectiveCategory, note?.completed);

    const beachFlagImg = document.createElement("img");
    beachFlagImg.src = iconUrl;

    const advancedMarker = new AdvancedMarkerElement({
      map: map,
      position: { lat: marker.lat, lng: marker.lng },
      title: marker.title,
      content: beachFlagImg,
    });
    //console.log(`Marker created with following latlong:`, advancedMarker.position);
    advancedMarker.addListener('click', () => {
      filterNotesByMarker(marker.noteID);
    });

    map.markers.push(advancedMarker);

  });
};


const getCategoryColor = (category) => {
  if (!category) return 'var(--secondary-400)';
  const lowerCategory = category.toLowerCase();
  switch (lowerCategory) {
    case 'blue':
      return 'var(--primary-500)';
    case 'red':
      return 'var(--error-500)';
    case 'green':
      return 'var(--success-500)';
    case 'purple':
      return '#8b5cf6';
    case 'pink':
      return '#ec4899';
    case 'yellow':
      return 'var(--warning-500)';
    case 'orange':
      return '#f97316';
    case 'light blue':
    case 'ltblue':
      return '#06b6d4';
    default:
      return 'var(--secondary-400)';
  }
};

const getMarkerIconUrl = (category, completed) => {
  // Handle both boolean and string formats from database
  const isCompleted = completed === true || completed === 'True' || completed === 'true';
  
  if (isCompleted) {
    // Use local images for completed icons, one png per color
    // Example: completed-blue.png, completed-red.png etc.
    const normalizedCategory = (category || '').toString().trim().toLowerCase();
    let categoryClass = normalizedCategory.replace(/\s+/g, '-');

    if (categoryClass === 'ltblue' || categoryClass === 'lightblue') {
      categoryClass = 'light-blue';
    }

    if (!categoryClass) {
      categoryClass = 'blue';
    }

    return `/images/completed-${categoryClass}.png`;
  } else {
    const lowerCaseString = category?.toLowerCase();
    switch (lowerCaseString) {
      case 'blue':
        return '/images/blue-dot.png';
      case 'red':
        return '/images/red-dot.png';
      case 'green':
        return '/images/green-dot.png';
      case 'purple':
        return '/images/purple-dot.png';
      case 'pink':
        return '/images/pink-dot.png';
      case 'yellow':
        return '/images/yellow-dot.png';
      case 'orange':
        return '/images/orange-dot.png';
      case 'light blue':
      case 'ltblue':
        return '/images/ltblue-dot.png';
      default:
        return '/images/blue-dot.png'; // Changed from red to blue as default
    }
  }
};


  const filterNotesByMarker = (noteID) => {
    const filtered = notes.filter(note => note.noteID === noteID);
    setFilteredNotes(filtered);
    setCurrentPage(1);
  };


  const handleNoteSelect = (note) => {
    const marker = markers.find((marker) => marker.noteID === note.noteID);

    if (marker && mapRef.current) {
      //console.log("Moving map too: " + marker.lat + "," + marker.lng);
      mapRef.current.panTo({ lat: marker.lat, lng: marker.lng });
      mapRef.current.setZoom(12);
    }
  };

  const handlePageChange = async (pageNumber) => {
    // Determine which pagination token to use
    let tokenToUse = null;
    
    if (pageNumber === 1) {
      // Going to page 1 - no token needed, start from beginning
      tokenToUse = null;
    } else if (pageNumber === currentPage + 1) {
      // Going forward one page - use current page's lastEvaluatedKey
      tokenToUse = lastEvaluatedKey;
    } else if (paginationTokens[pageNumber - 1]) {
      // Going to a previously visited page - use the token stored for the previous page
      // paginationTokens[pageNumber - 1] is the token to get FROM page (pageNumber - 1) TO pageNumber
      tokenToUse = paginationTokens[pageNumber - 1];
    } else {
      // Cannot jump to this page directly - need to fetch sequentially
      // This should not happen in normal usage, but handle gracefully
      console.warn(`Cannot jump to page ${pageNumber} directly. Starting from page 1.`);
      // Reset and fetch from beginning
      setPaginationTokens({});
      setLastEvaluatedKey(null);
      tokenToUse = null;
      pageNumber = 1; // Reset to page 1
    }
    
    setCurrentPage(pageNumber);
    
    // Fetch the new page
    await fetchNotes(plan, pageNumber, tokenToUse);
    
    // Scroll to top of the notes list when page changes
    // Try multiple scrollable containers to ensure we scroll the right one
    const scrollableContainers = [
      document.querySelector('.dashboard-content > *'),
      document.querySelector('.dashboard-content .notes-grid'),
      document.querySelector('.dashboard-content'),
      document.querySelector('.sidebar')
    ];
    
    scrollableContainers.forEach(container => {
      if (container) {
        container.scrollTop = 0;
      }
    });
    
    // Also scroll window to top as fallback
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'categoryColor') {
      setColorFilter(value);
      setFilters({ ...filters, [name]: value });
    } else {
      setFilters({ ...filters, [name]: value });
    }
    // Reset to page 1 when any filter changes
    setCurrentPage(1);
  };

  const handleFilterSelect = (filter) => {
    setSelectedFilter(filter);
    
    // Clear all filters first
    const clearedFilters = {
      noteCreationDate: '',
      fromEmail: '',
      noteActive: '',
      completed: '',
      lastEdited: '',
      recTags: '',
      activities: '',
      bodyText: '',
      emailSubject: '',
      name: '',
      description: '',
      categoryColor: '',
      categoryDescription: '',
      recTimes: '',
      favorite: ''
    };
    
    if (filter === 'favorite') {
      setFilters({ ...clearedFilters, favorite: 'favorite' });
    } else if (filter === 'completed') {
      setFilters({ ...clearedFilters, completed: 'true' });
    } else {
      setFilters(clearedFilters);
      setFiltersVisible(true);
    }
    // Reset to page 1 when filter type changes
    setCurrentPage(1);
  };

  const handleAddNewBox = (noteId, tabName) => {
    setEditingNoteId(noteId);
    setNewBox({ ...newBox, visible: true });
    setSelectedTab(`${noteId}-My Tags`);
  };

  const handleSaveNewBox = (noteId, tabName) => {
    setNotes(prevNotes => {
      return prevNotes.map(note => {
        if (note.noteID === noteId) {
          const updatedNote = { ...note };
          const capitalizedKey = newBox.key.charAt(0).toUpperCase() + newBox.key.slice(1).toLowerCase();

          if (tabName === 'My Tags') {
            if (!updatedNote.manualPromptResults) {
              updatedNote.manualPromptResults = {};
            }
            if (!updatedNote.manualPromptResults.manualTags) {
              updatedNote.manualPromptResults.manualTags = {};
            }
            if (capitalizedKey === 'Categorization' || capitalizedKey === 'Color') {
              const validColors = colorOptions.map(color => color.toLowerCase());
              if (!validColors.includes(newBox.value.toLowerCase())) {
                alert('Invalid color option');
                return note;
              }
            }
            updatedNote.manualPromptResults.manualTags[capitalizedKey] = newBox.value;
          } else if (tabName === 'Recommendations') {
            if (!updatedNote.aiPromptResults) {
              updatedNote.aiPromptResults = {};
            }
            updatedNote.aiPromptResults[capitalizedKey] = { value: newBox.value };
          }
          return updatedNote;
        }
        return note;
      });
    });
    setNewBox({ key: '', value: '', visible: false });
  };
  

  const applyFilters = useCallback(() => {
    if (!notes || !Array.isArray(notes)) return;

    const filtered = notes.filter((note) => {
      const { manualPromptResults, aiPromptResults, noteCreationDate, fromEmail, noteActive, completed, lastEdited, recTimes } = note;

      const bodyText = typeof manualPromptResults?.bodyText === 'string' ? manualPromptResults.bodyText.toLowerCase() : '';
      const emailSubject = typeof manualPromptResults?.emailSubject === 'string' ? manualPromptResults.emailSubject.toLowerCase() : '';
      const name = typeof aiPromptResults?.name?.value === 'string' ? aiPromptResults.name.value.toLowerCase() : '';
      const description = typeof aiPromptResults?.description?.value === 'string' ? aiPromptResults.description.value.toLowerCase() : '';
      const categoryColor = (typeof manualPromptResults?.manualTags?.Categorization === 'string'
        ? manualPromptResults.manualTags.Categorization.toLowerCase()
        : typeof aiPromptResults?.category?.[0]?.color?.value === 'string'
          ? aiPromptResults.category[0].color.value.toLowerCase()
          : '');
      const categoryDescription = typeof aiPromptResults?.category?.[0]?.description?.value === 'string' ? aiPromptResults.category[0].description.value.toLowerCase() : '';
      const recTags = typeof aiPromptResults?.recTags?.value === 'string' ? aiPromptResults.recTags.value.toLowerCase() : '';
      const activities = typeof aiPromptResults?.activities?.value === 'string' ? aiPromptResults.activities.value.toLowerCase() : '';
      const noteCreationDateLower = typeof noteCreationDate === 'string' ? noteCreationDate.toLowerCase() : '';
      const fromEmailLower = typeof fromEmail === 'string' ? fromEmail.toLowerCase() : '';
      const lastEditedLower = typeof lastEdited === 'string' ? lastEdited.toLowerCase() : '';
      const recTimesLower = typeof recTimes === 'string' ? recTimes.toLowerCase() : '';

      const filtersLower = Object.keys(filters).reduce((acc, key) => {
        acc[key] = typeof filters[key] === 'string' ? filters[key].toLowerCase() : '';
        return acc;
      }, {});
      
      

      const matchesColor = () => {
        // Only filter if color filter has a value
        if (!filtersLower.categoryColor || filtersLower.categoryColor === '') {
          return true; // Show all notes if no color filter is set
        }
        const noteKey = normalizeColorKey(getEffectiveCategory(note));
        const filterKey = normalizeColorKey(filtersLower.categoryColor);
        return Boolean(noteKey && filterKey && noteKey === filterKey);
      };

      const matchesFavorite = () => {
        // Only filter if favorite filter has a value
        if (!filtersLower.favorite || filtersLower.favorite === '') {
          return true; // Show all notes if no favorite filter is set
        }
        const manualTagFavorite = manualPromptResults?.manualTags?.Favorite?.toLowerCase();
        return manualTagFavorite === 'favorite';
      };

      // Copy the working pattern from color and favorite filters
      const matchesCompleted = () => {
        // Only filter if completed filter has a value
        if (!filtersLower.completed || filtersLower.completed === '') {
          return true; // Show all notes if no filter is set
        }
        // Handle both boolean and string formats from database
        const isCompleted = completed === true || completed === 'True' || completed === 'true';
        const completedValue = isCompleted ? 'true' : 'false';
        return completedValue === filtersLower.completed;
      };

      const matchesNoteActive = () => {
        // Only filter if noteActive filter has a value
        if (!filtersLower.noteActive || filtersLower.noteActive === '') {
          return true; // Show all notes if no filter is set
        }
        const noteActiveValue = noteActive?.toString() || '';
        return noteActiveValue.toLowerCase() === filtersLower.noteActive;
      };

      return (
        (!filtersLower.noteCreationDate || noteCreationDateLower.includes(filtersLower.noteCreationDate)) &&
        (!filtersLower.fromEmail || fromEmailLower.includes(filtersLower.fromEmail)) &&
        matchesCompleted() &&
        matchesNoteActive() &&
        (!filtersLower.lastEdited || lastEditedLower.includes(filtersLower.lastEdited)) &&
        (!filtersLower.recTags || recTags.includes(filtersLower.recTags)) &&
        (!filtersLower.activities || activities.includes(filtersLower.activities)) &&
        (!filtersLower.bodyText || bodyText.includes(filtersLower.bodyText)) &&
        (!filtersLower.emailSubject || emailSubject.includes(filtersLower.emailSubject)) &&
        (!filtersLower.name || name.includes(filtersLower.name)) &&
        (!filtersLower.description || description.includes(filtersLower.description)) &&
        matchesColor() &&
        (!filtersLower.categoryDescription || categoryDescription.includes(filtersLower.categoryDescription)) &&
        (!filtersLower.recTimes || recTimesLower.includes(filtersLower.recTimes)) &&
        matchesFavorite()
      );
    });

    setFilteredNotes(filtered);

    if (filtered.length > 0) {
      const lastNote = filtered[0];
      const address = lastNote.manualPromptResults?.manualTags?.Address || lastNote.aiPromptResults?.address?.value;
      // Use lat_long from backend - geolocation is now handled on the backend
      const latLong = lastNote.aiPromptResults?.lat_long?.value;
      //console.log("Change Map: setFilteredNotes: " + address);

      const latLng = parseAndValidateLatLong(latLong);
      if (latLng) {
        //console.log("Change Map: setFilteredNotes: " + latLng.lat + "," + latLng.lng);
        setCenter({ lat: latLng.lat, lng: latLng.lng });
        setZoom(DEFAULT_ZOOM);

        if (mapRef.current) {
          mapRef.current.panTo({ lat: latLng.lat, lng: latLng.lng });
          mapRef.current.setZoom(DEFAULT_ZOOM);
        }
      } else if (address) {
        centerMapOnAddress(address);
      }
    }
  }, [notes, filters, setFilteredNotes, setCenter, setZoom, mapRef, centerMapOnAddress]);

  const clearFilters = async () => {
    setFilters({
      noteCreationDate: '',
      fromEmail: '',
      noteActive: '',
      completed: '',
      lastEdited: '',
      recTags: '',
      activities: '',
      bodyText: '',
      emailSubject: '',
      name: '',
      description: '',
      categoryColor: '',
      categoryDescription: '',
      favorite: '',
      recTimes: ''
    });
    // Reset to first page when clearing filters
    setCurrentPage(1);
    setPaginationTokens({});
    setLastEvaluatedKey(null);
    setSelectedFilter('');
    setColorFilter('');
    // Fetch first page
    await fetchNotes(plan, 1, null);

    if (notes.length > 0) {
      const latestNote = notes[0];
      const address = latestNote.manualPromptResults?.manualTags?.Address || latestNote.aiPromptResults?.address?.value;

      if (address) {
        console.log("Gonna hit geocode: " + address);
        try {
          const location = await fetchGeolocationFromProvider(address);
          if (location) {
            setCenter(location);
            setZoom(5); // Adjust zoom level as needed
          
            if (mapRef.current) {
              mapRef.current.panTo({ lat: location.lat, lng: location.lng });
              mapRef.current.setZoom(DEFAULT_ZOOM);
            }

          } else {
            console.error(`Error geocoding address "${address}":`);
            setCenter(DEFAULT_CENTER);
            setZoom(DEFAULT_ZOOM);
            if (mapRef.current) {
              mapRef.current.panTo(DEFAULT_CENTER);
              mapRef.current.setZoom(DEFAULT_ZOOM);
            }
          }
        } catch (error) {
          console.error(`Error geocoding address "${address}": ${error.message}`);
          setCenter(DEFAULT_CENTER);
          setZoom(DEFAULT_ZOOM);
          if (mapRef.current) {
            mapRef.current.panTo(DEFAULT_CENTER);
            mapRef.current.setZoom(DEFAULT_ZOOM);
          }
        }
      } else {
        const latLng = parseAndValidateLatLong(latestNote.aiPromptResults?.lat_long?.value);
        if (latLng) {
          setCenter({ lat: latLng.lat, lng: latLng.lng });
          setZoom(DEFAULT_ZOOM);

          if (mapRef.current) {
            mapRef.current.panTo({ lat: latLng.lat, lng: latLng.lng });
            mapRef.current.setZoom(DEFAULT_ZOOM);
          }
        } else {
          setCenter(DEFAULT_CENTER);
          setZoom(DEFAULT_ZOOM);
          if (mapRef.current) {
            mapRef.current.panTo(DEFAULT_CENTER);
            mapRef.current.setZoom(DEFAULT_ZOOM);
          }
        }
      }
    } else {
      setCenter(DEFAULT_CENTER);
      setZoom(DEFAULT_ZOOM);
      if (mapRef.current) {
        mapRef.current.panTo(DEFAULT_CENTER);
        mapRef.current.setZoom(DEFAULT_ZOOM);
      }
    }
  };

  const toggleFilters = () => {
    if (filtersVisible) {
      clearFilters();
    }
    setFiltersVisible(!filtersVisible);
  };

  // Journey handlers
  const applyJourneyFilters = useCallback((filtersToApply) => {
    if (!journeys || !Array.isArray(journeys)) {
      setFilteredJourneys([]);
      return;
    }

    const filtered = journeys.filter((journey) => {
      const journeyName = (journey.title || journey.tripName || journey.name || '').toLowerCase();

      const matchesJourneyName = !filtersToApply.journeyName || filtersToApply.journeyName === '' || journeyName.includes(filtersToApply.journeyName.toLowerCase());

      // Boolean filters - only filter if value is 'true', otherwise show all
      const matchesIsComplete = !filtersToApply.isComplete || filtersToApply.isComplete === '' || 
        (filtersToApply.isComplete === 'true' ? journey.isComplete === true : true);
      const matchesIsActive = !filtersToApply.isActive || filtersToApply.isActive === '' || 
        (filtersToApply.isActive === 'true' ? journey.isActive === true : true);
      const matchesIsFavorite = !filtersToApply.isFavorite || filtersToApply.isFavorite === '' || 
        (filtersToApply.isFavorite === 'true' ? journey.isFavorite === true : true);

      // Progress range filter
      let matchesProgressRange = true;
      if (filtersToApply.progressRange) {
        const percentComplete = journey.percentComplete || journey.journeyPercentComplete || 0;
        const [min, max] = filtersToApply.progressRange.split('-').map(Number);
        matchesProgressRange = percentComplete >= min && percentComplete <= max;
      }

      return matchesJourneyName && 
             matchesIsComplete && matchesIsActive && matchesIsFavorite && matchesProgressRange;
    });

    setFilteredJourneys(filtered);
  }, [journeys]);

  const handleJourneyFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Handle checkboxes for boolean filters - use checked property instead of value
    const filterValue = type === 'checkbox' ? (checked ? 'true' : '') : value;
    const updatedFilters = { ...journeyFilters, [name]: filterValue };
    setJourneyFilters(updatedFilters);
    applyJourneyFilters(updatedFilters);
    setJourneyCurrentPage(1); // Reset to page 1 when filter changes
  };

  const handleJourneyFilterSelect = (filter) => {
    setSelectedJourneyFilter(filter);
    // Don't clear filters when selecting a filter type - let user set the value
    // Only clear if switching to a different filter type
    if (filter && filter !== selectedJourneyFilter) {
      const clearedFilters = {
        journeyName: '',
        destination: '',
        startDate: '',
        endDate: '',
        tripType: '',
        status: '',
        isComplete: '',
        isActive: '',
        isFavorite: '',
        progressRange: ''
      };
      setJourneyFilters(clearedFilters);
    }
    setJourneyCurrentPage(1); // Reset to page 1 when filter type changes
  };

  const clearJourneyFilters = () => {
    setJourneyFilters({
      journeyName: '',
      destination: '',
      startDate: '',
      endDate: '',
      tripType: '',
      status: '',
      isComplete: '',
      isActive: '',
      isFavorite: '',
      progressRange: ''
    });
    setFilteredJourneys(journeys);
    setSelectedJourneyFilter('');
    setJourneyCurrentPage(1); // Reset to page 1 when filters are cleared
  };

  const handleJourneyPageChange = (pageNumber) => {
    setJourneyCurrentPage(pageNumber);
    // Scroll to top of the journey list when page changes
    const scrollableContainers = [
      document.querySelector('.explorer-board .dashboard-content'),
      document.querySelector('.explorer-board .journey-list'),
      document.querySelector('.sidebar')
    ];
    
    scrollableContainers.forEach(container => {
      if (container) {
        container.scrollTop = 0;
      }
    });
    
    // Also scroll window to top as fallback
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleJourneyPageSizeChange = (newPageSize) => {
    setJourneysPerPage(newPageSize);
    setJourneyCurrentPage(1); // Reset to first page when changing page size
  };

  const toggleJourneyFilters = () => {
    if (journeyFiltersVisible) {
      clearJourneyFilters();
    }
    setJourneyFiltersVisible(!journeyFiltersVisible);
  };

  const handleJourneySelect = async (journeyId) => {
    setSelectedJourneyId(journeyId);
    await fetchJourney(journeyId);
    // TODO: When journey is selected, you might want to update the map to show journey-related notes
  };

  const handleJourneyUpdate = async (updatedJourneyData) => {
    // Update only the specific journey instead of refetching all journeys
    if (updatedJourneyData && updatedJourneyData.journeyID) {
      const journeyId = updatedJourneyData.journeyID || updatedJourneyData.elementID;
      
      // Handle journey removal
      if (updatedJourneyData._remove === true) {
        console.log('Removing journey from UI:', journeyId);
        
        // Remove journey from journeys state
        setJourneys((prevJourneys) => {
          const updated = prevJourneys.filter(j => j.journeyID !== journeyId);
          console.log('Journey removed from journeys list');
          return updated;
        });

        // Remove journey from filtered journeys as well
        setFilteredJourneys((prevFilteredJourneys) => {
          const updated = prevFilteredJourneys.filter(j => j.journeyID !== journeyId);
          return updated;
        });
        
        // Remove journey overlay from map if it exists
        if (journeyOverlaysRef.current.has(journeyId)) {
          const overlay = journeyOverlaysRef.current.get(journeyId);
          if (overlay && overlay.dataLayer) {
            overlay.dataLayer.setMap(null);
          }
          journeyOverlaysRef.current.delete(journeyId);
        }
        
        return; // Exit early for removal
      }
      
      console.log('Updating specific journey in UI (no full refresh):', journeyId);
      
      // Update the journey immediately by merging updated properties with existing journey data
      // This preserves all existing journey details (title, description, locationCount, etc.)
      // and only updates the changed properties (isFavorite, isComplete, manualPercentComplete, etc.)
      setJourneys((prevJourneys) => {
        const updated = prevJourneys.map((j) => {
          if (j.journeyID === journeyId) {
            const merged = { ...j, ...updatedJourneyData };
            
            // Recalculate percentComplete if manualPercentComplete or journeyPercentComplete changed
            // Use manualPercentComplete only if it's not 0, otherwise use journeyPercentComplete
            if (updatedJourneyData.manualPercentComplete !== undefined || updatedJourneyData.journeyPercentComplete !== undefined) {
              merged.percentComplete = (merged.manualPercentComplete !== undefined && 
                                       merged.manualPercentComplete !== null && 
                                       merged.manualPercentComplete !== 0)
                ? merged.manualPercentComplete
                : (merged.journeyPercentComplete !== undefined ? merged.journeyPercentComplete : (j.percentComplete || j.journeyPercentComplete || 0));
            }
            
            console.log('Merged journey update:', { 
              journeyID: journeyId, 
              updatedProperties: Object.keys(updatedJourneyData),
              mergedJourney: merged 
            });
            return merged;
          }
          return j;
        });
        
        return updated;
      });

      // Update filtered journeys as well
      setFilteredJourneys((prevFilteredJourneys) => {
        return prevFilteredJourneys.map((j) => {
          if (j.journeyID === journeyId) {
            const merged = { ...j, ...updatedJourneyData };
            
            // Recalculate percentComplete if manualPercentComplete or journeyPercentComplete changed
            if (updatedJourneyData.manualPercentComplete !== undefined || updatedJourneyData.journeyPercentComplete !== undefined) {
              merged.percentComplete = merged.manualPercentComplete !== undefined && merged.manualPercentComplete !== null
                ? merged.manualPercentComplete
                : (merged.journeyPercentComplete !== undefined ? merged.journeyPercentComplete : (j.percentComplete || 0));
            }
            
            return merged;
          }
          return j;
        });
      });
    }
  };

  const handleCreateJourney = async () => {
    // Refresh the journey list after a new journey is created
    await fetchMyJourneys();
  };

  const handleAddJourney = async (journeyDetails) => {
    if (!journeyDetails) {
      // If no journey details provided, the modal will handle the search
      // This function is called from the modal when a journey is selected
      return;
    }

    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
      
      // TODO: Replace with actual API endpoint to add journey to user's journeys
      // This should POST to something like: ${apiUrl}/user/journeys
      // For now, we'll add it locally and refresh the list
      console.log('Adding journey to user journeys:', journeyDetails);
      
      // Mark journey as calculating - backend will calculate progress
      setCalculatingJourneyIds(prev => new Set([...prev, journeyDetails.journeyID]));
      
      // Add the journey to local state temporarily
      // In production, this should be handled by the backend
      const newJourney = {
        journeyID: journeyDetails.journeyID,
        tripName: journeyDetails.title,
        name: journeyDetails.title,
        description: journeyDetails.description,
        status: 'upcoming',
        destination: journeyDetails.scope?.countries || [],
        startDate: null,
        endDate: null,
        tripType: journeyDetails.type || 'custom',
        ...journeyDetails
      };
      
      // Refresh journeys list after adding
      await fetchMyJourneys();
    } catch (error) {
      console.error('Error adding journey:', error);
    }
  };

  const toggleNightMode = () => {
    setIsNightMode(!isNightMode);
  };

  const truncateText = (text, wordLimit) => {
    const words = text.split(' ');
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(' ') + '...';
    }
    return text;
  };

  const canConvertNotes =
    !packitTrialComplete ||
    (typeof plan === 'string' && plan.trim().toLowerCase() !== 'basic');

  // Manage upgrade messages - only show when loading is complete and we have accurate note counts
  useEffect(() => {
    // Don't show messages while loading or if we don't have accurate counts yet
    if (isLoadingNotes || !allNotesLoaded) {
      setShowUpgradeMessage(false);
      setShowUpgradeLimitMessage(false);
      return;
    }

    // Only show messages for Basic plan users
    if (plan !== 'Basic') {
      setShowUpgradeMessage(false);
      setShowUpgradeLimitMessage(false);
      return;
    }

    // Use totalNotesCount if available and accurate, otherwise use notes.length
    const noteCount = totalNotesCount > 0 ? totalNotesCount : notes.length;

    // Show warning message when approaching limit (21-29 notes)
    if (noteCount > 20 && noteCount < 30) {
      setShowUpgradeMessage(true);
      setShowUpgradeLimitMessage(false);
    }
    // Show limit message when at or over limit (30+ notes)
    else if (noteCount >= 30) {
      setShowUpgradeMessage(false);
      setShowUpgradeLimitMessage(true);
    }
    // Hide messages when under limit
    else {
      setShowUpgradeMessage(false);
      setShowUpgradeLimitMessage(false);
    }
  }, [isLoadingNotes, allNotesLoaded, plan, notes.length, totalNotesCount]);

  // For API pagination, filteredNotes already contains only the current page
  const currentNotes = filteredNotes;
  const currentJourneys = filteredJourneys.slice((journeyCurrentPage - 1) * journeysPerPage, journeyCurrentPage * journeysPerPage);

  const updateMapMarkers = async () => {
    if (mapRef.current) {
      if (mapRef.current.markers) {
        mapRef.current.markers.forEach(marker => marker.setMap(null));
        mapRef.current.markers = [];
      }
      //console.log("Here55");
      //console.log("Here55" + markers[0].lat + "," + markers[0].lng);
      
      await createAdvancedMarkers(mapRef.current, markers);
    }
  };

  // Helper function to convert journeyColor object {r, g, b} to hex
  const journeyColorToHex = (journeyColor) => {
    if (!journeyColor) {
      return '#4285F4'; // Default blue
    }
    
    // Handle object format {r, g, b}
    if (typeof journeyColor === 'object' && journeyColor.r !== undefined) {
      const r = Math.max(0, Math.min(255, journeyColor.r));
      const g = Math.max(0, Math.min(255, journeyColor.g));
      const b = Math.max(0, Math.min(255, journeyColor.b));
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }
    
    // Fallback to default blue
    return '#4285F4';
  };

  // Throttle utility function (memoized to prevent recreation)
  const throttle = useMemo(() => {
    return (func, delay) => {
      let lastCall = 0;
      return function(...args) {
        const now = Date.now();
        if (now - lastCall >= delay) {
          lastCall = now;
          return func.apply(this, args);
        }
      };
    };
  }, []); // Only create once

  // Check if journey bounds intersect with viewport
  const isJourneyInViewport = (journey, viewportBounds) => {
    if (!viewportBounds || !journey.geospatialBoundaries) return true; // Default to true if no bounds
    
    // For bounding box format, check if any location intersects viewport
    if (typeof journey.geospatialBoundaries === 'object' && !journey.geospatialBoundaries.type) {
      return Object.values(journey.geospatialBoundaries).some(location => {
        if (location.bounds) {
          const { minLon, maxLon, minLat, maxLat } = location.bounds;
          return !(maxLon < viewportBounds.getSouthWest().lng() || 
                   minLon > viewportBounds.getNorthEast().lng() ||
                   maxLat < viewportBounds.getSouthWest().lat() || 
                   minLat > viewportBounds.getNorthEast().lat());
        }
        return false;
      });
    }
    
    // For GeoJSON, check if any feature intersects (simplified check)
    if (journey.geospatialBoundaries.type === 'FeatureCollection') {
      return journey.geospatialBoundaries.features.some(feature => {
        if (feature.geometry && feature.geometry.coordinates) {
          // Simple bounding box check for first coordinate
          const coords = feature.geometry.coordinates;
          if (coords.length > 0 && coords[0].length > 0) {
            const [lng, lat] = Array.isArray(coords[0][0]) ? coords[0][0] : coords[0];
            return viewportBounds.contains(new window.google.maps.LatLng(lat, lng));
          }
        }
        return false;
      });
    }
    
    return true; // Default to true if format unknown
  };

  // Get journey priority for limiting active journeys
  const getJourneyPriority = (journey) => {
    let priority = 0;
    if (journey.isFavorite) priority += 10;
    if (journey.isActive) priority += 5;
    if (journey.percentComplete > 0) priority += 2;
    return priority;
  };

  // Function to update journey overlays on the map
  // Helper function to log memory diagnostics
  const logMemoryDiagnostics = (operation, details = {}) => {
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && window.performance && window.performance.memory) {
      const memory = window.performance.memory;
      const memoryInfo = {
        used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
        total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
        limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024)
      };
      
      const overlayCount = journeyOverlaysRef.current.size;
      const geoJsonCount = loadedGeoJsonRef.current.size;
      
      console.log(`[Memory] ${operation}:`, {
        ...memoryInfo,
        overlayCount,
        geoJsonCount,
        ...details
      });
    }
  };

  const updateJourneyOverlays = (map, journeysToUpdate = null) => {
    if (!map || !window.google || !window.google.maps) return;
    
    logMemoryDiagnostics('updateJourneyOverlays START', {
      journeysToUpdate: journeysToUpdate?.length,
      totalJourneys: journeys?.length
    });
    
    const journeysToProcess = journeysToUpdate || journeys;
    
    // Get journeys that should be displayed
    let journeysToDisplay = journeysToProcess.filter(j => j.displayJourney === true);
    
    // Get current viewport bounds for lazy loading
    try {
      const bounds = map.getBounds();
      viewportBoundsRef.current = bounds;
    } catch (e) {
      // Map might not be ready yet
      viewportBoundsRef.current = null;
    }
    
    // Limit active journeys: prioritize favorites, active, and in-progress journeys
    if (journeysToDisplay.length > MAX_ACTIVE_JOURNEYS) {
      journeysToDisplay = journeysToDisplay
        .map(j => ({ journey: j, priority: getJourneyPriority(j) }))
        .sort((a, b) => b.priority - a.priority)
        .slice(0, MAX_ACTIVE_JOURNEYS)
        .map(item => item.journey);
    }
    
    // Filter journeys by viewport (lazy loading) - only for journeys without dataSetID
    // Dataset layers are handled by Google Maps, so we always show them
    // IMPORTANT: If displayJourney is true, always show the journey regardless of viewport
    const viewportBounds = viewportBoundsRef.current;
    if (viewportBounds) {
      journeysToDisplay = journeysToDisplay.filter(journey => {
        // Always show journeys that are explicitly set to display (displayJourney === true)
        if (journey.displayJourney === true) return true;
        // Always show dataset-based journeys (they're efficient)
        if (journey.dataSetID) return true;
        // For GeoJSON journeys, check viewport intersection (only for journeys not explicitly set to display)
        return isJourneyInViewport(journey, viewportBounds);
      });
    }
    
    // Get current journey IDs that should be displayed
    const currentDisplayedIds = new Set(journeysToDisplay.map(j => j.journeyID));
    
    // Remove ALL overlays first, then recreate only the ones that should be displayed
    // This ensures we don't have lingering overlays
    const overlaysToRemove = journeyOverlaysRef.current.size;
    if (process.env.NODE_ENV === 'development' && overlaysToRemove > 0) {
      console.log(`[Memory] Removing ${overlaysToRemove} overlays`);
    }
    
    journeyOverlaysRef.current.forEach((overlayData, journeyID) => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Memory] Removing overlay for journey ${journeyID}`);
        }
        // Handle both Data layer and Dataset feature layer
        if (overlayData.dataLayer) {
          // Remove all features from the data layer
          overlayData.dataLayer.forEach((feature) => {
            overlayData.dataLayer.remove(feature);
          });
          // Remove the data layer from the map
          overlayData.dataLayer.setMap(null);
        }
        // Handle dataset feature layer
        if (overlayData.datasetLayer) {
          // Remove click listener if it exists
          if (overlayData.clickListener) {
            try {
              window.google.maps.event.removeListener(overlayData.clickListener);
              overlayData.clickListener = null; // Clear reference
            } catch (e) {
              // Listener might already be removed
            }
          }
          // Remove style from dataset layer (this releases closures)
          overlayData.datasetLayer.style = null;
          // Clear the dataset layer reference
          overlayData.datasetLayer = null;
        }
        // Handle data layer click listener
        if (overlayData.dataLayerClickListener) {
          try {
            window.google.maps.event.removeListener(overlayData.dataLayerClickListener);
            overlayData.dataLayerClickListener = null; // Clear reference
          } catch (e) {
            // Listener might already be removed
          }
        }
        // Clear data layer reference
        if (overlayData.dataLayer) {
          overlayData.dataLayer = null;
        }
        // Clean up RAF callback if it exists
        if (overlayData.rafId !== null && overlayData.rafId !== undefined) {
          try {
            cancelAnimationFrame(overlayData.rafId);
          } catch (e) {
            // RAF might already be executed or canceled
          }
          overlayData.rafId = null; // Clear reference
        }
        // Close any open info windows
        if (overlayData.infoWindow) {
          overlayData.infoWindow.close();
        }
      } catch (error) {
        console.error(`Error removing overlay for ${journeyID}:`, error);
      }
    });
    // Clear the entire map
    journeyOverlaysRef.current.clear();
    
    // Clear GeoJSON from memory for journeys that are no longer displayed
    const displayedJourneyIds = new Set(journeysToDisplay.map(j => j.journeyID));
    loadedGeoJsonRef.current.forEach((geoJsonData, journeyID) => {
      if (!displayedJourneyIds.has(journeyID)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Memory] Clearing GeoJSON from memory for journey ${journeyID}`);
        }
        loadedGeoJsonRef.current.delete(journeyID);
      }
    });
    
    logMemoryDiagnostics('updateJourneyOverlays AFTER CLEAR', {
      overlaysRemoved: overlaysToRemove,
      journeysToAdd: journeysToDisplay.length,
      geoJsonCleared: overlaysToRemove - journeysToDisplay.length
    });
    
    // Initialize InfoWindow if needed
    if (!journeyInfoWindowRef.current) {
      journeyInfoWindowRef.current = new window.google.maps.InfoWindow();
    }
    
    // Group journeys by dataSetID to handle multiple journeys sharing the same dataset
    const journeysByDataSet = new Map(); // dataSetID -> [journeys]
    const journeysWithoutDataSet = []; // Journeys without dataSetID (use GeoJSON)
    
    journeysToDisplay.forEach((journey) => {
      if (journey.dataSetID) {
        if (!journeysByDataSet.has(journey.dataSetID)) {
          journeysByDataSet.set(journey.dataSetID, []);
        }
        journeysByDataSet.get(journey.dataSetID).push(journey);
      } else {
        journeysWithoutDataSet.push(journey);
      }
    });
    
    // Process journeys grouped by dataSetID
    journeysByDataSet.forEach((journeyGroup, dataSetID) => {
      if (journeyGroup.length === 0) return;
      
      // Get the dataset layer (shared by all journeys in this group)
      const datasetLayer = map.getDatasetFeatureLayer(dataSetID);
      if (!datasetLayer) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Dataset layer not found for dataSetID: ${dataSetID}`);
        }
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Setting up combined dataset layer for ${journeyGroup.length} journey(s) with dataSetID: ${dataSetID}`);
        journeyGroup.forEach(j => {
          console.log(`  - Journey ${j.journeyID} (${j.title || 'Untitled'}): displayJourney=${j.displayJourney}, displayItemsMode=${j.displayItemsMode}, mapType=${j.mapType}, completedObjectIds=${Array.isArray(j.completedObjectIds) ? j.completedObjectIds.length : 'N/A'}, journeyProgress=${Array.isArray(j.journeyProgress) ? j.journeyProgress.length : 'N/A'}`);
        });
      }
      
      // Prepare style data for all journeys in this group
      const journeyStyleData = journeyGroup.map((journey) => {
        // Get RGB color from journey and convert to hex for Google Maps
        const hexColor = journeyColorToHex(journey.journeyColor);
        
        // Get journeyProgress for this journey
        const journeyProgress = journey.journeyProgress || [];
        
        // Get displayItemsMode for this journey (default to "Both" if missing)
        const displayItemsMode = journey.displayItemsMode || 'Both';
        
        // Get mapType to determine styling logic
        const mapType = journey.mapType || '';
        const isContinentStyling = mapType === 'continentStyling';
        const isStateStylingUS = mapType === 'StateStyling_US';
        const isWorldWHCStyling = mapType === 'WorldWHCStyling';
        const isParkStylingUSNational = mapType === 'ParkStyling_US_National';
        
        // Get visited continents for continentStyling logic
        const visitedContinents = new Set(journey.visitedContinents || []);
        
        // Create Sets for completion tracking
        const completedWoeIds = new Set();
        const completedGeoids = new Set();
        const completedObjectIds = new Set();
        const allowedObjectIds = new Set();
        
        const MAX_SET_SIZE = 7000;
        
        // Extract OBJECTIDs from journey's GeoJSON/bounding box for filtering
        if ((isParkStylingUSNational || isWorldWHCStyling) && journey.geospatialBoundaries) {
          const addObjectId = (objId) => {
            if (objId === undefined || objId === null) return;
            allowedObjectIds.add(String(objId));
            if (typeof objId === 'number' || (!isNaN(objId) && !isNaN(parseFloat(objId)))) {
              allowedObjectIds.add(String(Number(objId)));
            }
          };
          
          if (journey.geospatialBoundaries.type === 'FeatureCollection' && Array.isArray(journey.geospatialBoundaries.features)) {
            journey.geospatialBoundaries.features.forEach(feature => {
              if (feature.properties && feature.properties.OBJECTID !== undefined && feature.properties.OBJECTID !== null) {
                addObjectId(feature.properties.OBJECTID);
              }
            });
          } else if (Array.isArray(journey.geospatialBoundaries)) {
            journey.geospatialBoundaries.forEach(feature => {
              if (feature.properties && feature.properties.OBJECTID !== undefined && feature.properties.OBJECTID !== null) {
                addObjectId(feature.properties.OBJECTID);
              }
            });
          } else if (journey.geospatialBoundaries.features && Array.isArray(journey.geospatialBoundaries.features)) {
            journey.geospatialBoundaries.features.forEach(feature => {
              if (feature.properties && feature.properties.OBJECTID !== undefined && feature.properties.OBJECTID !== null) {
                addObjectId(feature.properties.OBJECTID);
              }
            });
          } else if (typeof journey.geospatialBoundaries === 'object' && !journey.geospatialBoundaries.type) {
            Object.values(journey.geospatialBoundaries).forEach(location => {
              if (location) {
                if (location.properties && location.properties.OBJECTID !== undefined && location.properties.OBJECTID !== null) {
                  addObjectId(location.properties.OBJECTID);
                }
                if (location.OBJECTID !== undefined && location.OBJECTID !== null) {
                  addObjectId(location.OBJECTID);
                }
              }
            });
          }
        }
        
        // Add from journey.completedGeoids and journey.completedObjectIds if they exist
        if (Array.isArray(journey.completedGeoids) && journey.completedGeoids.length <= MAX_SET_SIZE) {
          journey.completedGeoids.forEach(geoid => completedGeoids.add(String(geoid)));
        }
        if (Array.isArray(journey.completedObjectIds) && journey.completedObjectIds.length <= MAX_SET_SIZE) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`[completedObjectIds] Journey ${journey.journeyID} (${journey.title || 'Untitled'}): Adding ${journey.completedObjectIds.length} OBJECTIDs from journey.completedObjectIds:`, journey.completedObjectIds.slice(0, 10));
          }
          journey.completedObjectIds.forEach(objId => {
            const objIdStr = String(objId);
            completedObjectIds.add(objIdStr);
            // For countryStyling, also add to completedWoeIds if it's a numeric woe_id
            if (!isParkStylingUSNational && !isWorldWHCStyling && !isStateStylingUS && !isContinentStyling) {
              // This is likely countryStyling, so objId might be a woe_id
              const woeIdNum = typeof objId === 'number' ? objId : (isNaN(objId) ? null : Number(objId));
              if (woeIdNum !== null && !isNaN(woeIdNum)) {
                completedWoeIds.add(woeIdNum);
              }
            }
          });
        } else if (process.env.NODE_ENV === 'development') {
          console.log(`[completedObjectIds] Journey ${journey.journeyID} (${journey.title || 'Untitled'}): journey.completedObjectIds is not an array or is too large:`, {
            isArray: Array.isArray(journey.completedObjectIds),
            length: journey.completedObjectIds?.length,
            value: journey.completedObjectIds
          });
        }
        
        // Process journeyProgress
        if (Array.isArray(journeyProgress) && journeyProgress.length <= MAX_SET_SIZE) {
          if (process.env.NODE_ENV === 'development' && journeyProgress.length > 0) {
            console.log(`[completedObjectIds] Journey ${journey.journeyID} (${journey.title || 'Untitled'}): Processing ${journeyProgress.length} items from journeyProgress:`, journeyProgress.slice(0, 10));
          }
          journeyProgress.forEach(progressId => {
            if (typeof progressId === 'number' || (typeof progressId === 'string' && !isNaN(progressId))) {
              completedWoeIds.add(Number(progressId));
            }
            if (typeof progressId === 'string' && progressId.length > 0) {
              completedGeoids.add(String(progressId));
            }
            if (typeof progressId === 'string' || typeof progressId === 'number') {
              completedObjectIds.add(String(progressId));
            }
          });
        }
        
        // Log final Set sizes for debugging
        if (process.env.NODE_ENV === 'development') {
          console.log(`[completedObjectIds] Journey ${journey.journeyID} (${journey.title || 'Untitled'}): Final Set sizes:`, {
            mapType: journey.mapType,
            displayItemsMode: displayItemsMode,
            isParkStylingUSNational: isParkStylingUSNational,
            isWorldWHCStyling: isWorldWHCStyling,
            isStateStylingUS: isStateStylingUS,
            isContinentStyling: isContinentStyling,
            completedWoeIds: completedWoeIds.size,
            completedGeoids: completedGeoids.size,
            completedObjectIds: completedObjectIds.size,
            sampleCompletedWoeIds: Array.from(completedWoeIds).slice(0, 10),
            sampleCompletedGeoids: Array.from(completedGeoids).slice(0, 10),
            sampleCompletedObjectIds: Array.from(completedObjectIds).slice(0, 10),
            fromJourneyCompletedObjectIds: Array.isArray(journey.completedObjectIds) ? journey.completedObjectIds.length : 0,
            fromJourneyProgress: Array.isArray(journeyProgress) ? journeyProgress.length : 0
          });
        }
        
        // Check if the entire journey is complete
        const isJourneyComplete = journey.isComplete === true || 
                                  journey.manualPercentComplete === 100 ||
                                  journey.percentComplete === 100;
        
        // Pre-compute display mode flags
        const showVisitedOnly = displayItemsMode === 'visited';
        const showNotVisitedOnly = displayItemsMode === 'notVisited';
        const showBoth = displayItemsMode === 'Both';
        
        // Determine filter key field based on mapType
        const filterKeyField = isStateStylingUS ? 'GEOID' : 
                              (isWorldWHCStyling || isParkStylingUSNational) ? 'OBJECTID' :
                              isContinentStyling ? 'continent' : 'woe_id';
        
        return {
          journey,
          hexColor,
          isContinentStyling,
          isStateStylingUS,
          isWorldWHCStyling,
          isParkStylingUSNational,
          visitedContinents,
          completedWoeIds,
          completedGeoids,
          completedObjectIds,
          allowedObjectIds,
          isJourneyComplete,
          showVisitedOnly,
          showNotVisitedOnly,
          showBoth,
          filterKeyField,
          displayJourney: journey.displayJourney !== false // Default to true if not explicitly false
        };
      });
      
      // Create combined style function that checks all journeys in the group
      const combinedStyleFunction = ({ feature }) => {
        if (!feature) return null;
        
        // Get feature identifier based on mapType
        const attrs = feature.datasetAttributes;
        const props = feature.feature?.properties;
        let featureId = null;
        
        // Determine which field to use based on the first journey's mapType (they should all be the same for a dataSetID)
        const firstJourneyMapType = journeyStyleData.length > 0 ? journeyStyleData[0].journey.mapType : '';
        const isCountryStyling = firstJourneyMapType === 'countryStyling' || (!firstJourneyMapType && attrs?.woe_id && !attrs?.OBJECTID);
        const isParkStyling = firstJourneyMapType === 'ParkStyling_US_National' || firstJourneyMapType === 'WorldWHCStyling';
        const isStateStyling = firstJourneyMapType === 'StateStyling_US';
        const isContinentStyling = firstJourneyMapType === 'continentStyling';
        
        if (attrs) {
          // Prioritize based on mapType
          if (isParkStyling) {
            featureId = attrs.OBJECTID || attrs.GEOID || attrs.continent || attrs.woe_id;
          } else if (isStateStyling) {
            featureId = attrs.GEOID || attrs.OBJECTID || attrs.continent || attrs.woe_id;
          } else if (isContinentStyling) {
            featureId = attrs.continent || attrs.OBJECTID || attrs.GEOID || attrs.woe_id;
          } else if (isCountryStyling) {
            featureId = attrs.woe_id || attrs.OBJECTID || attrs.GEOID || attrs.continent;
          } else {
            featureId = attrs.OBJECTID || attrs.GEOID || attrs.continent || attrs.woe_id;
          }
        } else if (props) {
          if (isParkStyling) {
            featureId = props.OBJECTID || props.GEOID || props.continent || props.woe_id;
          } else if (isStateStyling) {
            featureId = props.GEOID || props.OBJECTID || props.continent || props.woe_id;
          } else if (isContinentStyling) {
            featureId = props.continent || props.OBJECTID || props.GEOID || props.woe_id;
          } else if (isCountryStyling) {
            featureId = props.woe_id || props.OBJECTID || props.GEOID || props.continent;
          } else {
            featureId = props.OBJECTID || props.GEOID || props.continent || props.woe_id;
          }
        }
        
        if (featureId === null || featureId === undefined) {
          if (process.env.NODE_ENV === 'development' && Math.random() < 0.001) {
            console.log(`[completedObjectIds] Feature has no identifier:`, { attrs, props, mapType: firstJourneyMapType });
          }
          return null;
        }
        
        // Log feature identifier extraction (sample only)
        if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
          console.log(`[completedObjectIds] Extracted featureId:`, {
            featureId: featureId,
            featureIdType: typeof featureId,
            mapType: firstJourneyMapType,
            fromAttrs: !!attrs,
            fromProps: !!props,
            attrsKeys: attrs ? Object.keys(attrs).slice(0, 5) : null,
            propsKeys: props ? Object.keys(props).slice(0, 5) : null
          });
        }
        
        // Find the first matching journey for this feature
        for (const styleData of journeyStyleData) {
          // Skip if this journey is not set to display
          if (!styleData.displayJourney) continue;
          
          // Check if feature is allowed by this journey (filtering)
          if ((styleData.isParkStylingUSNational || styleData.isWorldWHCStyling) && styleData.allowedObjectIds.size > 0) {
            const featureIdStr = String(featureId);
            const featureIdNum = typeof featureId === 'number' ? featureId : (isNaN(featureId) ? null : Number(featureId));
            const isAllowed = styleData.allowedObjectIds.has(featureIdStr) || 
                            (featureIdNum !== null && styleData.allowedObjectIds.has(String(featureIdNum)));
            if (!isAllowed) continue; // Skip this journey, feature not in its allowed list
          }
          
          // Determine completion status
          let isCompleted = false;
          if (styleData.isJourneyComplete) {
            isCompleted = true;
          } else if (styleData.isStateStylingUS) {
            // Check GEOID matching (handle both string formats)
            const geoidStr = String(featureId);
            isCompleted = styleData.completedGeoids.has(geoidStr);
            
            // Log completion check for debugging (sample only to avoid spam)
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
              console.log(`[completedObjectIds] Feature GEOID check for journey ${styleData.journey.journeyID}:`, {
                featureId: featureId,
                featureIdType: typeof featureId,
                geoidStr: geoidStr,
                completedGeoidsSize: styleData.completedGeoids.size,
                isCompleted: isCompleted,
                sampleCompletedGeoids: Array.from(styleData.completedGeoids).slice(0, 10)
              });
            }
          } else if (styleData.isWorldWHCStyling || styleData.isParkStylingUSNational) {
            // Check both string and number formats for OBJECTID matching
            const featureIdStr = String(featureId);
            const featureIdNum = typeof featureId === 'number' ? featureId : (isNaN(featureId) ? null : Number(featureId));
            const hasString = styleData.completedObjectIds.has(featureIdStr);
            const hasNumber = featureIdNum !== null && !isNaN(featureIdNum) && styleData.completedObjectIds.has(String(featureIdNum));
            isCompleted = hasString || hasNumber;
            
            // Log completion check for debugging (sample only to avoid spam)
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
              console.log(`[completedObjectIds] Feature OBJECTID check for journey ${styleData.journey.journeyID}:`, {
                featureId: featureId,
                featureIdType: typeof featureId,
                featureIdStr: featureIdStr,
                featureIdNum: featureIdNum,
                completedObjectIdsSize: styleData.completedObjectIds.size,
                hasString: hasString,
                hasNumber: hasNumber,
                isCompleted: isCompleted,
                sampleCompletedObjectIds: Array.from(styleData.completedObjectIds).slice(0, 10)
              });
            }
          } else if (styleData.isContinentStyling) {
            isCompleted = styleData.visitedContinents.has(featureId);
          } else {
            // For countryStyling, check completedWoeIds
            // Handle both string and number formats for woe_id
            const woeIdNum = typeof featureId === 'number' ? featureId : (isNaN(featureId) ? null : Number(featureId));
            const woeIdStr = String(featureId);
            isCompleted = (woeIdNum !== null && !isNaN(woeIdNum) && styleData.completedWoeIds.has(woeIdNum)) ||
                         styleData.completedWoeIds.has(Number(woeIdStr));
            
            // Log completion check for debugging (sample only to avoid spam)
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) {
              console.log(`[completedObjectIds] Feature woe_id check for journey ${styleData.journey.journeyID}:`, {
                featureId: featureId,
                featureIdType: typeof featureId,
                woeIdNum: woeIdNum,
                woeIdStr: woeIdStr,
                completedWoeIdsSize: styleData.completedWoeIds.size,
                hasNumber: woeIdNum !== null && !isNaN(woeIdNum) && styleData.completedWoeIds.has(woeIdNum),
                hasString: styleData.completedWoeIds.has(Number(woeIdStr)),
                isCompleted: isCompleted,
                sampleCompletedWoeIds: Array.from(styleData.completedWoeIds).slice(0, 10)
              });
            }
          }
          
          // Determine visibility
          if (styleData.isContinentStyling) {
            const continent = featureId;
            if (styleData.showVisitedOnly) {
              if (!continent || !styleData.visitedContinents.has(continent)) continue;
            } else if (styleData.showNotVisitedOnly) {
              if (styleData.isJourneyComplete || !continent || styleData.visitedContinents.has(continent)) continue;
            }
          } else {
            if (styleData.showVisitedOnly && !isCompleted) continue;
            if (styleData.showNotVisitedOnly) {
              if (styleData.isJourneyComplete || isCompleted) continue;
            }
          }
          
          // This journey matches - apply its style with proper opacity differentiation
          let opacity, strokeOpacity;
          if (styleData.isContinentStyling) {
            const continent = featureId;
            const isContinentVisited = continent && styleData.visitedContinents.has(continent);
            opacity = isContinentVisited ? 0.8 : 0.3;
            strokeOpacity = isContinentVisited ? 0.8 : 0.5;
          } else {
            // For all other map types (countries, states, parks, etc.), differentiate opacity based on completion
            // In "Both" mode: completed = 0.8, not completed = 0.3
            // In "visited" mode: all shown are completed = 0.8
            // In "notVisited" mode: all shown are not completed = 0.3
            if (styleData.showBoth || (!styleData.showVisitedOnly && !styleData.showNotVisitedOnly)) {
              // Both mode: show different opacity for completed vs not completed
              opacity = isCompleted ? 0.8 : 0.3;
              strokeOpacity = isCompleted ? 0.8 : 0.5;
            } else if (styleData.showVisitedOnly) {
              // Visited only mode: all shown are completed, use higher opacity
              opacity = 0.8;
              strokeOpacity = 0.8;
            } else {
              // Not visited only mode: all shown are not completed, use lower opacity
              opacity = 0.3;
              strokeOpacity = 0.5;
            }
          }
          
          return {
            fillColor: styleData.hexColor,
            fillOpacity: opacity,
            strokeColor: styleData.hexColor,
            strokeOpacity: strokeOpacity,
            strokeWeight: 1
          };
        }
        
        // No matching journey found - hide feature
        return null;
      };
      
      // Apply combined style function to dataset layer
      let styleUpdatePending = false;
      let journeyRafId = null;
      
      datasetLayer.style = ({ feature }) => {
        if (!styleUpdatePending) {
          styleUpdatePending = true;
          if (journeyRafId !== null) {
            cancelAnimationFrame(journeyRafId);
          }
          journeyRafId = requestAnimationFrame(() => {
            styleUpdatePending = false;
            journeyRafId = null;
          });
          styleUpdateFrameRef.current = journeyRafId;
        }
        return combinedStyleFunction({ feature });
      };
      
      // Add click listener to dataset layer
      const clickListener = datasetLayer.addListener('click', async (event) => {
        const feature = event.features && event.features.length > 0 ? event.features[0] : null;
        if (!feature) return;
        
        // Find matching journey for this feature
        const attrs = feature.datasetAttributes;
        const props = feature.feature?.properties;
        let featureId = attrs?.OBJECTID || attrs?.GEOID || attrs?.continent || attrs?.woe_id ||
                       props?.OBJECTID || props?.GEOID || props?.continent || props?.woe_id;
        
        // Find the first matching journey that is displayed
        for (const styleData of journeyStyleData) {
          // Skip if this journey is not set to display
          if (!styleData.displayJourney) continue;
          
          if ((styleData.isParkStylingUSNational || styleData.isWorldWHCStyling) && styleData.allowedObjectIds.size > 0) {
            const featureIdStr = String(featureId);
            const featureIdNum = typeof featureId === 'number' ? featureId : (isNaN(featureId) ? null : Number(featureId));
            const isAllowed = styleData.allowedObjectIds.has(featureIdStr) || 
                            (featureIdNum !== null && styleData.allowedObjectIds.has(String(featureIdNum)));
            if (!isAllowed) continue;
          }
          
          // This journey matches - show its info window
          const journey = styleData.journey;
          const name = attrs?.name_en || attrs?.name || props?.name_en || props?.name || null;
          const objectId = attrs?.OBJECTID || props?.OBJECTID || null;
          const unitCode = attrs?.UNIT_CODE || props?.UNIT_CODE || null;
          const unitType = attrs?.UNIT_TYPE || props?.UNIT_TYPE || null;
          const country = attrs?.country || attrs?.states_names || props?.country || props?.states_names || null;
          
          let infoContent = `<div style="font-family: Arial, sans-serif; padding: 10px;">`;
          infoContent += `<h3 style="margin: 0 0 10px 0; font-size: 16px;">${journey.title || 'Journey'}</h3>`;
          if (name) infoContent += `<p style="margin: 5px 0;"><strong>Name:</strong> ${name}</p>`;
          if (objectId) infoContent += `<p style="margin: 5px 0;"><strong>OBJECTID:</strong> ${objectId}</p>`;
          if (unitCode) infoContent += `<p style="margin: 5px 0;"><strong>Unit Code:</strong> ${unitCode}</p>`;
          if (unitType) infoContent += `<p style="margin: 5px 0;"><strong>Unit Type:</strong> ${unitType}</p>`;
          if (country) {
            const countryStr = Array.isArray(country) ? country.join(', ') : country;
            infoContent += `<p style="margin: 5px 0;"><strong>Location:</strong> ${countryStr}</p>`;
          }
          infoContent += `</div>`;
          
          journeyInfoWindowRef.current.setContent(infoContent);
          journeyInfoWindowRef.current.setPosition(event.latLng);
          journeyInfoWindowRef.current.open(map);
          break;
        }
      });
      
      // Store overlay data for all journeys in this group
      journeyGroup.forEach((journey) => {
        journeyOverlaysRef.current.set(journey.journeyID, {
          datasetLayer: datasetLayer,
          clickListener: clickListener,
          infoWindow: journeyInfoWindowRef.current,
          dataSetID: dataSetID
        });
      });
    });
    
    // Process journeys without dataSetID (GeoJSON-based)
    journeysWithoutDataSet.forEach((journey, idx) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Memory] Creating overlay ${idx + 1}/${journeysToDisplay.length} for journey ${journey.journeyID}`);
      }
        if (!journey.geospatialBoundaries && !journey.dataSetID) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Journey ${journey.journeyID} (${journey.title}) has no geospatialBoundaries or dataSetID, skipping overlay`);
          }
          return;
        }
        
        if (!journey.journeyColor) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Journey ${journey.journeyID} (${journey.title}) has no journeyColor, skipping overlay`);
          }
          return;
        }
      
      try {
        // Process GeoJSON-based journeys (journeys without dataSetID are already filtered above)
        // Create new Data layer for this journey
        const dataLayer = new window.google.maps.Data();
        dataLayer.setMap(map);
        
        // Parse GeoJSON
        const geoData = journey.geospatialBoundaries;
        let geoJsonData = null;
        
        // Handle GeoJSON FeatureCollection format
        if (geoData && geoData.type === 'FeatureCollection' && Array.isArray(geoData.features)) {
          // Deep clone to preserve exact coordinates and prevent any modifications
          geoJsonData = JSON.parse(JSON.stringify(geoData));
        } else if (geoData && typeof geoData === 'object') {
          // Handle object-based format (boundingbox) - convert to GeoJSON
          const features = Object.keys(geoData).map((locationName, index) => {
            const location = geoData[locationName];
            if (location.bounds) {
              const { minLon, maxLon, minLat, maxLat } = location.bounds;
              return {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [[
                    [minLon, minLat],
                    [maxLon, minLat],
                    [maxLon, maxLat],
                    [minLon, maxLat],
                    [minLon, minLat]
                  ]]
                },
                properties: location.properties || { name: locationName }
              };
            }
            return null;
          }).filter(Boolean);
          
          geoJsonData = {
            type: 'FeatureCollection',
            features: features
          };
        }
        
        if (geoJsonData) {
          // Get RGB color from journey and convert to hex for Google Maps
          // journeyColor is stored as {r, g, b} object
          const hexColor = journeyColorToHex(journey.journeyColor);
          
          // Get journeyProgress for this journey
          const journeyProgress = journey.journeyProgress || [];
          
          // Get displayItemsMode for this journey (default to "Both" if missing)
          const displayItemsMode = journey.displayItemsMode || 'Both';
          
          // Helper function to check if a feature (from GeoJSON) is completed
          // This works with raw GeoJSON feature objects before they're added to the data layer
          const isFeatureCompletedFromGeoJSON = (feature) => {
            if (!journeyProgress || journeyProgress.length === 0) return false;
            
            const properties = feature.properties || {};
            
            // Check by property name
            const name = properties.name;
            if (name && journeyProgress.includes(name)) {
              return true;
            }
            
            // Check by OBJECTID
            const objectId = properties.OBJECTID;
            if (objectId !== undefined && objectId !== null) {
              // Handle both string and number comparisons
              if (journeyProgress.some(progressId => 
                String(progressId) === String(objectId) || Number(progressId) === Number(objectId)
              )) {
                return true;
              }
            }
            
            // Check by woe_id (for Google dataset matching)
            const woeId = properties.woe_id;
            if (woeId !== undefined && woeId !== null) {
              // Handle both string and number comparisons
              if (journeyProgress.some(progressId => 
                Number(progressId) === Number(woeId)
              )) {
                return true;
              }
            }
            
            return false;
          };
          
          // Helper function to check if a feature (from data layer) is completed
          // This works with Google Maps Data.Feature objects after they're added to the data layer
          const isFeatureCompleted = (feature) => {
            if (!journeyProgress || journeyProgress.length === 0) return false;
            
            // Check by property name
            const name = feature.getProperty('name');
            if (name && journeyProgress.includes(name)) {
              return true;
            }
            
            // Check by OBJECTID
            const objectId = feature.getProperty('OBJECTID');
            if (objectId !== undefined && objectId !== null) {
              // Handle both string and number comparisons
              if (journeyProgress.some(progressId => 
                String(progressId) === String(objectId) || Number(progressId) === Number(objectId)
              )) {
                return true;
              }
            }
            
            // Check by woe_id (for Google dataset matching)
            const woeId = feature.getProperty('woe_id');
            if (woeId !== undefined && woeId !== null) {
              // Handle both string and number comparisons
              if (journeyProgress.some(progressId => 
                Number(progressId) === Number(woeId)
              )) {
                return true;
              }
            }
            
            return false;
          };
          
          // Filter features based on displayItemsMode before adding to data layer
          let filteredFeatures = geoJsonData.features || [];
          
          if (displayItemsMode === 'visited') {
            // Only show features that have completed notes
            filteredFeatures = filteredFeatures.filter(feature => {
              // Check if entire journey is complete
              const isJourneyComplete = journey.isComplete === true || 
                                        journey.manualPercentComplete === 100 ||
                                        journey.percentComplete === 100;
              return isJourneyComplete || isFeatureCompletedFromGeoJSON(feature);
            });
          } else if (displayItemsMode === 'notVisited') {
            // Only show features that do NOT have completed notes
            filteredFeatures = filteredFeatures.filter(feature => {
              // Check if entire journey is complete - if so, don't show any (all are visited)
              const isJourneyComplete = journey.isComplete === true || 
                                        journey.manualPercentComplete === 100 ||
                                        journey.percentComplete === 100;
              if (isJourneyComplete) {
                return false; // All locations are visited, so show none
              }
              return !isFeatureCompletedFromGeoJSON(feature);
            });
          }
          // If displayItemsMode is "Both" or missing, show all features (no filtering)
          
          // Create filtered GeoJSON with only the features that should be displayed
          // Deep clone to preserve exact coordinates and prevent any modifications
          const filteredGeoJsonData = {
            type: 'FeatureCollection',
            features: filteredFeatures.map(feature => {
              // Deep clone each feature to preserve exact coordinate precision
              return JSON.parse(JSON.stringify(feature));
            })
          };
          
          // Add filtered GeoJSON to the data layer
          // Google Maps addGeoJson should preserve coordinates, but we ensure exact precision by deep cloning
          // Log coordinates for debugging (can be removed in production)
          if (filteredGeoJsonData.features && filteredGeoJsonData.features.length > 0) {
            const firstFeature = filteredGeoJsonData.features[0];
            if (firstFeature.geometry && firstFeature.geometry.coordinates) {
              console.log('Coordinates are:', 
                JSON.stringify(firstFeature.geometry.coordinates[0].slice(0, 2))); // Log first 2 coordinates
            }
          }
          dataLayer.addGeoJson(filteredGeoJsonData);
          
          // Check if the entire journey is complete (isComplete or manualPercentComplete === 100)
          const isJourneyComplete = journey.isComplete === true || 
                                    journey.manualPercentComplete === 100 ||
                                    journey.percentComplete === 100;
          
          // Set styling with function-based approach to check completion status per feature
          // If journey is complete, all boxes get 80% opacity
          // Otherwise, completed locations get 80% opacity, others get default opacity
          dataLayer.setStyle((feature) => {
            // If entire journey is complete, all features are completed
            const isCompleted = isJourneyComplete || isFeatureCompleted(feature);
            
            return {
              fillColor: hexColor,
              fillOpacity: isCompleted ? 0.8 : 0.3,  // 80% opacity for completed, 30% for others
              strokeColor: hexColor,
              strokeOpacity: isCompleted ? 0.8 : 0.5,  // 80% opacity for completed borders, 50% for others
              strokeWeight: 1,
              clickable: true,  // Keep clickable for info windows
              visible: true
            };
          });
          
          // Ensure the data layer renders behind markers by setting it to a lower z-index layer
          // Data layers are typically rendered behind markers by default, but we can be explicit
          if (dataLayer.setDrawingMode) {
            // This ensures the layer doesn't interfere with marker interactions
            dataLayer.setDrawingMode(null);
          }
          
          // Add click listener to show metadata
          const dataLayerClickListener = dataLayer.addListener('click', async (event) => {
            const feature = event.feature;
            
            // Access properties directly from the feature using getProperty()
            // Properties from GeoJSON are flattened and accessible directly
            // Handle different property name variations
            const name = feature.getProperty('name_en') || feature.getProperty('name');
            const objectId = feature.getProperty('OBJECTID');
            const country = feature.getProperty('country') || feature.getProperty('states_names');
            const state = feature.getProperty('state');
            const featType = feature.getProperty('featType') || feature.getProperty('type');
            
            // Extract url and changesMade if available
            const url = feature.getProperty('url');
            const changesMade = feature.getProperty('changesMade');
            
            // Get clicked location coordinates
            const clickedLat = event.latLng.lat();
            const clickedLng = event.latLng.lng();
            
            // Calculate distances
            let distanceFromHome = null;
            let distanceFromClosestNote = null;
            let closestNoteName = null;
            
            // Calculate distance from user's home address if available
            if (userAddress && userAddress.lat && userAddress.lng) {
              distanceFromHome = calculateDistance(
                userAddress.lat,
                userAddress.lng,
                clickedLat,
                clickedLng
              );
            }
            
            // Calculate distance from closest note
            const notesWithCoords = notes.filter(note => {
              const latLong = note.aiPromptResults?.lat_long?.value;
              return latLong && latLong.includes(',');
            });
            
            if (notesWithCoords.length > 0) {
              let minDistance = Infinity;
              let closestNote = null;
              
              notesWithCoords.forEach(note => {
                const latLong = note.aiPromptResults.lat_long.value;
                const [noteLat, noteLng] = latLong.split(',').map(Number);
                const distance = calculateDistance(clickedLat, clickedLng, noteLat, noteLng);
                
                if (distance < minDistance) {
                  minDistance = distance;
                  closestNote = note;
                }
              });
              
              if (closestNote) {
                distanceFromClosestNote = minDistance;
                // Use note title/name (emailSubject) instead of noteID
                closestNoteName = closestNote.manualPromptResults?.emailSubject || closestNote.emailSubject || 'Note';
              }
            }
            
            // Build InfoWindow content with journey and location metadata
            let content = `<div style="padding: 10px; max-width: 300px;">`;
            content += `<h3 style="margin: 0 0 10px 0; font-size: 16px; font-weight: bold;">${journey.title || 'Journey'}</h3>`;
            
            if (journey.description) {
              content += `<p style="margin: 5px 0; font-size: 14px;">${journey.description}</p>`;
            }
            
            // Add location-specific properties if available
            if (name) {
              content += `<p style="margin: 5px 0; font-size: 14px;"><strong style="color: #1a73e8;">Location:</strong> ${name}</p>`;
            }
            if (country) {
              content += `<p style="margin: 5px 0; font-size: 14px;"><strong style="color: #1a73e8;">Country:</strong> ${country}</p>`;
            }
            if (state && state !== 'N/A') {
              content += `<p style="margin: 5px 0; font-size: 14px;"><strong style="color: #1a73e8;">State:</strong> ${state}</p>`;
            }
            if (featType) {
              content += `<p style="margin: 5px 0; font-size: 14px;"><strong style="color: #1a73e8;">Type:</strong> ${featType}</p>`;
            }
            if (journey.locationCount !== undefined) {
              content += `<p style="margin: 5px 0; font-size: 14px;"><strong style="color: #1a73e8;">Total Locations:</strong> ${journey.locationCount}</p>`;
            }
            
            // Add url if available
            if (url) {
              content += `<p style="margin: 5px 0; font-size: 14px;"><strong style="color: #1a73e8;">URL:</strong> <a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #1a73e8; text-decoration: underline;">${url}</a></p>`;
            }
            
            // Add changesMade if available
            if (changesMade) {
              content += `<p style="margin: 5px 0; font-size: 14px;"><strong style="color: #1a73e8;">Changes Made:</strong> ${changesMade}</p>`;
            }
            
            // Add distance information
            content += `<div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #ddd;">`;
            content += `<p style="margin: 5px 0; font-size: 12px; color: #666;"><strong style="color: #1a73e8;">Distance Information:</strong></p>`;
            
            if (distanceFromHome !== null) {
              const distanceText = formatDistance(distanceFromHome);
              content += `<p style="margin: 5px 0; font-size: 12px; color: #666;">📍 From your address: ${distanceText}</p>`;
            } else {
              content += `<p style="margin: 5px 0; font-size: 12px; color: #999;">📍 From your address: Not available (set address in Account Settings)</p>`;
            }
            
            if (distanceFromClosestNote !== null) {
              const distanceText = formatDistance(distanceFromClosestNote);
              content += `<p style="margin: 5px 0; font-size: 12px; color: #666;">📝 From closest note (${closestNoteName}): ${distanceText}</p>`;
            } else {
              content += `<p style="margin: 5px 0; font-size: 12px; color: #999;">📝 From closest note: No notes with location data</p>`;
            }
            
            // Add Edit button for user-owned private journeys with editable geospatial boundaries
            // Use inline onclick - InfoWindow content may be in iframe where addEventListener doesn't receive clicks
            const currentUserId = currentUserIdRef.current;
            const isUserOwned = journey.ownerID && journey.ownerID !== 'System' && journey.ownerID === currentUserId;
            const isPrivate = journey.public === false || journey.public === 'false';
            const hasEditableBoundaries = journey.geospatialBoundaries && !journey.dataSetID;
            if (isUserOwned && isPrivate && hasEditableBoundaries) {
              const journeyID = journey.journeyID;
              // JSON.stringify + HTML-escape quotes so onclick attribute parses correctly
              const journeyIDJson = JSON.stringify(String(journeyID)).replace(/"/g, '&quot;');
              content += `<div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #ddd;">`;
              content += `<button class="journey-popup-edit-btn" onclick="(function(){var w=typeof top!=='undefined'?top:window;if(w.__handleJourneyPopupEdit){w.__handleJourneyPopupEdit(${journeyIDJson});}})();return false;" style="padding: 6px 12px; background: #1a73e8; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">Edit</button>`;
              content += `</div>`;
            }
            
            content += `</div>`;
            content += `</div>`;
            
            // Show InfoWindow at clicked location
            journeyInfoWindowRef.current.setContent(content);
            journeyInfoWindowRef.current.setPosition(event.latLng);
            journeyInfoWindowRef.current.open(map);
          });
          
          // Store the overlay with click listener for cleanup
          journeyOverlaysRef.current.set(journey.journeyID, {
            dataLayer: dataLayer,
            dataLayerClickListener: dataLayerClickListener, // Store for cleanup
            infoWindow: journeyInfoWindowRef.current
          });
          
          if (process.env.NODE_ENV === 'development') {
            logMemoryDiagnostics('updateJourneyOverlays AFTER DATA LAYER', {
              journeyId: journey.journeyID,
              totalOverlays: journeyOverlaysRef.current.size,
              geoJsonSize: journey.geospatialBoundaries ? JSON.stringify(journey.geospatialBoundaries).length : 0
            });
          }
        }
      } catch (error) {
        console.error(`Error adding journey overlay for ${journey.journeyID}:`, error);
      }
    });
    
    logMemoryDiagnostics('updateJourneyOverlays END', {
      totalOverlays: journeyOverlaysRef.current.size,
      journeysDisplayed: journeysToDisplay.length
    });
  };

  // Use ref to store latest journeys to avoid closure issues
  const journeysRef = useRef(journeys);
  useEffect(() => {
    journeysRef.current = journeys;
  }, [journeys]);

  // Global handler for Edit button clicks (InfoWindow content may be in iframe, so inline onclick calls this)
  useEffect(() => {
    const handleJourneyPopupEdit = (journeyID) => {
      try {
        if (journeyInfoWindowRef.current) {
          journeyInfoWindowRef.current.close();
        }
        const journeyToEdit = journeysRef.current?.find(j => j.journeyID === journeyID);
        window.dispatchEvent(new CustomEvent('openJourneyPolygonEdit', {
          detail: { journeyID, journey: journeyToEdit }
        }));
      } catch (e) {
        console.error('Error handling journey popup edit:', e);
      }
    };
    window.__handleJourneyPopupEdit = handleJourneyPopupEdit;
    return () => {
      delete window.__handleJourneyPopupEdit;
    };
  }, []);

  // Throttled version of updateJourneyOverlays for map movements
  // Use ref to avoid capturing journeys in closure (prevents memory leaks)
  const throttledUpdateJourneyOverlaysRef = useRef(null);
  
  // Create throttled function once and reuse it
  if (!throttledUpdateJourneyOverlaysRef.current) {
    throttledUpdateJourneyOverlaysRef.current = throttle((map) => {
      if (map && isLoaded) {
        // Use ref to get latest journeys instead of closure
        updateJourneyOverlays(map, journeysRef.current);
      }
    }, 300); // 300ms throttle for map movements
  }
  
  const throttledUpdateJourneyOverlays = throttledUpdateJourneyOverlaysRef.current;

  // Update journey overlays when journeys change or when map loads
  useEffect(() => {
    if (mapRef.current && isLoaded && journeys.length >= 0) {
      // Always update overlays based on current journeys state
      updateJourneyOverlays(mapRef.current, journeys);
    }
  }, [journeys, isLoaded]);

  // Handle map movements for viewport-based lazy loading
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;

    const map = mapRef.current;
    
    // Debounced handler for map movements
    const handleMapMove = () => {
      if (mapMoveTimeoutRef.current) {
        clearTimeout(mapMoveTimeoutRef.current);
      }
      mapMoveTimeoutRef.current = setTimeout(() => {
        throttledUpdateJourneyOverlays(map);
      }, 200); // 200ms debounce
    };

    // Listen to map bounds changes for viewport-based loading
    const boundsListener = map.addListener('bounds_changed', handleMapMove);
    const idleListener = map.addListener('idle', () => {
      // Update on idle (after pan/zoom completes)
      throttledUpdateJourneyOverlays(map);
    });

    return () => {
      // Clean up map listeners
      if (window.google && window.google.maps) {
        try {
          window.google.maps.event.removeListener(boundsListener);
        } catch (e) {
          // Listener might already be removed
        }
        try {
          window.google.maps.event.removeListener(idleListener);
        } catch (e) {
          // Listener might already be removed
        }
      }
      // Clean up timeout
      if (mapMoveTimeoutRef.current) {
        clearTimeout(mapMoveTimeoutRef.current);
        mapMoveTimeoutRef.current = null;
      }
    };
  }, [isLoaded]); // Only recreate when isLoaded changes, throttledUpdateJourneyOverlays is stable via ref

  const handleLogout = () => {
    signOut();
    navigate('/');
  };

  const handleCreateNoteClick = () => {
    // Check if user has reached their note limit
    if (plan === 'Basic' && notes.length >= 30) {
      // Don't allow navigation if basic user has reached limit
      return;
    }
    
    ReactGA.event({
      category: 'User',
      action: 'Created New Note',
    });
    
    navigate('/create-note', {
      state: {
        clientId: connectionId,
      },
    });
  };

  const handleCreateNoteClickMap = async (position) => {
    // Check if user has reached their note limit
    if (plan === 'Basic' && notes.length >= 30) {
      // Don't allow navigation if basic user has reached limit
      return;
    }
    
    try {
      const { lat, lng } = position;
      const address = await fetchAddressFromCoordinates(lat, lng); // Uses providerRef for fetching address
      navigate('/create-note', {
        state: {
          clientId: connectionId,
          manualTags: {
            Address: address.replace(/[^ -~]+/g, '').replace(/,/g, ''),
          },
          lat_long: { value: `${lat},${lng}`, probability: 100.0 },
        },
      });
    } catch (error) {
      console.error('Error fetching address:', error);
    }
  };
  
  

  const handleTabClick = (noteId, tabName) => {
    const key = `${noteId}-${tabName}`;
    setSelectedTab(selectedTab === key ? null : key);
  };

  const renderTabContent = (note, tabName) => {
    const aiResults = note.aiPromptResults || {};

    const normalizeKey = (key) => key.replace(/[\s:]/g, '').toLowerCase();

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    //const hardcodedTimezone = 'America/Denver';

    // Convert and adjust the dates
    const creationDate = new Date(note.noteCreationDate);
    const lastEdited = new Date(note.lastEdited);
  
    //console.log('Original Creation Date:', creationDate);
    //console.log('Original Last Edited Date:', lastEdited);
  
    // Format dates to Mountain Time with 12-hour format and AM/PM
    const formattedCreationDate = formatInTimeZone(
      creationDate,
      userTimezone,
      'yyyy-MM-dd'
    );
    const formattedLastEdited = formatInTimeZone(
      lastEdited,
      userTimezone,
      'yyyy-MM-dd'
    );
  
    //console.log('Formatted Creation Date (Mountain Time):', formattedCreationDate);
    //console.log('Formatted Last Edited Date (Mountain Time):', formattedLastEdited);
    
    const renderObject = (obj, keyPrefix) => {
      const uniqueKeys = new Set();
      if (Array.isArray(obj)) {
        return obj
          .filter(item => item && item.value)
          .map((item, index) => {
            const key = `${keyPrefix}-${index}`;
            const normalizedKey = normalizeKey(item.value);
            if (uniqueKeys.has(normalizedKey)) return null;
            uniqueKeys.add(normalizedKey);
            return (
              <div key={key} className="blue-rectangle">
                {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, `${keyPrefix}-${index}`)}></button>}
                <span className="add-text">{item.value}</span>
              </div>
            );
          })
          .filter(item => item !== null);
      } else if (typeof obj === 'object' && obj !== null) {
        return Object.entries(obj)
          .filter(([key, value]) => key !== 'probability' && value?.value)
          .filter(([key]) => {
            const normalizedKey = normalizeKey(key);
            if (uniqueKeys.has(normalizedKey)) return false;
            uniqueKeys.add(normalizedKey);
            return true;
          })
          .map(([key, value], index) => {
            const elementKey = `${keyPrefix}-${key}`;
            return (
              <div key={elementKey} className="blue-rectangle">
                {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, `${keyPrefix}-${key}`)}></button>}
                <span>{`${key}: ${value.value}`}</span>
              </div>
            );
          });
      }
      return [];
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleString();
    };

    const renderRecTimes = () => {
      if (Array.isArray(aiResults.recTimes)) {
        return aiResults.recTimes
          .filter(time => time && (time['best season to visit']?.value || time['best day to visit']?.value || time['best time to visit']?.value || time.reason?.value))
          .map((time, index) => {
            const key = `recTimes-${index}`;
            return (
              <div key={key} className="blue-rectangle">
                {editingNoteId === note.noteID && (
                  <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'recTimes')}></button>
                )}
                <span>
                  {time['best season to visit'] && (
                    <div>
                      <span className="highlight-text">Best season to visit: </span>
                      {time['best season to visit'].value}
                    </div>
                  )}
                  {time['best day to visit'] && (
                    <div>
                      <span className="highlight-text">Best day to visit: </span>
                      {time['best day to visit'].value}
                    </div>
                  )}
                  {time['best time to visit'] && (
                    <div>
                      <span className="highlight-text">Best time to visit: </span>
                      {time['best time to visit'].value}
                    </div>
                  )}
                  {time.reason && (
                    <div>
                      <span className="highlight-text">Reason: </span>
                      {time.reason.value}
                    </div>
                  )}
                </span>
              </div>
            );
          });
      } else if (typeof aiResults.recTimes === 'object' && aiResults.recTimes !== null) {
        return [
          <div key="recTimes-single" className="blue-rectangle">
            {editingNoteId === note.noteID && (
              <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'recTimes')}></button>
            )}
            <span>
              {aiResults.recTimes['best season to visit'] && (
                <div>
                  <span className="highlight-text">Best season to visit: </span>
                  {aiResults.recTimes['best season to visit'].value}
                </div>
              )}
              {aiResults.recTimes['best day to visit'] && (
                <div>
                  <span className="highlight-text">Best day to visit: </span>
                  {aiResults.recTimes['best day to visit'].value}
                </div>
              )}
              {aiResults.recTimes['best time to visit'] && (
                <div>
                  <span className="highlight-text">Best time to visit: </span>
                  {aiResults.recTimes['best time to visit'].value}
                </div>
              )}
              {aiResults.recTimes.reason && (
                <div>
                  <span className="highlight-text">Reason: </span>
                  {aiResults.recTimes.reason.value}
                </div>
              )}
            </span>
          </div>
        ];
      }
      return [];
    };

    switch (tabName) {
      case 'Note Details':
        return [
          note.noteCreationDate && (
            <div key="dateCreated" className="blue-rectangle">
              <span><span className="highlight-text">Date Created: </span>{formattedCreationDate}</span>
              
            </div>
          ),
          note.lastEdited && (
            <div key="lastUpdated" className="blue-rectangle">
              <span><span className="highlight-text">Last Updated: </span>{formattedLastEdited}</span>
            </div>
          ),
          note.fromEmail && (
            <div key="fromEmail" className="blue-rectangle">
              <span><span className="highlight-text">From Email: </span>{note.fromEmail}</span>
            </div>
          ),
          note.completed != null && (
            <div key="completed" className="blue-rectangle">
              <span><span className="highlight-text">Completed State: </span>{(note.completed === true || note.completed === 'True' || note.completed === 'true') ? 'True' : 'False'}</span>
            </div>
          ),
          note.noteActive != null && (
            <div key="activeState" className="blue-rectangle">
              <span><span className="highlight-text">Active State: </span>{(note.noteActive === true || note.noteActive === 'True' || note.noteActive === 'true') ? 'True' : 'False'}</span>
            </div>
          )
        ].filter(element => element);

      case 'My Tags':
        return [
          ...Object.entries(note.manualPromptResults?.manualTags || {})
            .filter(([key, value]) => value)
            .map(([key, value]) => {
              const elementKey = `manualTags-${key}`;
              return (
                <div key={elementKey} className="blue-rectangle">
                  {editingNoteId === note.noteID && (
                    <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, 'My Tags', key)}></button>
                  )}
                  <span>
                    <span className="highlight-text">{key}:</span> {value}
                  </span>
                </div>
              );
            }),
          editingNoteId === note.noteID && newBox.visible && (
            <div key="new-tag" className="blue-rectangle new-rectangle">
              <input
                type="text"
                className="new-rectangle-input"
                placeholder="Key"
                value={newBox.key}
                onChange={(e) => setNewBox({ ...newBox, key: e.target.value })}
              />
              {(newBox.key.toLowerCase() === 'categorization' || newBox.key.toLowerCase() === 'color') && (
                <select
                  className="new-rectangle-input"
                  value={newBox.value}
                  onChange={(e) => setNewBox({ ...newBox, value: e.target.value })}
                >
                  <option value="">Select Color</option>
                  {colorOptions.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
              )}
              {newBox.key.toLowerCase() !== 'categorization' && newBox.key.toLowerCase() !== 'color' && (
                <input
                  type="text"
                  className="new-rectangle-input"
                  placeholder="Value"
                  value={newBox.value}
                  onChange={(e) => setNewBox({ ...newBox, value: e.target.value })}
                />
              )}
              <button className="save-new-rectangle-button" onClick={() => handleSaveNewBox(note.noteID, 'My Tags')}>Save</button>
              <AddTagTooltip>
                <div className="add-tag-tooltip-content">
                  <ul>
                    <li>Add an "address" label to overwrite or change the location.</li>
                    <li>Add a "color" or "categorization" label to overwrite or change the color.</li>
                    <li>Add your own tags.</li>
                  </ul>
                </div>
              </AddTagTooltip>
            </div>
          )
        ].filter(element => element);

      case 'Recommendations':
        return [
          aiResults.name?.value && (
            <div key="name" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'name')}></button>}
              <span><span className="highlight-text">Name: </span>{aiResults.name.value}</span>
            </div>
          ),
          aiResults.description?.value && (
            <div key="description" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'description')}></button>}
              <span><span className="highlight-text">Description: </span>{aiResults.description.value}</span>
            </div>
          ),
          aiResults.address?.value && (
            <div key="address" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'address')}></button>}
              <span><span className="highlight-text">Address: </span>{aiResults.address.value}</span>
            </div>
          ),
          Array.isArray(aiResults.activities?.value) && aiResults.activities.value.length > 0 && (
            <div key="activities" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'activities')}></button>}
              <span><span className="highlight-text">Activities: </span>{aiResults.activities.value.join(', ')}</span>
            </div>
          ),
          Array.isArray(aiResults.activities) && aiResults.activities.length > 0 && (
            <div key="activities" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'activities')}></button>}
              <span><span className="highlight-text">Activities: </span>{aiResults.activities.map(activity => activity.value).join(', ')}</span>
            </div>
          ),
          Array.isArray(aiResults.recTags?.value) && aiResults.recTags.value.length > 0 && (
            <div key="recTags" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'recTags')}></button>}
              <span><span className="highlight-text">Recommended Tags: </span>{aiResults.recTags.value.join(', ')}</span>
            </div>
          ),
          Array.isArray(aiResults.recTags) && aiResults.recTags.length > 0 && (
            <div key="recTags" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'recTags')}></button>}
              <span><span className="highlight-text">Recommended Tags: </span>{aiResults.recTags.map(tag => tag.value).join(', ')}</span>
            </div>
          ),
          aiResults.category?.[0]?.color?.value && (
            <div key="category" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'category')}></button>}
              <span><span className="highlight-text">Categorization: </span>{aiResults.category[0].color.value}</span>
            </div>
          ),
          Array.isArray(aiResults.nearby?.value) && aiResults.nearby.value.length > 0 && (
            <div key="nearby" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'nearby')}></button>}
              <span><span className="highlight-text">Nearby things to do: </span>{aiResults.nearby.value.join(', ')}</span>
            </div>
          ),
          Array.isArray(aiResults.nearby) && aiResults.nearby.length > 0 && (
            <div key="nearby" className="blue-rectangle">
              {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, 'nearby')}></button>}
              <span><span className="highlight-text">Nearby things to do: </span>{aiResults.nearby.map(location => location.value).join(', ')}</span>
            </div>
          ),
          ...renderObject(aiResults.lat_long, 'lat_long'),
          ...renderRecTimes(),
          ...Object.entries(note.aiPromptResults || {})
            .filter(([key, value]) => value && value.value && !['name', 'address', 'recTags', 'activities','lat_long', 'geolat_long','description', 'nearby'].includes(key.toLowerCase()))
            .reduce((acc, [key, value]) => {
              const normalizedKey = normalizeKey(key);
              if (!acc.some(([existingKey]) => normalizeKey(existingKey) === normalizedKey)) {
                acc.push([key, value]);
              }
              return acc;
            }, [])
            .map(([key, value], index) => {
              const elementKey = `aiPromptResults-${key}`;
              return (
                <div key={elementKey} className="blue-rectangle">
                  {editingNoteId === note.noteID && <button className="delete-box-button" onClick={() => handleDeleteBox(note.noteID, tabName, `${elementKey}-${index}`)}></button>}
                  <span><span className="highlight-text">{key}:</span> {value.value}</span>
                </div>
              );
            })
        ].filter(element => element);
      default:
        return [];
    }
  };

  const handleDeleteNote = async (noteId, lastEdited) => {
    const confirmed = window.confirm("Are you sure you want to delete this note?");
    if (confirmed) {
      try {
        const token = (await fetchAuthSession()).tokens?.idToken?.toString();
        const headers = {
          'Content-type': 'application/json; charset=UTF-8',
          'Authorization': `Bearer ${token}`
        };

        //await axios.delete(`https://rccd7493ld.execute-api.us-west-2.amazonaws.com/dev/notes/${noteId}?lastEdited=${encodeURIComponent(lastEdited)}`, { headers });
        await axios.delete(`${apiUrl}/notes/${noteId}?lastEdited=${encodeURIComponent(lastEdited)}`, { headers });
        
        // Reset pagination and fetch first page
        setCurrentPage(1);
        setPaginationTokens({});
        setLastEvaluatedKey(null);
        fetchNotes(plan, 1, null);
      } catch (error) {
        console.error('Error deleting note:', error);
      }
    }
  };

  const handleEditNote = (noteId, currentBody, currentLink, currentEmailSubject) => {
    setEditingNoteId(noteId);
    setEditedNote(currentBody);
    setEditedLink(currentLink);
    setEditedEmailSubject(currentEmailSubject);
    setOriginalNote(currentBody);
  };

  const handleSaveNote = async (noteId) => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
  
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
  
      const noteToSave = notes.find(note => note.noteID === noteId);
      if (!noteToSave) {
        console.error('Note not found:', noteId);
        return;
      }
  
      const { noteCreationDate, lastEdited, fromEmail, noteActive, completed } = noteToSave;
      const manualTags = noteToSave.manualPromptResults?.manualTags || {};
      const aiPromptResults = noteToSave.aiPromptResults || {};
  
      // Preserve the exact completed status from the original note
      // Only convert to backend format ('True'/'False') but preserve the original state
      // Handle all possible formats: true, 'True', 'true', false, 'False', 'false', null, undefined
      const preserveCompletedStatus = (completedValue) => {
        // If completed is null or undefined, don't include it in the payload
        // This preserves the original state (backend won't change it)
        if (completedValue === null || completedValue === undefined) {
          return null;
        }
        // Convert to backend format while preserving the actual state
        // Check if it represents a completed state (true, 'True', 'true')
        const isCompleted = completedValue === true || completedValue === 'True' || completedValue === 'true';
        return isCompleted ? 'True' : 'False';
      };
  
      const noteDetails = {
        ...(noteCreationDate && { dateCreated: formatTimestamp(new Date(noteCreationDate)) }),
        ...(lastEdited && { lastUpdated: formatTimestamp(new Date(lastEdited)) }),
        ...(fromEmail && { fromEmail }),
        ...(editedEmailSubject && { emailSubject: editedEmailSubject }),
        // Preserve completed status exactly as it is - only include if it exists
        // This ensures we don't accidentally change the completed status when editing other fields
        ...(completed !== null && completed !== undefined && { completed: preserveCompletedStatus(completed) }),
        ...(noteActive != null && { activeState: noteActive ? 'True' : 'False' })
      };
  
      const tags = Object.entries(manualTags).reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  
      const aiRecommendations = {
        ...aiPromptResults,
        geolat_long: aiPromptResults.geolat_long || { value: '' }, // Ensure geolat_long is included
        address: aiPromptResults.address || { value: '', probability: 0 },
        recTags: Array.isArray(aiPromptResults.recTags) ? aiPromptResults.recTags : [],
        activities: Array.isArray(aiPromptResults.activities) ? aiPromptResults.activities : [],
        lat_long: aiPromptResults.lat_long || { value: '', probability: 0 },
        name: aiPromptResults.name || { value: '', probability: 0 },
        description: aiPromptResults.description || { value: '', probability: 0 },
        recTimes: Array.isArray(aiPromptResults.recTimes) ? aiPromptResults.recTimes : [],
        category: Array.isArray(aiPromptResults.category) ? aiPromptResults.category : [],
        nearby: Array.isArray(aiPromptResults.nearby) ? aiPromptResults.nearby : []
      };
  
      const updatedTime = formatTimestamp(new Date());
  
      const urlsArray = noteToSave.manualPromptResults?.url || [];
      const urlsPresent = urlsArray.length > 0;

      const newAddress = manualTags.Address;
      const previousAddress = aiPromptResults.address?.value;
      let latLng; // Variable to store the new geolocation coordinates

      if (newAddress && (newAddress !== previousAddress)) {
        console.log("checked new address and hitting geocode: " + newAddress);
        console.log("checked old address and hitting geocode: " + previousAddress);
        console.log(`Updating geolat_long for note ${noteId}: ${aiRecommendations.geolat_long.value}`);

        try {
          const location = await fetchGeolocationFromProvider(newAddress);
          if (location) {
            latLng = [location.lat, location.lng];

            // Update the geolat_long value
            aiRecommendations.geolat_long = { value: `${latLng[0]},${latLng[1]}` };

            // Update the note with the new geolat_long
            //await updateNoteWithGeolatLong(noteId, latLng, noteToSave);
          } else {
            console.error(`Error geocoding address "${newAddress}":`);
          }
        } catch (error) {
          console.error('Error geocoding address:', error.message);
        }
      }
      console.log(`Updated geolat_long for note ${noteId}: ${aiRecommendations.geolat_long.value}`);


      const combinedBodyText  = JSON.stringify({
        bodyText: editedNote,
        //url: editedLink,
        url: urlsArray,
        urlsPresent: urlsPresent,
        noteDetails,
        tags,
        aiRecommendations,
        //clientId: connectionId,
        lastEdited
      }, null, 2);
  
      await axios.put(
        //`https://rccd7493ld.execute-api.us-west-2.amazonaws.com/dev/notes/${noteId}`,
        `${apiUrl}/notes/${noteId}`,
        { bodyText: combinedBodyText   },
        { headers }
      );
  
      // Update the markers after the note has been saved
      if (latLng) {
        console.log(`Updated markers for ${noteId}: ` + latLng);
        updateMarkers(noteId, latLng);
      }
      setEditingNoteId(null);
      // Refresh current page after saving
      const tokenToUse = currentPage === 1 ? null : paginationTokens[currentPage - 1];
      fetchNotes(plan, currentPage, tokenToUse);
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };
  
  // Function to update the markers list and remove the old marker
  const updateMarkers = (noteId, latLng) => {
    // Remove old markers from the map instance
    if (mapRef.current && mapRef.current.markers) {
      // Find the marker to be removed
      const markersToRemove = mapRef.current.markers.filter((marker) => marker.noteID === noteId);
  
      // Remove each marker from the map
      markersToRemove.forEach((marker) => marker.setMap(null));
  
      // Update the markers array on the mapRef instance
      mapRef.current.markers = mapRef.current.markers.filter((marker) => marker.noteID !== noteId);
    }
  
    // Update the state with new markers
    setMarkers((prevMarkers) => {
      // Filter out the old marker with the same noteId
      const filteredMarkers = prevMarkers.filter((marker) => marker.noteID !== noteId);
  
      // Create the new marker for the updated note
      const updatedMarker = {
        lat: latLng[0],
        lng: latLng[1],
        noteID: noteId,
        title: notes.find((note) => note.noteID === noteId)?.manualPromptResults?.emailSubject,
        body: notes.find((note) => note.noteID === noteId)?.manualPromptResults?.url,
        category: notes.find((note) => note.noteID === noteId)?.manualPromptResults?.manualTags?.Categorization ||
          notes.find((note) => note.noteID === noteId)?.aiPromptResults?.category?.[0]?.color?.value,
      };
  
      // Add the new marker to the markers array
      const newMarkers = [...filteredMarkers, updatedMarker];
  
      // Add the new marker to the map instance
      if (mapRef.current) {
        createAdvancedMarkers(mapRef.current, newMarkers);
      }
  
      return newMarkers;
    });
  
    // Recenter the map to the new location
    setCenter({ lat: latLng[0], lng: latLng[1] });
    setZoom(12);
  
    if (mapRef.current) {
      mapRef.current.panTo({ lat: latLng[0], lng: latLng[1] });
      mapRef.current.setZoom(12);
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditedNote(originalNote);
  };

  const handleDeleteBox = (noteId, tabName, boxKey) => {
    setNotes((prevNotes) => {
      return prevNotes.map(note => {
        if (note.noteID === noteId) {
          const updatedNote = { ...note };

          if (tabName === 'Note Details') {
            if (boxKey === 'dateCreated') {
              delete updatedNote.noteCreationDate;
            } else if (boxKey === 'lastUpdated') {
              delete updatedNote.lastEdited;
            } else if (boxKey === 'fromEmail') {
              delete updatedNote.fromEmail;
            } else if (boxKey === 'activeState') {
              delete updatedNote.noteActive;
            }else if (boxKey === 'completed') {
              delete updatedNote.completed;
            }
          } else if (tabName === 'My Tags') {
            if (updatedNote.manualPromptResults && updatedNote.manualPromptResults.manualTags) {
              const manualTags = { ...updatedNote.manualPromptResults.manualTags };
              if (manualTags[boxKey]) {
                delete manualTags[boxKey];
                updatedNote.manualPromptResults.manualTags = manualTags;
              }
            }
          } else if (tabName === 'Recommendations') {
            if (boxKey === 'name') {
              delete updatedNote.aiPromptResults.name;
            } else if (boxKey === 'description') {
              delete updatedNote.aiPromptResults.description;
            } else if (boxKey === 'address') {
              delete updatedNote.aiPromptResults.address;
            } else if (boxKey === 'activities') {
              delete updatedNote.aiPromptResults.activities;
            } else if (boxKey === 'recTags') {
              delete updatedNote.aiPromptResults.recTags;
            } else if (boxKey === 'category') {
              delete updatedNote.aiPromptResults.category;
            } else if (boxKey === 'nearby') {
              delete updatedNote.aiPromptResults.nearby;
            } else if (boxKey === 'recTimes') {
              delete updatedNote.aiPromptResults.recTimes;
            } else if (updatedNote.aiPromptResults.lat_long && Array.isArray(updatedNote.aiPromptResults.lat_long)) {
              updatedNote.aiPromptResults.lat_long.splice(boxKey, 1);
            }
          }
          return updatedNote;
        }
        return note;
      });
    });
  };

  const clearSelectedNotes = useCallback(() => {
    setSelectedNoteIds([]);
    setIsSelectionMode(false);
  }, []);

  const clearSelectionOnly = useCallback(() => {
    setSelectedNoteIds([]);
    setIsSelectionMode(false);
  }, []);

  const handleClearAll = useCallback(async () => {
    // Clear selections
    setSelectedNoteIds([]);
    setIsSelectionMode(false);
    // Clear filters and refresh list
    setFilters({
      noteCreationDate: '',
      fromEmail: '',
      noteActive: '',
      completed: '',
      lastEdited: '',
      recTags: '',
      activities: '',
      bodyText: '',
      emailSubject: '',
      name: '',
      description: '',
      categoryColor: '',
      categoryDescription: '',
      recTimes: ''
    });
    // Reset to first page when clearing filters
    setCurrentPage(1);
    setPaginationTokens({});
    setLastEvaluatedKey(null);
    setSelectedFilter('');
    setColorFilter('');
    // Fetch first page
    await fetchNotes(plan, 1, null);
  }, [plan, fetchNotes]);

  const handleStartTutorial = useCallback(() => {
    setShowTutorial(true);
  }, []);

  const handleTutorialComplete = useCallback(() => {
    setShowTutorial(false);
    // Mark tutorial as completed in localStorage
    localStorage.setItem('idealscribe_tutorial_completed', 'true');
  }, []);

  const handleDismissExplorerHint = useCallback(() => {
    setShowExplorerHint(false);
    localStorage.setItem('idealscribe_explorer_hint_dismissed', 'true');
  }, []);

  const handleSelectionStart = useCallback(() => {
    setIsSelectionMode((prev) => {
      if (!prev) {
        setSelectedNoteIds([]);
      }
      return true;
    });
  }, []);

  const handleToggleNoteSelection = useCallback((noteId) => {
    setSelectedNoteIds((prev) => {
      if (prev.includes(noteId)) {
        return prev.filter((id) => id !== noteId);
      }
      return [...prev, noteId];
    });
  }, []);

  const handleConvertSelectedNotes = () => {
    // If no notes selected, enable selection mode so user can select notes
    if (selectedNoteIds.length === 0) {
      setIsSelectionMode(true);
      return;
    }

    if (selectedNotes.length === 0) {
      return;
    }

    const prefillPayload = buildPackitPrefillPayload(selectedNotes, userHomeLocation);
    if (!prefillPayload) {
      return;
    }

    navigate('/create-pack-it', {
      state: {
        prefill: prefillPayload,
        from: '/app',
      },
    });

    clearSelectedNotes();
  };

  const handleDeleteSelectedNotes = async () => {
    // If no notes selected, enable selection mode so user can select notes
    if (selectedNoteIds.length === 0) {
      setIsSelectionMode(true);
      return;
    }

    const noteCount = selectedNoteIds.length;
    const confirmed = window.confirm(
      `Are you sure you want to delete ${noteCount} note${noteCount > 1 ? 's' : ''}? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      // Get the notes to delete with their lastEdited timestamps
      const notesToDelete = notes.filter(note => 
        note && selectedNoteIds.includes(note.noteID)
      );

      // Delete all selected notes in parallel
      const deletePromises = notesToDelete.map(note => 
        axios.delete(`${apiUrl}/notes/${note.noteID}?lastEdited=${encodeURIComponent(note.lastEdited || '')}`, { headers })
          .catch(error => {
            console.error(`Error deleting note ${note.noteID}:`, error);
            // Continue with other deletions even if one fails
            return null;
          })
      );

      await Promise.all(deletePromises);
      
      // Clear selection, reset to first page, and refresh notes
      clearSelectedNotes();
      setCurrentPage(1);
      setPaginationTokens({});
      setLastEvaluatedKey(null);
      fetchNotes(plan, 1, null);
    } catch (error) {
      console.error('Error deleting selected notes:', error);
      alert('An error occurred while deleting notes. Please try again.');
    }
  };

  const handleCompleteToggle = async (noteId, completedDate = null) => {
    const noteToToggle = notes.find(note => note.noteID === noteId);
    if (!noteToToggle) {
      console.error('Note not found:', noteId);
      return;
    }

    // Check if note is currently completed
    const isCurrentlyCompleted = noteToToggle.completed === true || noteToToggle.completed === 'True' || noteToToggle.completed === 'true';
    
    // If marking as completed, show modal to get date
    if (!isCurrentlyCompleted) {
      setPendingCompleteNoteId(noteId);
      setShowCompletedDateModal(true);
      return; // Don't proceed with update yet, wait for modal confirmation
    }

    // If unmarking as incomplete, proceed immediately (no date needed)
    await updateNoteCompletion(noteId, false, null);
  };

  const handleCompletedDateSave = async (completedDate) => {
    if (!pendingCompleteNoteId) {
      console.error('No pending note ID for completion');
      return;
    }

    // Close modal first
    setShowCompletedDateModal(false);
    const noteId = pendingCompleteNoteId;
    setPendingCompleteNoteId(null);

    // Update note with completion and date
    await updateNoteCompletion(noteId, true, completedDate);
  };

  const handleCompletedDateCancel = () => {
    setShowCompletedDateModal(false);
    setPendingCompleteNoteId(null);
    // Don't change completion status if user cancels
  };

  const updateNoteCompletion = async (noteId, completed, completedDate) => {
    // Store previous notes for error recovery
    const previousNotes = [...notes];
    
    const updatedNotes = notes.map((note) => {
      if (note.noteID === noteId) {
        const updatedNote = { ...note };
        updatedNote.completed = completed;
        if (completedDate) {
          updatedNote.completedDate = completedDate;
        } else {
          // Clear completedDate when unmarking as incomplete
          const { completedDate: _, ...noteWithoutDate } = updatedNote;
          return noteWithoutDate;
        }
        return updatedNote;
      }
      return note;
    });
  
    // Skip next applyFilters runs so our optimistic update sticks (2 for React StrictMode double-invoke)
    skipApplyFiltersCountRef.current = 2;
    setNotes(updatedNotes);
    // Optimistically update filteredNotes so checkmark icon updates immediately (mark/unmark)
    setFilteredNotes(prev => prev.map((note) => {
      if (note.noteID !== noteId) return note;
      const updated = { ...note, completed };
      if (completedDate) {
        updated.completedDate = completedDate;
      } else {
        delete updated.completedDate;
      }
      return updated;
    }));
  
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };
  
      const noteToUpdate = updatedNotes.find(note => note.noteID === noteId);
      if (!noteToUpdate) {
        console.error('Note not found:', noteId);
        return;
      }
  
      const { noteCreationDate, lastEdited, fromEmail, noteActive, completed: noteCompleted, manualPromptResults, aiPromptResults } = noteToUpdate;
      const emailSubject = manualPromptResults?.emailSubject;
      const manualTags = noteToUpdate.manualPromptResults?.manualTags || {};
      const aiRecommendations = {
        address: aiPromptResults?.address || { value: '', probability: 0 },
        geolat_long: aiPromptResults?.geolat_long || { value: '' },
        recTags: Array.isArray(aiPromptResults?.recTags) ? aiPromptResults.recTags : [],
        activities: Array.isArray(aiPromptResults?.activities) ? aiPromptResults.activities : [],
        lat_long: aiPromptResults?.lat_long || { value: '', probability: 0 },
        name: aiPromptResults?.name || { value: '', probability: 0 },
        description: aiPromptResults?.description || { value: '', probability: 0 },
        recTimes: Array.isArray(aiPromptResults?.recTimes) ? aiPromptResults.recTimes : [],
        category: Array.isArray(aiPromptResults?.category) ? aiPromptResults.category : [],
        nearby: Array.isArray(aiPromptResults?.nearby) ? aiPromptResults.nearby : []
      };
  
      const noteDetails = {
        ...(noteCreationDate && { dateCreated: formatTimestamp(new Date(noteCreationDate)) }),
        ...(lastEdited && { lastUpdated: formatTimestamp(new Date(lastEdited)) }),
        ...(fromEmail && { fromEmail }),
        ...(emailSubject && { emailSubject }),
        ...(noteCompleted != null && { completed: noteCompleted ? 'True' : 'False' }),
        ...(noteActive != null && { activeState: noteActive ? 'True' : 'False' }),
        ...(completedDate && { completedDate })
      };
  
      const urlsArray = noteToUpdate.manualPromptResults?.url || [];
      const urlsPresent = urlsArray.length > 0;
  
      const combinedBodyText = JSON.stringify({
        bodyText: noteToUpdate.manualPromptResults?.bodyText || '',
        url: urlsArray,
        urlsPresent: urlsPresent,
        noteDetails,
        tags: manualTags,
        aiRecommendations,
        lastEdited
      }, null, 2);
  
      // Prepare PUT request body with completedDate at the same level as completed
      const putBody = {
        bodyText: combinedBodyText,
        completed: noteCompleted ? 'True' : 'False',
        ...(completedDate ? { completedDate } : { completedDate: null })
      };
  
      await axios.put(
        `${apiUrl}/notes/${noteId}`,
        putBody,
        { headers }
      );

      // Markers update via normal flow: filteredNotes -> markerData -> markers -> useEffect
      // (updateMapMarkers uses stale closure; filteredNotesKey now includes completed)
      
      // Trigger journey progress recalculation when note completion changes
      // This will update journey overlays and journey detail views via WebSocket
      try {
        const userID = getUserFromToken(token);
        if (userID && journeys.length > 0) {
          // Build POST body with journeys array (journeyID and ownerID) and clientId
          // Use connectionId (base64 string) to match briefings API pattern, not clientId (UUID)
          const postBody = {
            journeys: journeys
              .filter(j => j.journeyID && j.ownerID) // Only include journeys with both journeyID and ownerID
              .map(j => ({
                journeyID: j.journeyID,
                ownerID: j.ownerID
              })),
            clientId: connectionId || ''
          };
          
          // Trigger recalculation asynchronously (non-blocking)
          axios.post(
            `${apiUrl}/users/${encodeURIComponent(userID)}/journey-progress?sendWebSocket=true`,
            postBody,
            { headers }
          ).then(response => {
            if (response.status === 201) {
              console.log('Journey progress recalculation triggered after note completion (201)', response.data);
            } else {
              console.log('Journey progress recalculation triggered (status:', response.status, ')', response.data);
            }
          }).catch(progressError => {
            // Don't fail note completion if progress recalculation fails
            console.warn('Failed to trigger journey progress recalculation after note completion:', progressError);
          });
        }
      } catch (progressError) {
        // Don't fail note completion if progress recalculation setup fails
        console.warn('Error setting up journey progress recalculation:', progressError);
      }
      
    } catch (error) {
      console.error('Error updating completion status:', error);
      // Revert local state on error
      setNotes(previousNotes);
      alert('Failed to update note completion. Please try again.');
    }
  };
  
  
  const handleFavoriteToggle = async (noteId) => {
    const updatedNotes = notes.map((note) => {
      if (note.noteID === noteId) {
        if (note.manualPromptResults?.manualTags?.Favorite) {
          delete note.manualPromptResults.manualTags.Favorite;
        } else {
          if (!note.manualPromptResults) {
            note.manualPromptResults = {};
          }
          if (!note.manualPromptResults.manualTags) {
            note.manualPromptResults.manualTags = {};
          }
          note.manualPromptResults.manualTags.Favorite = 'Favorite';
        }
      }
      return note;
    });

    setNotes(updatedNotes);

    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();

      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      const noteToUpdate = updatedNotes.find(note => note.noteID === noteId);
      if (!noteToUpdate) {
        console.error('Note not found:', noteId);
        return;
      }

      const { noteCreationDate, lastEdited, fromEmail, noteActive, completed, manualPromptResults } = noteToUpdate;
      const emailSubject = manualPromptResults?.emailSubject;
      const manualTags = noteToUpdate.manualPromptResults?.manualTags || {};
      const aiPromptResults = noteToUpdate.aiPromptResults || {};

      const noteDetails = {
        ...(noteCreationDate && { dateCreated: formatTimestamp(new Date(noteCreationDate)) }),
        ...(lastEdited && { lastUpdated: formatTimestamp(new Date(lastEdited)) }),
        ...(fromEmail && { fromEmail }),
        ...(emailSubject && { emailSubject }),
        ...(completed != null && { completed: completed ? 'True' : 'False' }),
        ...(noteActive != null && { activeState: noteActive ? 'True' : 'False' })
      };

      const tags = Object.entries(manualTags).reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

      const aiRecommendations = {
        address: aiPromptResults.address || { value: '', probability: 0 },
        geolat_long: aiPromptResults.geolat_long || { value: '' }, // Ensure geolat_long is included
        recTags: Array.isArray(aiPromptResults.recTags) ? aiPromptResults.recTags : [],
        activities: Array.isArray(aiPromptResults.activities) ? aiPromptResults.activities : [],
        lat_long: aiPromptResults.lat_long || { value: '', probability: 0 },
        name: aiPromptResults.name || { value: '', probability: 0 },
        description: aiPromptResults.description || { value: '', probability: 0 },
        recTimes: Array.isArray(aiPromptResults.recTimes) ? aiPromptResults.recTimes : [],
        category: Array.isArray(aiPromptResults.category) ? aiPromptResults.category : [],
        nearby: Array.isArray(aiPromptResults.nearby) ? aiPromptResults.nearby : []
      };

      const updatedTime = formatTimestamp(new Date());

      const urlsArray = noteToUpdate.manualPromptResults?.url || [];
      const urlsPresent = urlsArray.length > 0;

      const combinedBodyText  = JSON.stringify({
        bodyText: noteToUpdate.manualPromptResults?.bodyText || '',
        url: urlsArray,
        urlsPresent: urlsPresent,
        noteDetails,
        tags,
        aiRecommendations,
        //clientId: connectionId, // Use clientId.current here
        lastEdited
      }, null, 2);

      await axios.put(
        //`https://rccd7493ld.execute-api.us-west-2.amazonaws.com/dev/notes/${noteId}`,
        `${apiUrl}/notes/${noteId}`,
        { bodyText: combinedBodyText },
        { headers }
      );

    } catch (error) {
      console.error('Error updating favorite status:', error);
    }
  };

  const handleMapClick = async (e) => {
    // Check if the clicked event contains latLng and pixel information
    if (!e.latLng) {
      console.warn('Map click event does not contain latLng. This might be a click on a Google Maps icon.');
      return; // Exit early if latLng is not available
    }
  
    // Fetch latitude and longitude from the clicked position
    const latLng = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
  
    // Check if pixel information is available
    if (!e.pixel) {
      console.warn('Map click event does not contain pixel information.');
      // Fetch place details using Google's Places API
      const service = new window.google.maps.places.PlacesService(mapRef.current);
      const request = {
        location: new window.google.maps.LatLng(latLng.lat, latLng.lng),
        radius: '50' // Radius to search for place details
      };
  
      service.nearbySearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results[0]) {
          const place = results[0];
          console.log('Place details:', place);
          //alert(`Place: ${place.name}\nAddress: ${place.vicinity}`);
        } else {
          console.warn('No place details found.');
        }
      });
  
      return; // Exit after displaying place details
    }
  
    // If pixel information is available, handle the map click normally
    const x = e.pixel.x;
    const y = e.pixel.y;
  
    setClickPosition((prevPosition) => {
      if (prevPosition) {
        return null;
      }
      return { lat: latLng.lat, lng: latLng.lng, x, y };
    });
  };

    // Example function to update plan and provider based on user interaction
  const updatePlanAndProvider = (newPlan) => {
      setPlan(newPlan);
      // Update the provider based on the plan
      const newProvider = newPlan === 'Basic' ? 'mapbox' : 'google';
      setProvider(newProvider);
      
      console.log(`Plan updated to ${newPlan}, provider set to ${newProvider}`);
    };
  
      // Track page views on route changes
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);



  // Sync the provider state to providerRef
  useEffect(() => {
      providerRef.current = provider;
    }, [provider]);

  useEffect(() => {
    //console.error('UseEffect1: ');
    clientIdRef.current = clientId;
  }, [clientId]);

  useEffect(() => {
    const initializeMapCenter = async () => {
      // Skip when doing completion update - don't pan away from current view
      if (skipApplyFiltersCountRef.current > 0) return;
      //console.error('UseEffect2: ');
      if (notes.length > 0) {
        // Sort notes by lastEdited in descending order to get the most recent one
        const lastEditedNote = notes.sort((a, b) => new Date(b.lastEdited) - new Date(a.lastEdited))[0];
  
        // Use lat_long from backend - geolocation is now handled on the backend
        const latLong = lastEditedNote.aiPromptResults?.lat_long?.value;
        const address = lastEditedNote.manualPromptResults?.manualTags?.Address || lastEditedNote.aiPromptResults?.address?.value;
        //console.error('UseEffect2: ' + lastEditedNote.aiPromptResults?.lat_long?.value);
        //console.error('UseEffect2: ');
        const latLng = parseAndValidateLatLong(latLong);
        if (latLng) {
          //console.log("Change lat long1?: " + latLng.lat +","+latLng.lng);
          setCenter({ lat: latLng.lat, lng: latLng.lng });
          setZoom(DEFAULT_ZOOM);

          if (mapRef.current) {
            //console.log("Change lat long2?: " + address);
            //console.log("Change lat long3?: " + latLng.lat +","+latLng.lng);
            mapRef.current.panTo({ lat: latLng.lat, lng: latLng.lng });
            mapRef.current.setZoom(DEFAULT_ZOOM);
          }
        }
        // Note: Geolocation lookup removed - lat_long is now handled on the backend
      } else {
        setCenter(DEFAULT_CENTER);
        setZoom(DEFAULT_ZOOM);
      }
    };
  
    // Call the function once notes are fetched and loaded
    if (notes.length > 0) {
      initializeMapCenter();
    }
  }, [notes]); // Changed from [notes.length] to [notes] to prevent infinite loops
  
  // Update filtered notes when notes or filters change (filtering is done on current page only)
  useEffect(() => {
    if (skipApplyFiltersCountRef.current > 0) {
      skipApplyFiltersCountRef.current -= 1;
      return; // Skip so completion toggle's optimistic update sticks and map doesn't pan away
    }
    if (notes && notes.length > 0) {
      console.log('Notes or filters changed, updating filtered notes. Total notes:', notes.length);
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, filters]); // Re-filter when notes or filters change (applyFilters is stable useCallback)
  
  // Track previous filters to detect changes
  const prevFiltersRef = useRef(filters);
  
  // When filters change, reset to page 1 and fetch
  useEffect(() => {
    // Check if filters actually changed (not just initial mount)
    const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    
    if (filtersChanged && currentPage !== 1) {
      // Reset to page 1 when filters change (and we're not already on page 1)
      // Clear all pagination state to prevent duplicates
      setCurrentPage(1);
      setPaginationTokens({});
      setLastEvaluatedKey(null);
      setAllNotesLoaded(false);
      setTotalNotesCount(0);
      fetchNotes(plan, 1, null);
    }
    // Note: If already on page 1, the applyFilters useEffect (with filters dependency) will handle filtering
    
    prevFiltersRef.current = filters;
  }, [filters, currentPage, plan]); // Reset to page 1 when filters change

  useEffect(() => {
    fetchUserInfo();
    // fetchNotes will be called after fetchUserInfo completes (inside fetchUserInfo)
  }, []); // Removed [plan] dependency to prevent infinite loop

  // Fetch journeys when explorer tab is active
  useEffect(() => {
    if (activeTab === 'explorer') {
      fetchMyJourneys();
    }
  }, [activeTab]);

  // Apply journey filters when filters or journeys change
  useEffect(() => {
    applyJourneyFilters(journeyFilters);
  }, [journeyFilters, journeys, applyJourneyFilters]);

  // Track if this is the initial mount to avoid double-fetching on load
  const isInitialMount = useRef(true);

  // Refresh notes when navigating back to /app (e.g., after creating a note)
  useEffect(() => {
    // Skip on initial mount (fetchUserInfo already handles that)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Only refresh when navigating to /app and we have a plan
    if (location.pathname === '/app' && plan) {
      // Reset to first page and clear pagination to ensure we get all notes
      setCurrentPage(1);
      setPaginationTokens({});
      setLastEvaluatedKey(null);
      // Refresh notes from the beginning
      fetchNotes(plan, 1, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, plan]); // fetchNotes is stable enough - we check pathname to prevent infinite loops

  // Performance monitoring for WebSocket (only in development)
  useEffect(() => {
    // Only enable memory logging in development mode
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && window.performance) {
      // Monitor memory usage
      let lastLoggedMemory = 0;
      const logMemoryUsage = () => {
        if (performance.memory) {
          const memoryInfo = {
            used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
            total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
            limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
          };
          
          // Only log if memory usage changed significantly (more than 10MB) or if it's high
          const memoryChanged = Math.abs(memoryInfo.used - lastLoggedMemory) > 10;
          const isHighMemory = memoryInfo.used > memoryInfo.limit * 0.9; // 90% threshold
          
          if (memoryChanged || isHighMemory) {
            // Get current overlay and GeoJSON counts
            const overlayCount = journeyOverlaysRef.current?.size || 0;
            const geoJsonCount = loadedGeoJsonRef.current?.size || 0;
            
            if (isHighMemory) {
              console.warn('High memory usage detected:', {
                ...memoryInfo,
                overlayCount,
                geoJsonCount,
                percentage: Math.round((memoryInfo.used / memoryInfo.limit) * 100) + '%'
              });
            } else {
              console.log('Memory usage:', {
                ...memoryInfo,
                overlayCount,
                geoJsonCount
              });
            }
            lastLoggedMemory = memoryInfo.used;
          }
        }
      };

      // Log memory usage every 60 seconds (reduced frequency) (only in development)
      const memoryInterval = setInterval(logMemoryUsage, 60000);
      
      // Log connection quality changes (only once)
      if (connectionQuality) {
        console.log('WebSocket connection quality:', connectionQuality);
      }

      return () => {
        clearInterval(memoryInterval);
      };
    }
  }, []); // Removed connectionQuality dependency to prevent continuous re-initialization


  // Separate function for heavy processing to avoid blocking main thread
  const processNewNote = async (newNote) => {
    try {
      // lat_long is now handled on the backend, no geolocation lookup needed
      // This function can be used for other processing if needed in the future
      if (!newNote.aiPromptResults?.lat_long?.value) {
        console.log('Note does not have lat_long - backend should provide this');
      }
    } catch (error) {
      console.error('Error in processNewNote:', error);
    }
  };

  useEffect(() => {
    if (!socket) return;

    const safeParse = (raw) => {
      // Try to parse repeatedly if the server double-stringified the payload.
      let parsed = raw;
      let attempts = 0;
      const maxAttempts = 5; // safety to avoid infinite loop
      while (typeof parsed === 'string' && attempts < maxAttempts) {
        try {
          parsed = JSON.parse(parsed);
        } catch (err) {
          // If parsing fails, stop and return whatever we have
          console.error('safeParse failed at attempt', attempts, err);
          break;
        }
        attempts += 1;
      }
      return parsed;
    };

    const handleMessage = async (event) => {
  try {
    console.log('Raw WebSocket message received:', event.data);

    // Normalize parsed data (handles double-stringified JSON)
    let data;
    if (typeof event.data === 'string' || typeof event.data === 'object') {
      data = safeParse(event.data);
    } else {
      data = event.data;
    }

    console.log('After safeParse, typeof data:', typeof data, 'value:', data);

    // Ensure we have an object
    if (!data || typeof data !== 'object') {
      console.warn('Parsed message is not an object — ignoring.', data);
      return;
    }

    console.log('Data.type:', data.type, 'clientId:', data.clientId);

    if (data.type === 'newNote') {
      console.log('Processing newNote message with clientId:', data.clientId, 'connectionId:', connectionId);

      const noteData = data.note;
      if (!noteData || typeof noteData !== 'object') {
        console.warn('newNote message missing note object; skipping.', noteData);
        return;
      }

      const newNote = {
        noteID: noteData.noteID,
        manualPromptResults: noteData.manualPromptResults,
        aiPromptResults: noteData.aiPromptResults,
        noteActive: noteData.noteActive,
        completed: noteData.completed,
        noteCreationDate: noteData.noteCreationDate,
        fromEmail: noteData.fromEmail,
        lastEdited: noteData.lastEdited,
      };

      console.log('Processing new note from WebSocket:', newNote);

      setNotes((prevNotes) => {
        const updatedNotes = [...prevNotes, newNote];
        updatedNotes.sort((a, b) => new Date(b.lastEdited) - new Date(a.lastEdited));
        return updatedNotes;
      });

      setCurrentPage(1);

      // run heavier things after state update
      await processNewNote(newNote);
      
      // Update markers with new note
      // Use lat_long from backend - geolocation is now handled on the backend
      const latLng = parseAndValidateLatLong(newNote.aiPromptResults?.lat_long?.value);

      if (latLng) {
        // Use the effective category helper function
        const category = getEffectiveCategory(newNote);
        const marker = {
          lat: latLng.lat,
          lng: latLng.lng,
          noteID: newNote.noteID,
          title: newNote.manualPromptResults?.emailSubject,
          body: newNote.manualPromptResults?.url,
          category: category,
        };
        setMarkers((prevMarkers) => [...prevMarkers, marker]);
        setCenter({ lat: latLng.lat, lng: latLng.lng });
        setZoom(12);

        if (mapRef.current) {
          mapRef.current.panTo({ lat: latLng.lat, lng: latLng.lng });
          mapRef.current.setZoom(12);
        }
      }
    } else {
      console.log('WebSocket message did not match newNote condition. Message type:', data.type);
    }
  } catch (error) {
    console.error('Error handling WebSocket message:', error);
  }
};

    // Use addEventListener instead of direct assignment to prevent memory leaks
    socket.addEventListener('message', handleMessage);

    // Cleanup function to remove event listener
    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [socket, connectionId]);

  // WebSocket listener for journey updates
  useEffect(() => {
    if (!socket) return;

    const safeParse = (raw) => {
      let parsed = raw;
      let attempts = 0;
      const maxAttempts = 5;
      while (typeof parsed === 'string' && attempts < maxAttempts) {
        try {
          parsed = JSON.parse(parsed);
        } catch (err) {
          console.error('safeParse failed at attempt', attempts, err);
          break;
        }
        attempts += 1;
      }
      return parsed;
    };

    // Debounce timer for journey progress updates to prevent rapid successive updates
    let progressUpdateTimeout = null;
    const DEBOUNCE_DELAY = 1000; // 1 second debounce

    const handleJourneyMessage = async (event) => {
      try {
        console.log('Raw WebSocket message received for journeys:', event.data);

        let data;
        if (typeof event.data === 'string' || typeof event.data === 'object') {
          data = safeParse(event.data);
        } else {
          data = event.data;
        }

        console.log('Parsed WebSocket journey message:', data);

        if (!data || typeof data !== 'object') {
          return;
        }

        // Handle journey progress updates - silently update journey progress without full refetch
        if (data.type === 'journeyProgressUpdated' || data.type === 'Journeyprogressupdated') {
          console.log('Journey progress updated via WebSocket, updating silently:', data);
          
          // Clear any pending debounce timer
          if (progressUpdateTimeout) {
            clearTimeout(progressUpdateTimeout);
          }
          
          // Debounce the update to prevent rapid successive updates
          progressUpdateTimeout = setTimeout(async () => {
            try {
              const token = (await fetchAuthSession()).tokens?.idToken?.toString();
              const headers = {
                'Content-type': 'application/json; charset=UTF-8',
                'Authorization': `Bearer ${token}`
              };
              
              const userID = getUserFromToken(token);
              if (!userID) return;
              
              // Fetch only the journeyProgress data, not all journeys
              const userResponse = await axios.get(`${apiUrl}/users/${encodeURIComponent(userID)}?journeyComplete=true`, { headers });
              const currentUserData = Array.isArray(userResponse.data) ? userResponse.data[0] : userResponse.data;
              
              // Parse journeyProgress structure
              const journeyProgressObj = currentUserData?.journeyProgress || {};
              const journeyProgressArray = journeyProgressObj?.journeyProgress || [];
              
              // Create map for quick lookup
              const journeyProgressMap = {};
              journeyProgressArray.forEach(progress => {
                if (progress.journeyID) {
                  const completedLocationIds = [];
                  const visitedContinents = new Set(); // Track visited continents for continentStyling
                  const completedGeoids = new Set(); // Track completed GEOIDs for StateStyling_US
                  
                  if (progress.compGeoBoundaries && Array.isArray(progress.compGeoBoundaries)) {
                    progress.compGeoBoundaries.forEach(boundary => {
                      if (boundary.properties) {
                        if (boundary.properties.OBJECTID !== undefined && boundary.properties.OBJECTID !== null) {
                          completedLocationIds.push(boundary.properties.OBJECTID);
                        }
                        if (boundary.properties.name) {
                          completedLocationIds.push(boundary.properties.name);
                        }
                        if (boundary.properties.GEOID !== undefined && boundary.properties.GEOID !== null) {
                          completedLocationIds.push(boundary.properties.GEOID);
                          completedGeoids.add(String(boundary.properties.GEOID));
                        }
                        // Add woe_id if available (for Google dataset matching)
                        if (boundary.properties.woe_id !== undefined && boundary.properties.woe_id !== null) {
                          completedLocationIds.push(boundary.properties.woe_id);
                        }
                        // Extract continent for continentStyling logic
                        if (boundary.properties.continent) {
                          visitedContinents.add(boundary.properties.continent);
                        }
                      }
                    });
                  }
                  
                  journeyProgressMap[progress.journeyID] = {
                    journeyPercentComplete: progress.journeyPercentComplete || 0,
                    completedLocationIds: completedLocationIds,
                    visitedContinents: Array.from(visitedContinents), // Convert Set to Array for storage
                    completedGeoids: Array.from(completedGeoids) // Convert Set to Array for StateStyling_US
                  };
                }
              });
              
              // Remove ALL journeys that have progress data from calculating set
              // The presence in journeyProgressMap means backend has finished calculating (even if 0%)
              setCalculatingJourneyIds(prev => {
                const updated = new Set(prev);
                // Remove all journey IDs that are in the progress map
                Object.keys(journeyProgressMap).forEach(journeyID => {
                  updated.delete(journeyID);
                });
                return updated;
              });
              
              // Silently update journeys state with new progress data without refetching all journey details
              setJourneys((prevJourneys) => {
                return prevJourneys.map(journey => {
                  const progressData = journeyProgressMap[journey.journeyID] || {
                    journeyPercentComplete: 0,
                    completedLocationIds: [],
                    visitedContinents: [],
                    completedGeoids: []
                  };
                  
                  // Recalculate percentComplete
                  const percentComplete = (journey.manualPercentComplete !== undefined && 
                                           journey.manualPercentComplete !== null && 
                                           journey.manualPercentComplete !== 0)
                    ? journey.manualPercentComplete
                    : (progressData.journeyPercentComplete !== undefined ? progressData.journeyPercentComplete : (journey.percentComplete || 0));
                  
                  // Only update if progress data actually changed
                  if (journey.journeyProgress?.length !== progressData.completedLocationIds.length ||
                      journey.journeyPercentComplete !== progressData.journeyPercentComplete ||
                      journey.percentComplete !== percentComplete ||
                      JSON.stringify(journey.visitedContinents || []) !== JSON.stringify(progressData.visitedContinents || []) ||
                      JSON.stringify(journey.completedGeoids || []) !== JSON.stringify(progressData.completedGeoids || [])) {
                    return {
                      ...journey,
                      journeyPercentComplete: progressData.journeyPercentComplete,
                      percentComplete: percentComplete,
                      journeyProgress: progressData.completedLocationIds,
                      visitedContinents: progressData.visitedContinents || [],
                      completedGeoids: progressData.completedGeoids || []
                    };
                  }
                  
                  return journey; // No changes, return as-is
                });
              });
              
              // Also update filtered journeys
              setFilteredJourneys((prevFilteredJourneys) => {
                return prevFilteredJourneys.map(journey => {
                  const progressData = journeyProgressMap[journey.journeyID] || {
                    journeyPercentComplete: 0,
                    completedLocationIds: [],
                    visitedContinents: [],
                    completedGeoids: []
                  };
                  
                  const percentComplete = (journey.manualPercentComplete !== undefined && 
                                           journey.manualPercentComplete !== null && 
                                           journey.manualPercentComplete !== 0)
                    ? journey.manualPercentComplete
                    : (progressData.journeyPercentComplete !== undefined ? progressData.journeyPercentComplete : (journey.percentComplete || 0));
                  
                  if (journey.journeyProgress?.length !== progressData.completedLocationIds.length ||
                      journey.journeyPercentComplete !== progressData.journeyPercentComplete ||
                      journey.percentComplete !== percentComplete ||
                      JSON.stringify(journey.visitedContinents || []) !== JSON.stringify(progressData.visitedContinents || []) ||
                      JSON.stringify(journey.completedGeoids || []) !== JSON.stringify(progressData.completedGeoids || [])) {
                    return {
                      ...journey,
                      journeyPercentComplete: progressData.journeyPercentComplete,
                      percentComplete: percentComplete,
                      journeyProgress: progressData.completedLocationIds,
                      visitedContinents: progressData.visitedContinents || [],
                      completedGeoids: progressData.completedGeoids || []
                    };
                  }
                  
                  return journey;
                });
              });
              
              console.log('Journey progress updated silently without full refetch');
            } catch (error) {
              console.error('Error updating journey progress silently:', error);
              // Fallback: if silent update fails, do a full refetch (but this shouldn't happen often)
              // await fetchMyJourneys();
            }
          }, DEBOUNCE_DELAY);
          
          return;
        }

        // Handle journeyGeoJSONGenerated messages
        if (data.type === 'journeyGeoJSONGenerated') {
          console.log('Processing journeyGeoJSONGenerated WebSocket message:', data);
          
          const journeyId = data.journeyID || data.journeyId;
          if (!journeyId) {
            console.warn('journeyGeoJSONGenerated message missing journeyID');
            return;
          }
          
          // Fetch the updated journey with GeoJSON
          try {
            const token = (await fetchAuthSession()).tokens?.idToken?.toString();
            const headers = {
              'Content-type': 'application/json; charset=UTF-8',
              'Authorization': `Bearer ${token}`
            };
            
            // Determine dataType from journey if available, otherwise default to geoJSON
            // For journeyGeoJSONGenerated, we typically want geoJSON, but check if journey has dataType
            const dataType = 'geojson'; // Default for newly generated journeys
            const journeyResponse = await axios.get(
              `${apiUrl}/journeys/${journeyId}?dataType=${dataType}`,
              { headers }
            );
            const journeyData = journeyResponse.data;
            
            // Check if this journey is in pendingJourneys (created by user)
            const isPendingJourney = window.pendingJourneys && window.pendingJourneys.has(journeyId);
            
            if (isPendingJourney) {
              // Remove from pending set
              window.pendingJourneys.delete(journeyId);
              
              // Show notification to user
              if (data.status === 'completed' && journeyData.geospatialBoundaries) {
                const shouldReview = window.confirm(
                  `Your journey "${journeyData.title || journeyId}" has been completed! Would you like to review and accept it?`
                );
                
                if (shouldReview) {
                  // Dispatch event to open CreateJourneyModal in review mode
                  const reviewEvent = new CustomEvent('openJourneyReview', {
                    detail: {
                      journeyID: journeyId,
                      journeyData: journeyData,
                      geospatialBoundaries: journeyData.geospatialBoundaries
                    }
                  });
                  window.dispatchEvent(reviewEvent);
                }
              } else if (data.status === 'error') {
                alert(`Error generating journey "${journeyData.title || journeyId}": ${data.error || 'Unknown error'}`);
              }
            }
            
            // Dispatch custom event to notify CreateJourneyModal (for backward compatibility)
            const event = new CustomEvent(`journeyReady_${journeyId}`, {
              detail: {
                journeyID: journeyId,
                status: data.status || 'completed',
                error: data.error || null,
                geospatialBoundaries: journeyData.geospatialBoundaries || null,
                locationCount: data.locationCount || null,
                message: data.message || null
              }
            });
            window.dispatchEvent(event);
            
            // Also update the journeys list if the journey exists
            if (data.status === 'completed' && journeyData.geospatialBoundaries) {
              setJourneys((prevJourneys) => {
                const journeyExists = prevJourneys.some(j => j.journeyID === journeyId);
                if (journeyExists) {
                  return prevJourneys.map((j) => {
                    if (j.journeyID === journeyId) {
                      return {
                        ...j,
                        geospatialBoundaries: journeyData.geospatialBoundaries
                      };
                    }
                    return j;
                  });
                }
                return prevJourneys;
              });
              
              // Update filtered journeys as well
              setFilteredJourneys((prevFilteredJourneys) => {
                const journeyExists = prevFilteredJourneys.some(j => j.journeyID === journeyId);
                if (journeyExists) {
                  return prevFilteredJourneys.map((j) => {
                    if (j.journeyID === journeyId) {
                      return {
                        ...j,
                        geospatialBoundaries: journeyData.geospatialBoundaries
                      };
                    }
                    return j;
                  });
                }
                return prevFilteredJourneys;
              });
            }
          } catch (error) {
            console.error('Error fetching journey after GeoJSON generation:', error);
            
            // Check if this journey is in pendingJourneys (created by user)
            const isPendingJourney = window.pendingJourneys && window.pendingJourneys.has(journeyId);
            
            if (isPendingJourney) {
              // Remove from pending set
              window.pendingJourneys.delete(journeyId);
              
              // Show error notification
              alert(`Error generating journey: ${error.message || 'Failed to fetch journey'}`);
            }
            
            // Still dispatch event with error status (for backward compatibility)
            const event = new CustomEvent(`journeyReady_${journeyId}`, {
              detail: {
                journeyID: journeyId,
                status: 'error',
                error: error.message || 'Failed to fetch journey'
              }
            });
            window.dispatchEvent(event);
          }
          
          return;
        }

        // Handle journey-related updates
        if (data.type === 'journeyUpdated' || data.type === 'userUpdated' || data.type === 'journeyAdded') {
          console.log('Processing journey WebSocket message:', data);

          // Check if this is a journey update (could be in user data or direct journey data)
          const journeyId = data.journeyId || data.journeyID || data.elementID;
          
          // If we have a specific journey ID, update only that journey
          if (journeyId) {
            console.log('Updating specific journey via WebSocket:', journeyId);
            
            // Fetch updated journey details
            try {
              const token = (await fetchAuthSession()).tokens?.idToken?.toString();
              const headers = {
                'Content-type': 'application/json; charset=UTF-8',
                'Authorization': `Bearer ${token}`
              };

              // Fetch updated user data to get the journey metadata with completion data
              const userID = getUserFromToken(token);
              if (userID) {
                const userResponse = await axios.get(`${apiUrl}/users/${encodeURIComponent(userID)}?journeyComplete=true`, { headers });
                const currentUserData = Array.isArray(userResponse.data) ? userResponse.data[0] : userResponse.data;
                const userJourneys = currentUserData?.journeys || [];
                
                // Parse journeyProgress structure
                const journeyProgressObj = currentUserData?.journeyProgress || {};
                const journeyProgressArray = journeyProgressObj?.journeyProgress || [];
                
                // Create map for quick lookup
                const journeyProgressMap = {};
                journeyProgressArray.forEach(progress => {
                  if (progress.journeyID) {
                    const completedLocationIds = [];
                    const visitedContinents = new Set(); // Track visited continents for continentStyling
                    const completedGeoids = new Set(); // Track completed GEOIDs for StateStyling_US
                    
                    if (progress.compGeoBoundaries && Array.isArray(progress.compGeoBoundaries)) {
                      progress.compGeoBoundaries.forEach(boundary => {
                        if (boundary.properties) {
                          if (boundary.properties.OBJECTID !== undefined && boundary.properties.OBJECTID !== null) {
                            completedLocationIds.push(boundary.properties.OBJECTID);
                          }
                          if (boundary.properties.name) {
                            completedLocationIds.push(boundary.properties.name);
                          }
                          // Add woe_id if available (for Google dataset matching)
                          if (boundary.properties.woe_id !== undefined && boundary.properties.woe_id !== null) {
                            completedLocationIds.push(boundary.properties.woe_id);
                          }
                          // Add GEOID if available (for StateStyling_US matching)
                          if (boundary.properties.GEOID !== undefined && boundary.properties.GEOID !== null) {
                            completedLocationIds.push(boundary.properties.GEOID);
                            completedGeoids.add(String(boundary.properties.GEOID));
                          }
                          // Extract continent for continentStyling logic
                          if (boundary.properties.continent) {
                            visitedContinents.add(boundary.properties.continent);
                          }
                        }
                      });
                    }
                    
                    journeyProgressMap[progress.journeyID] = {
                      journeyPercentComplete: progress.journeyPercentComplete || 0,
                      completedLocationIds: completedLocationIds,
                      visitedContinents: Array.from(visitedContinents), // Convert Set to Array for storage
                      completedGeoids: Array.from(completedGeoids) // Convert Set to Array for StateStyling_US
                    };
                  }
                });
                
                const updatedUserJourney = userJourneys.find(j => j.elementID === journeyId);

                if (updatedUserJourney) {
                  // Fetch full journey details - use dataType from journey if available
                  const dataType = updatedUserJourney.dataType || 'geojson';
                  const journeyResponse = await axios.get(`${apiUrl}/journeys/${journeyId}?dataType=${dataType}`, { headers });
                  const journeyDetails = journeyResponse.data;

                  // Get journey completion data
                  const progressData = journeyProgressMap[journeyId] || {
                    journeyPercentComplete: 0,
                    completedLocationIds: [],
                    visitedContinents: [],
                    completedGeoids: []
                  };
                  
                  // Use journeyPercentComplete from journeyProgress if manualPercentComplete is 0 or not set
                  const percentComplete = (updatedUserJourney.manualPercentComplete !== undefined && 
                                           updatedUserJourney.manualPercentComplete !== null && 
                                           updatedUserJourney.manualPercentComplete !== 0)
                    ? updatedUserJourney.manualPercentComplete
                    : (progressData.journeyPercentComplete !== undefined ? progressData.journeyPercentComplete : 0);

                  // Combine user journey metadata with full journey details
                  const updatedJourney = {
                    ...journeyDetails,
                    journeyID: journeyId,
                    journeyColor: updatedUserJourney.journeyColor,
                    displayItemsMode: updatedUserJourney.displayItemsMode,
                    overrides: updatedUserJourney.overrides || [],
                    isActive: updatedUserJourney.isActive,
                    manualPercentComplete: updatedUserJourney.manualPercentComplete,
                    journeyPercentComplete: progressData.journeyPercentComplete,
                    percentComplete: percentComplete,
                    journeyProgress: progressData.completedLocationIds,
                    visitedContinents: progressData.visitedContinents || [],
                    completedGeoids: progressData.completedGeoids || [],
                    displayJourney: updatedUserJourney.displayJourney,
                    isComplete: updatedUserJourney.isComplete,
                    isFavorite: updatedUserJourney.isFavorite
                  };

                  // Update or add the journey
                  setJourneys((prevJourneys) => {
                    const journeyExists = prevJourneys.some(j => j.journeyID === journeyId);
                    
                    if (journeyExists) {
                      // Update only the specific journey in the journeys array
                      console.log('Journey updated via WebSocket (existing journey):', updatedJourney);
                      return prevJourneys.map((j) =>
                        j.journeyID === journeyId ? updatedJourney : j
                      );
                    } else if (data.type === 'journeyAdded') {
                      // If it's a new journey, add it to the list
                      console.log('Journey added via WebSocket:', updatedJourney);
                      // Mark as calculating - backend will calculate progress
                      setCalculatingJourneyIds(prev => new Set([...prev, journeyId]));
                      return [...prevJourneys, updatedJourney];
                    }
                    return prevJourneys;
                  });

                  // Update filtered journeys as well
                  setFilteredJourneys((prevFilteredJourneys) => {
                    const journeyExists = prevFilteredJourneys.some(j => j.journeyID === journeyId);
                    
                    if (journeyExists) {
                      return prevFilteredJourneys.map((j) =>
                        j.journeyID === journeyId ? updatedJourney : j
                      );
                    } else if (data.type === 'journeyAdded') {
                      return [...prevFilteredJourneys, updatedJourney];
                    }
                    return prevFilteredJourneys;
                  });
                }
              }
            } catch (error) {
              console.error('Error fetching updated journey details from WebSocket:', error);
            }
          } else if (data.user && data.user.journeys) {
            // If user data includes journeys array but no specific journey ID, log it
            console.log('User journeys updated via WebSocket (no specific journey ID):', data);
          }
        }
      } catch (error) {
        console.error('Error handling WebSocket journey message:', error);
      }
    };

    socket.addEventListener('message', handleJourneyMessage);

    // Cleanup function to remove event listener and clear debounce timer
    return () => {
      socket.removeEventListener('message', handleJourneyMessage);
      if (progressUpdateTimeoutRef.current) {
        clearTimeout(progressUpdateTimeoutRef.current);
        progressUpdateTimeoutRef.current = null;
      }
    };
  }, [socket, connectionId]);

// Use the debounce function defined at the top of the file
const debouncedUpdateNoteWithGeolatLong = debounce(updateNoteWithGeolatLong, 1000); 


const applyFiltersToNewNote = (newNote, updatedNotes) => {
    console.log('applyFiltersToNewNote called with:', { newNote, updatedNotesCount: updatedNotes.length, filters });
    
    const matchesFilters = (note) => {
        const { manualPromptResults, aiPromptResults, noteCreationDate, fromEmail, noteActive, completed, lastEdited } = note;

        const bodyText = manualPromptResults?.bodyText?.toLowerCase() || '';
        const emailSubject = manualPromptResults?.emailSubject?.toLowerCase() || '';
        const name = aiPromptResults?.name?.value?.toLowerCase() || '';
        const description = aiPromptResults?.description?.value?.toLowerCase() || '';
        const lastEditedLower = lastEdited?.toLowerCase() || '';
        const fromEmailLower = fromEmail?.toLowerCase() || '';
        const noteCreationDateLower = noteCreationDate?.toLowerCase() || '';

        const matchesColor = filters.categoryColor
          ? (() => {
              const noteKey = normalizeColorKey(getEffectiveCategory(note));
              const filterKey = normalizeColorKey(filters.categoryColor);
              return Boolean(noteKey && filterKey && noteKey === filterKey);
            })()
          : true;
        const matchesFavorite = filters.favorite ? manualPromptResults?.manualTags?.Favorite?.toLowerCase() === 'favorite' : true;
        
        // Copy the working pattern from color and favorite filters
        const matchesCompleted = () => {
            const completedValue = completed ? 'true' : 'false';
            const result = filters.completed ? completedValue === filters.completed.toLowerCase() : true;
            console.log('matchesCompleted check:', { completed, completedValue, filterCompleted: filters.completed, result });
            return result;
        };

        const result = (
            (!filters.noteCreationDate || noteCreationDateLower.includes(filters.noteCreationDate.toLowerCase())) &&
            (!filters.fromEmail || fromEmailLower.includes(filters.fromEmail.toLowerCase())) &&
            matchesCompleted() &&
            (!filters.lastEdited || lastEditedLower.includes(filters.lastEdited.toLowerCase())) &&
            (!filters.bodyText || bodyText.includes(filters.bodyText.toLowerCase())) &&
            (!filters.emailSubject || emailSubject.includes(filters.emailSubject.toLowerCase())) &&
            (!filters.name || name.includes(filters.name.toLowerCase())) &&
            (!filters.description || description.includes(filters.description.toLowerCase())) &&
            matchesColor &&
            matchesFavorite
        );
        
        // Log filtering result for the new note
        if (note === newNote) {
            console.log('New note filter result:', { 
                noteSubject: emailSubject, 
                completed, 
                noteActive, 
                result,
                filters 
            });
        }
        
        return result;
    };

    // Apply filters to all notes, including the new note
    const filtered = updatedNotes.filter(matchesFilters);
    console.log('Filtered notes count:', filtered.length, 'out of', updatedNotes.length);
    
    // Sort by noteCreationDate in descending order (newest first)
    filtered.sort((a, b) => {
        const dateA = new Date(a.noteCreationDate || a.lastEdited || 0);
        const dateB = new Date(b.noteCreationDate || b.lastEdited || 0);
        return dateB - dateA;
    });
    
    return filtered;
};

  useEffect(() => {
    if (selectedNoteIds.length === 0) {
      return;
    }
    setSelectedNoteIds((prev) =>
      prev.filter((id) => notes.some((note) => note.noteID === id))
    );
  }, [notes]);


  // Filter out markers for deleted notes whenever notes change
  useEffect(() => {
    setMarkers((prevMarkers) => {
      const validMarkers = prevMarkers.filter((marker) => 
        notes.some((note) => note && note.noteID === marker.noteID)
      );
      // Only update if markers were actually removed
      if (validMarkers.length !== prevMarkers.length) {
        console.log(`Filtered out ${prevMarkers.length - validMarkers.length} markers for deleted notes`);
        return validMarkers;
      }
      return prevMarkers;
    });
  }, [notes]);

  // Update map markers only when markers state changes (markers are already derived from filteredNotes via markerData)
  useEffect(() => {
    if (mapRef.current && markers.length > 0) {
      //console.error('UseEffect6: ');
      updateMapMarkers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers]); // Only depend on markers, not filteredNotes (to avoid circular updates)

  // Handle orientation change to resize map
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current && window.google && window.google.maps) {
        // Trigger map resize after delays to ensure CSS has updated
        setTimeout(() => {
          if (mapRef.current) {
            window.google.maps.event.trigger(mapRef.current, 'resize');
            // Also try to recenter the map
            if (center) {
              mapRef.current.setCenter(center);
            }
          }
        }, 200);
        // Second resize trigger for reliability
        setTimeout(() => {
          if (mapRef.current) {
            window.google.maps.event.trigger(mapRef.current, 'resize');
          }
        }, 500);
      }
    };

    // Listen for orientation changes and window resize
    window.addEventListener('orientationchange', handleResize);
    window.addEventListener('resize', handleResize);
    // Also listen for media query changes
    const mediaQuery = window.matchMedia('(orientation: landscape)');
    mediaQuery.addEventListener('change', handleResize);

    return () => {
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('resize', handleResize);
      mediaQuery.removeEventListener('change', handleResize);
    };
  }, [center]);

  // Create a stable key for filteredNotes to prevent unnecessary marker recalculations
  // Include completed so marker icons (checkmark vs regular) update when completion changes
  const filteredNotesKey = useMemo(() => {
    return filteredNotes
      .map(n => `${n.noteID}-${n.aiPromptResults?.lat_long?.value || ''}-${n.completed}`)
      .sort()
      .join('|');
  }, [filteredNotes]);

  // Memoize marker data to prevent unnecessary recalculations
  // Only recalculate when the actual note IDs or coordinates change, not just array reference
  const markerData = useMemo(() => {
    return filteredNotes
      .map((item) => {
        // Use lat_long from backend - geolocation is now handled on the backend
        const latLng = parseAndValidateLatLong(item.aiPromptResults?.lat_long?.value);
        
        if (latLng) {
          // Use the effective category helper function
          const category = getEffectiveCategory(item);
          return {
            lat: latLng.lat,
            lng: latLng.lng,
            noteID: item.noteID,
            title: item.manualPromptResults?.emailSubject,
            body: item.manualPromptResults?.url,
            category: category,
          };
        }
        return null;
      })
      .filter((marker) => marker !== null);
  }, [filteredNotesKey]); // Only depend on the stable key, parseAndValidateLatLong is stable (useCallback)

  // Update markers only when marker data actually changes
  useEffect(() => {
    setMarkers(markerData);
  }, [markerData]);

  useEffect(() => {
    const root = document.documentElement;
    if (isNightMode) {
      root.style.setProperty('--bg-color', '#333');
      root.style.setProperty('--text-color', '#fff');
      root.style.setProperty('--sidebar-bg-color', '#444');
      root.style.setProperty('--note-bg-color', '#555');
      root.style.setProperty('--button-bg-color', '#555');
      root.style.setProperty('--button-hover-bg-color', '#666');
    } else {
      root.style.setProperty('--bg-color', '#fff');
      root.style.setProperty('--text-color', '#000');
      root.style.setProperty('--sidebar-bg-color', '#f8f9fa');
      root.style.setProperty('--note-bg-color', '#fff');
      root.style.setProperty('--button-bg-color', '#007bff');
      root.style.setProperty('--button-hover-bg-color', '#0056b3');
    }
  }, [isNightMode]);

  // Cleanup effect on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clean up all journey overlays
      if (journeyOverlaysRef.current) {
        journeyOverlaysRef.current.forEach((overlayData, journeyID) => {
          try {
            if (overlayData.dataLayer) {
              overlayData.dataLayer.setMap(null);
            }
            if (overlayData.datasetLayer) {
              overlayData.datasetLayer.style = null;
            }
            if (overlayData.infoWindow) {
              overlayData.infoWindow.close();
            }
          } catch (error) {
            console.error(`Error cleaning up overlay for ${journeyID}:`, error);
          }
        });
        journeyOverlaysRef.current.clear();
      }

      // Clean up journey info window
      if (journeyInfoWindowRef.current) {
        journeyInfoWindowRef.current.close();
        journeyInfoWindowRef.current = null;
      }

      // Clear progress update timeout
      if (progressUpdateTimeoutRef.current) {
        clearTimeout(progressUpdateTimeoutRef.current);
        progressUpdateTimeoutRef.current = null;
      }

      // Clear markers from map
      if (mapRef.current && mapRef.current.markers) {
        mapRef.current.markers.forEach(marker => {
          try {
            if (marker.setMap) {
              marker.setMap(null);
            }
          } catch (error) {
            // Silently handle cleanup errors
          }
        });
        mapRef.current.markers = [];
      }
    };
  }, []); // Only run on mount/unmount

  useEffect(() => {
    if (isLoaded && mapRef.current) {
      //console.error('UseEffect7: ');
      const initMap = async () => {
        mapRef.current.markers = [];
        await createAdvancedMarkers(mapRef.current, markers);
        //mapRef.current.panTo(DEFAULT_CENTER);
        mapRef.current.setZoom(DEFAULT_ZOOM);
      };
      initMap();
    }
  }, [isLoaded, markers]);

  if (loadError) {
    return <div>Error loading Google Maps API</div>;
  }

  return (
    <div className={`App ${isNightMode ? 'night-mode' : ''}`}>
      <Header currentPage="app" />
      <TutorialModal 
        isOpen={showTutorial} 
        onClose={() => setShowTutorial(false)}
        onComplete={handleTutorialComplete}
      />
      <CompletedDateModal
        isOpen={showCompletedDateModal}
        onClose={handleCompletedDateCancel}
        onSave={handleCompletedDateSave}
      />
      <div className="main-content">
        <div className="sidebar">
          <TabNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            showExplorerHint={showExplorerHint}
            onDismissExplorerHint={handleDismissExplorerHint}
          />
          
          {activeTab === 'quicknotes' ? (
            <ModernDashboard
            title="My Quick Notes"
            onCreateNote={handleCreateNoteClick}
            onToggleFilters={toggleFilters}
            filtersVisible={filtersVisible}
            showUpgradeMessage={showUpgradeMessage}
            showUpgradeLimitMessage={showUpgradeLimitMessage}
            onUpgradeClick={handleSubscriptionUpgrade}
            userPlan={plan}
            noteCount={notes.length}
            filters={filters}
            onFilterChange={handleFilterChange}
            onFilterSelect={handleFilterSelect}
            selectedFilter={selectedFilter}
            colorFilter={colorFilter}
            colorOptions={colorOptions}
            clearFilters={clearFilters}
            currentNotesCount={currentNotes.length}
            totalNotesCount={totalNotesCount || filteredNotes.length}
            currentPage={currentPage}
            totalPages={notesPerPage > 0 ? Math.ceil((totalNotesCount || filteredNotes.length) / notesPerPage) : 1}
            isLoading={isLoadingNotes}
            onPageChange={handlePageChange}
            notesPerPage={notesPerPage}
            onPageSizeChange={handlePageSizeChange}
            onFetchTotalCount={fetchTotalNotesCount}
            allNotesLoaded={allNotesLoaded}
            selectionMode={isSelectionMode}
            selectedCount={selectedNoteIds.length}
            canConvertNotes={canConvertNotes}
            onSelectionStart={handleSelectionStart}
            onConvertSelected={handleConvertSelectedNotes}
            onDeleteSelected={handleDeleteSelectedNotes}
            onClearSelection={clearSelectionOnly}
            onClearAll={handleClearAll}
            onStartTutorial={handleStartTutorial}
          >
            {isLoadingNotes ? (
              <div className="loading-container">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading notes...</span>
              </div>
            ) : currentNotes.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-sticky-note"></i>
                <h3>No Notes Yet</h3>
                <p>Add your first note to get started!</p>
                <button className="create-note-btn modern-icon-button primary" onClick={handleCreateNoteClick}>
                  <i className="fas fa-plus-circle"></i>
                  <span>Add Note</span>
                </button>
              </div>
            ) : (
              <div className="notes-grid">
                {currentNotes.map((item) => (
                  <ModernNoteCard
                    key={item.noteID}
                    note={item}
                    onEdit={handleEditNote}
                    onDelete={handleDeleteNote}
                    onFavorite={handleFavoriteToggle}
                    onComplete={handleCompleteToggle}
                    onSelect={handleNoteSelect}
                    selectionMode={isSelectionMode}
                    isSelected={selectedNoteIds.includes(item.noteID)}
                    onToggleSelect={handleToggleNoteSelection}
                    isEditing={editingNoteId === item.noteID}
                    onSave={handleSaveNote}
                    onCancel={handleCancelEdit}
                    editedNote={editedNote}
                    setEditedNote={setEditedNote}
                    editedEmailSubject={editedEmailSubject}
                    setEditedEmailSubject={setEditedEmailSubject}
                    editedLink={editedLink}
                    setEditedLink={setEditedLink}
                    selectedTab={selectedTab}
                    onTabClick={handleTabClick}
                    renderTabContent={renderTabContent}
                    onAddNewBox={handleAddNewBox}
                    newBox={newBox}
                    setNewBox={setNewBox}
                    onSaveNewBox={handleSaveNewBox}
                    onDeleteBox={handleDeleteBox}
                    editingNoteId={editingNoteId}
                  />
                ))}
              </div>
            )}
          </ModernDashboard>
          ) : (
            <ExplorerBoard
              journeys={currentJourneys}
              totalJourneys={filteredJourneys.length}
              filters={journeyFilters}
              onFilterChange={handleJourneyFilterChange}
              onFilterSelect={handleJourneyFilterSelect}
              selectedFilter={selectedJourneyFilter}
              clearFilters={clearJourneyFilters}
              filtersVisible={journeyFiltersVisible}
              onToggleFilters={toggleJourneyFilters}
              onCreateJourney={handleCreateJourney}
              onAddJourney={handleAddJourney}
              onJourneySelect={handleJourneySelect}
              onJourneyUpdate={handleJourneyUpdate}
              selectedJourneyId={selectedJourneyId}
              isLoading={isLoadingJourneys}
              currentPage={journeyCurrentPage}
              totalPages={Math.ceil(filteredJourneys.length / journeysPerPage)}
              onPageChange={handleJourneyPageChange}
              journeysPerPage={journeysPerPage}
              onPageSizeChange={handleJourneyPageSizeChange}
              onCenterMap={(lat, lng) => {
                if (mapRef.current) {
                  mapRef.current.panTo({ lat, lng });
                  mapRef.current.setZoom(12); // Zoom in to show the location clearly
                  setZoom(12); // Update state for consistency
                }
              }}
              userPlan={plan}
              notes={notes}
              calculatingJourneyIds={calculatingJourneyIds}
              userAddress={userAddress}
            />
          )}
        </div>
       
        <div className="map">
          {isLoaded ? (
              <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={zoom}
              options={{
                mapId: '5994bfa3e3ab80cd',
                mapTypeControl: true,
                mapTypeControlOptions: {
                  style: window.google.maps.MapTypeControlStyle.DEFAULT,
                  position: window.google.maps.ControlPosition.TOP_RIGHT,
                },
                fullscreenControl: true,
                streetViewControl: true,
                zoomControl: true,
                zoomControlOptions: {
                  position: window.google.maps.ControlPosition.RIGHT_CENTER,
                },
                minZoom: 3, // Prevent zooming out too far (3 = world view, prevents multiple map instances and grey space)
                maxZoom: 20, // Maximum zoom level
                restriction: {
                  latLngBounds: {
                    north: 85, // Maximum latitude (prevents panning to poles)
                    south: -85, // Minimum latitude
                    east: 180, // Maximum longitude
                    west: -180 // Minimum longitude
                  },
                  strictBounds: true // Prevent horizontal wrapping and panning outside world bounds
                },
                gestureHandling: 'cooperative' // Require Ctrl/Cmd to zoom with scroll wheel, prevents accidental zoom
              }}
              onLoad={(map) => {
                mapRef.current = map;
                createAdvancedMarkers(mapRef.current, markers);
                // Initialize journey overlays when map loads
                if (journeys.length > 0) {
                  updateJourneyOverlays(map, journeys);
                }
              }}
              onClick={(e) => {
                // Close journey InfoWindow if clicking on map (not on overlay)
                if (journeyInfoWindowRef.current) {
                  journeyInfoWindowRef.current.close();
                }
                handleMapClick(e);
              }}
            >
              <Autocomplete
                onLoad={(autocomplete) => {
                  autocompleteRef.current = autocomplete;
                }}
                onPlaceChanged={() => {
                  const place = autocompleteRef.current.getPlace();
                  if (place && place.geometry) {
                    const location = place.geometry.location;
                    setCenter({ lat: location.lat(), lng: location.lng() });
                    setZoom(12);

                    if (mapRef.current) {
                      mapRef.current.panTo({ lat: location.lat(), lng: location.lng() });
                      mapRef.current.setZoom(12);
                    }
                  }
                }}
              >
                <input
                  type="text"
                  placeholder="Search for a place"
                  className="autocomplete-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (autocompleteRef.current) {
                        const place = autocompleteRef.current.getPlace();
                        if (place && place.geometry) {
                          const location = place.geometry.location;
                          setCenter({ lat: location.lat(), lng: location.lng() });
                          setZoom(12);

                          if (mapRef.current) {
                            mapRef.current.panTo({ lat: location.lat(), lng: location.lng() });
                            mapRef.current.setZoom(12);
                          }
                        }
                      }
                    }
                  }}
                />
              </Autocomplete>
              {clickPosition &&  (
                <div
                  className="create-note-overlay"
                  style={{
                    position: 'absolute',
                    left: `${clickPosition.x}px`,
                    top: `${clickPosition.y}px`,
                  }}
                >
                  <button className="create-note-button-map" onClick={() => handleCreateNoteClickMap(clickPosition)}>
                    Create Note
                  </button>
                </div>
              )}
              <div className="map-legend2">
                <div className="legend-header" onClick={() => setIsLegendExpanded(!isLegendExpanded)}>
                  <h3>Legend</h3>
                  <button className="legend-toggle-btn" title={isLegendExpanded ? 'Collapse Legend' : 'Expand Legend'}>
                    <i className={`fas fa-chevron-${isLegendExpanded ? 'up' : 'down'}`}></i>
                  </button>
                </div>
                {isLegendExpanded && (
                  <div className="legend-container">
                    {Object.entries(categorization).map(([color, description]) => {
                      // Convert the color name to lowercase and replace spaces with hyphens for matching class names
                      // Normalize ltblue/lightblue variants to "ltblue" for CSS
                      let colorClass = color.toLowerCase().replace(' ', '-');
                      if (colorClass === 'lightblue') colorClass = 'ltblue';
                      return (
                        <div key={color} className="legend-row">
                          <div className={`legend-icon color-indicator ${colorClass}`}></div>
                          <div className="legend-text">{description}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </GoogleMap>
            
          ) : (
            <div>Loading...</div>
          )}
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default MainApp;
